{
  "accountManagement": {
    "description": "Account and organization management.",
    "methods": {
      "createOrganization": {
        "description": "Create a real Stytch B2B organization and its default account. Does not switch the caller's session into the new org (`auth` is not agent-dispatchable). After this returns, the user must switch in the account picker before further writes land there.",
        "params": {
          "params": "CreateOrganizationRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string",
              "description": "Display name for the new organization. Still required: it names the Stytch B2B Organization created for this org, even though the platform no longer persists it in its own `organizations` table."
            },
            "is_test_tenant": {
              "type": "boolean"
            }
          },
          "required": [
            "name"
          ]
        },
        "returns": "Organization",
        "safety": "write"
      },
      "listOrganizations": {
        "description": "listOrganizations on the accountManagement resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "Organization[]",
        "safety": "read"
      },
      "getOrganization": {
        "description": "getOrganization on the accountManagement resource.",
        "params": {
          "organizationId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "Organization",
        "safety": "read"
      },
      "updateOrganization": {
        "description": "updateOrganization on the accountManagement resource.",
        "params": {
          "organizationId": "string",
          "params": "UpdateOrganizationRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string",
              "description": "The platform no longer stores the organization name (Stytch owns it), so this update is a no-op on the server until it is retargeted at Stytch."
            }
          },
          "required": [
            "name"
          ]
        },
        "returns": "Organization",
        "safety": "write"
      },
      "createAccount": {
        "description": "createAccount on the accountManagement resource.",
        "params": {
          "organizationId": "string",
          "params": "CreateAccountRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string"
            }
          },
          "required": [
            "name"
          ]
        },
        "returns": "Account",
        "safety": "write"
      },
      "listAccounts": {
        "description": "listAccounts on the accountManagement resource.",
        "params": {
          "organizationId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "Account[]",
        "safety": "read"
      },
      "getAccount": {
        "description": "getAccount on the accountManagement resource.",
        "params": {
          "organizationId": "string",
          "accountId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "Account",
        "safety": "read"
      },
      "deleteAccount": {
        "description": "Permanently erase a customer account from the product. The backend records that as a soft-delete: calling on an already-deleted account also returns successfully (both \"live → soft-deleted\" and \"already soft-deleted\" are 204). Calling on a non-existent account id raises a 404. Search-pod teardown and other downstream resource cleanup fire from the backend workflow.",
        "params": {
          "organizationId": "string",
          "accountId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "void",
        "safety": "destructive"
      },
      "ensureUser": {
        "description": "ensureUser on the accountManagement resource.",
        "params": {
          "params": "EnsureUserRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "email": {
              "type": "string"
            }
          },
          "required": [
            "email"
          ]
        },
        "returns": "User",
        "safety": "write"
      },
      "listUsers": {
        "description": "listUsers on the accountManagement resource.",
        "params": {
          "params": "ListUsersParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "email_prefix": {
              "type": "string"
            },
            "limit": {
              "type": "number"
            }
          }
        },
        "returns": "User[]",
        "safety": "read"
      },
      "getUser": {
        "description": "getUser on the accountManagement resource.",
        "params": {
          "userId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "User",
        "safety": "read"
      },
      "updateUser": {
        "description": "updateUser on the accountManagement resource.",
        "params": {
          "userId": "string",
          "params": "UpdateUserRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "email": {
              "type": "string"
            }
          },
          "required": [
            "email"
          ]
        },
        "returns": "User",
        "safety": "write"
      },
      "deleteUser": {
        "description": "deleteUser on the accountManagement resource.",
        "params": {
          "userId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "void",
        "safety": "destructive"
      },
      "listUserAccounts": {
        "description": "listUserAccounts on the accountManagement resource.",
        "params": {
          "userId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "Account[]",
        "safety": "read"
      },
      "linkUserToAccount": {
        "description": "linkUserToAccount on the accountManagement resource.",
        "params": {
          "organizationId": "string",
          "accountId": "string",
          "params": "LinkUserToAccountRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "role": {
              "type": "string"
            }
          },
          "required": [
            "role"
          ]
        },
        "returns": "AccountMember",
        "safety": "write"
      },
      "listAccountMembers": {
        "description": "listAccountMembers on the accountManagement resource.",
        "params": {
          "organizationId": "string",
          "accountId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "AccountMember[]",
        "safety": "read"
      },
      "unlinkUserFromAccount": {
        "description": "unlinkUserFromAccount on the accountManagement resource.",
        "params": {
          "organizationId": "string",
          "accountId": "string",
          "userId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "void",
        "safety": "destructive"
      },
      "getChatDefaults": {
        "description": "Read the account's chat defaults (context RAG layer).",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ChatDefaults",
        "safety": "read"
      },
      "updateChatDefaults": {
        "description": "Update the account's chat defaults (context RAG layer).",
        "params": {
          "params": "UpdateChatDefaultsParams"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "context_rag_enabled_default": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "boolean"
                }
              ],
              "description": "true/false sets the default; null clears to unset (platform default)."
            },
            "context_rag_child_runs_enabled_default": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "boolean"
                }
              ]
            }
          }
        },
        "returns": "ChatDefaults",
        "safety": "write"
      }
    }
  },
  "account": {
    "description": "Account-scoped product APIs — product onboarding status, defer, and founder intake.",
    "methods": {
      "getWorkstationTerms": {
        "description": "Returns the current workstation-install responsibility terms and this user's acceptance state.",
        "params": {
          "accountId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "WorkstationTermsStatus",
        "safety": "read"
      },
      "acceptWorkstationTerms": {
        "description": "Accepts exactly the immutable terms version and digest returned by getWorkstationTerms.",
        "params": {
          "accountId": "string",
          "params": "AcceptWorkstationTermsRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "version": {
              "type": "string"
            },
            "sha256": {
              "type": "string"
            }
          },
          "required": [
            "version",
            "sha256"
          ]
        },
        "returns": "WorkstationTermsStatus",
        "safety": "write"
      },
      "getProductOnboardingStatus": {
        "description": "Returns the current product-onboarding phase, session_id, and resource IDs for focusing the Shogun onboarding conversation after commercial complete.",
        "params": {
          "accountId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ProductOnboardingStatus",
        "safety": "read"
      },
      "deferProductOnboarding": {
        "description": "Parks product onboarding in the deferred phase so the user can use the product shell and resume later without completing founder intake.",
        "params": {
          "accountId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "DeferProductOnboardingResponse",
        "safety": "write"
      },
      "saveProductOnboardingIntake": {
        "description": "Persists structured founder intake (who / business-or-interests / intended use) and advances product onboarding past awaiting_intake. Owner-only.",
        "params": {
          "accountId": "string",
          "params": "SaveProductOnboardingIntakeRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "who_text": {
              "type": "string"
            },
            "business_or_interests": {
              "type": "string"
            },
            "intended_use": {
              "type": "string"
            },
            "raw_structured": {
              "type": "object",
              "properties": {},
              "additionalProperties": {}
            }
          }
        },
        "returns": "ProductOnboardingStatus",
        "safety": "write"
      },
      "reportProductOnboardingResources": {
        "description": "Records created team and workspace IDs (optional project_id) after real WM/device creates and advances product onboarding to first_project. Owner-only.",
        "params": {
          "accountId": "string",
          "params": "ReportProductOnboardingResourcesRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "team_id": {
              "type": "string"
            },
            "workspace_id": {
              "type": "string"
            },
            "project_id": {
              "type": "string"
            }
          },
          "required": [
            "team_id",
            "workspace_id"
          ]
        },
        "returns": "ProductOnboardingStatus",
        "safety": "write"
      },
      "completeProductOnboardingFirstProject": {
        "description": "Marks the first project as underway and completes product onboarding. Pass project_id when it was not already recorded via reportProductOnboardingResources. Owner-only.",
        "params": {
          "accountId": "string",
          "params": "CompleteProductOnboardingFirstProjectRequest (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "project_id": {
              "type": "string"
            }
          }
        },
        "returns": "ProductOnboardingStatus",
        "safety": "write"
      }
    }
  },
  "admin": {
    "description": "Administrative operations and platform management.",
    "methods": {
      "listAdminActions": {
        "description": "listAdminActions on the admin resource.",
        "params": {
          "params": "ListAdminActionsParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "admin_id": {
              "type": "string"
            },
            "admin_type": {
              "type": "string"
            },
            "entity_type": {
              "type": "string"
            },
            "entity_id": {
              "type": "string"
            },
            "workspace_id": {
              "type": "string"
            },
            "action": {
              "type": "string"
            },
            "created_after": {
              "type": "string"
            },
            "created_before": {
              "type": "string"
            },
            "limit": {
              "type": "number"
            }
          }
        },
        "returns": "AuditLog[]",
        "safety": "read"
      },
      "getEntityAuditTrail": {
        "description": "getEntityAuditTrail on the admin resource.",
        "params": {
          "entityType": "string",
          "entityId": "string",
          "params": "GetEntityAuditTrailParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "entityType": {
              "type": "string"
            },
            "entityId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "limit": {
                  "type": "number"
                }
              }
            }
          },
          "required": [
            "entityType",
            "entityId"
          ]
        },
        "returns": "AuditLog[]",
        "safety": "read"
      },
      "getPlatformUsageSummary": {
        "description": "getPlatformUsageSummary on the admin resource.",
        "params": {
          "params": "GetPlatformUsageParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "time_range": {
              "type": "string"
            },
            "metric_key": {
              "type": "string"
            }
          }
        },
        "returns": "UsageSummary",
        "safety": "read"
      },
      "getAccountUsageDetails": {
        "description": "getAccountUsageDetails on the admin resource.",
        "params": {
          "accountId": "string",
          "params": "GetAccountUsageParams"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "time_range": {
              "type": "string"
            },
            "metric_key": {
              "type": "string"
            }
          }
        },
        "returns": "UsageSummary",
        "safety": "read"
      },
      "exportUsageData": {
        "description": "exportUsageData on the admin resource.",
        "params": {
          "params": "ExportUsageParams"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "start_time": {
              "type": "string"
            },
            "end_time": {
              "type": "string"
            },
            "format": {
              "type": "string",
              "enum": [
                "csv",
                "json"
              ]
            },
            "workspace_id": {
              "type": "string"
            },
            "metric_key": {
              "type": "string"
            },
            "limit": {
              "type": "number"
            }
          },
          "required": [
            "start_time",
            "end_time"
          ]
        },
        "returns": "unknown",
        "safety": "read"
      },
      "listSessions": {
        "description": "listSessions on the admin resource.",
        "params": {
          "params": "ListSessionsAdminParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "limit": {
              "type": "number"
            },
            "offset": {
              "type": "number"
            },
            "workspace_id": {
              "type": "string"
            },
            "created_after": {
              "type": "string"
            },
            "created_before": {
              "type": "string"
            }
          }
        },
        "returns": "AdminSession[]",
        "safety": "read"
      },
      "getSession": {
        "description": "getSession on the admin resource.",
        "params": {
          "sessionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "sessionId": {
              "type": "string"
            }
          },
          "required": [
            "sessionId"
          ]
        },
        "returns": "AdminSession",
        "safety": "read"
      },
      "replaySession": {
        "description": "replaySession on the admin resource.",
        "params": {
          "sessionId": "string",
          "params": "ReplaySessionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "sessionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "user_prompt": {
                  "type": "string"
                }
              },
              "required": [
                "user_prompt"
              ],
              "additionalProperties": {}
            }
          },
          "required": [
            "sessionId",
            "params"
          ]
        },
        "returns": "ReplaySessionResponse",
        "safety": "write"
      },
      "listActiveAgents": {
        "description": "listActiveAgents on the admin resource.",
        "params": {
          "params": "ListActiveAgentsParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "limit": {
              "type": "number"
            },
            "offset": {
              "type": "number"
            }
          }
        },
        "returns": "ActiveAgent[]",
        "safety": "read"
      },
      "terminateAgent": {
        "description": "terminateAgent on the admin resource.",
        "params": {
          "workflowId": "string",
          "reason": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workflowId": {
              "type": "string"
            },
            "reason": {
              "type": "string"
            }
          },
          "required": [
            "workflowId",
            "reason"
          ]
        },
        "returns": "TerminateAgentResponse",
        "safety": "write"
      },
      "listBridgeConnections": {
        "description": "listBridgeConnections on the admin resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "BridgeConnection[]",
        "safety": "read"
      },
      "listSystemPersonas": {
        "description": "Admin catalog, including reserved `_system_*` cognitive personas.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "SystemPersonaWithConfig[]",
        "safety": "read"
      }
    }
  },
  "agentProcesses.steps": {
    "description": "Agent-process step templates — system and account step presets: create, list, get, update, delete.",
    "methods": {
      "create": {
        "description": "create on the agentProcesses.steps resource.",
        "params": {
          "params": "CreateStepTemplateRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string"
            },
            "description": {
              "type": "string"
            },
            "step": {
              "type": "object",
              "properties": {
                "persona_mode": {
                  "type": "string",
                  "enum": [
                    "parent",
                    "named",
                    "auto"
                  ]
                },
                "persona_name": {
                  "type": "string"
                },
                "task_prompt": {
                  "type": "string"
                },
                "system_prompt": {
                  "type": "object",
                  "properties": {
                    "mode": {
                      "type": "string",
                      "enum": [
                        "append",
                        "replace"
                      ]
                    },
                    "content": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "mode",
                    "content"
                  ]
                },
                "tools": {
                  "type": "object",
                  "properties": {
                    "include": {
                      "type": "array",
                      "items": {
                        "type": "string"
                      }
                    },
                    "exclude": {
                      "type": "array",
                      "items": {
                        "type": "string"
                      }
                    }
                  }
                },
                "max_iterations": {
                  "type": "number"
                },
                "context_requirements": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "category": {
                        "type": "string"
                      },
                      "filter": {
                        "type": "string"
                      }
                    },
                    "required": [
                      "category"
                    ]
                  }
                },
                "emit_event": {
                  "type": "string"
                }
              },
              "required": [
                "task_prompt"
              ]
            }
          },
          "required": [
            "name",
            "step"
          ]
        },
        "returns": "StepTemplate",
        "safety": "write"
      },
      "list": {
        "description": "list on the agentProcesses.steps resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "StepTemplate[]",
        "safety": "read"
      },
      "get": {
        "description": "get on the agentProcesses.steps resource.",
        "params": {
          "stepTemplateId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "stepTemplateId": {
              "type": "string"
            }
          },
          "required": [
            "stepTemplateId"
          ]
        },
        "returns": "StepTemplate",
        "safety": "read"
      },
      "update": {
        "description": "update on the agentProcesses.steps resource.",
        "params": {
          "stepTemplateId": "string",
          "params": "UpdateStepTemplateRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "stepTemplateId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "name": {
                  "type": "string"
                },
                "description": {
                  "type": "string"
                },
                "step": {
                  "type": "object",
                  "properties": {
                    "persona_mode": {
                      "type": "string",
                      "enum": [
                        "parent",
                        "named",
                        "auto"
                      ]
                    },
                    "persona_name": {
                      "type": "string"
                    },
                    "task_prompt": {
                      "type": "string"
                    },
                    "system_prompt": {
                      "type": "object",
                      "properties": {
                        "mode": {
                          "type": "string",
                          "enum": [
                            "append",
                            "replace"
                          ]
                        },
                        "content": {
                          "type": "string"
                        }
                      },
                      "required": [
                        "mode",
                        "content"
                      ]
                    },
                    "tools": {
                      "type": "object",
                      "properties": {
                        "include": {
                          "type": "array",
                          "items": {
                            "type": "string"
                          }
                        },
                        "exclude": {
                          "type": "array",
                          "items": {
                            "type": "string"
                          }
                        }
                      }
                    },
                    "max_iterations": {
                      "type": "number"
                    },
                    "context_requirements": {
                      "type": "array",
                      "items": {
                        "type": "object",
                        "properties": {
                          "category": {
                            "type": "string"
                          },
                          "filter": {
                            "type": "string"
                          }
                        },
                        "required": [
                          "category"
                        ]
                      }
                    },
                    "emit_event": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "task_prompt"
                  ]
                }
              }
            }
          },
          "required": [
            "stepTemplateId",
            "params"
          ]
        },
        "returns": "StepTemplate",
        "safety": "write"
      },
      "delete": {
        "description": "delete on the agentProcesses.steps resource.",
        "params": {
          "stepTemplateId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "stepTemplateId": {
              "type": "string"
            }
          },
          "required": [
            "stepTemplateId"
          ]
        },
        "returns": "{ success: boolean }",
        "safety": "write"
      }
    }
  },
  "agentProcesses": {
    "description": "Agent processes — reasoning-graph definitions and step templates: create, update, delete, validate, copy.",
    "methods": {
      "create": {
        "description": "create on the agentProcesses resource.",
        "params": {
          "params": "CreateAgentProcessRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string"
            },
            "description": {
              "type": "string"
            },
            "definition": {
              "type": "object",
              "properties": {
                "version": {
                  "type": "number",
                  "enum": [
                    1
                  ]
                },
                "max_refinements": {
                  "type": "number"
                },
                "response": {
                  "type": "object",
                  "properties": {
                    "max_iterations": {
                      "type": "number"
                    },
                    "included_tools": {
                      "type": "array",
                      "items": {
                        "type": "string"
                      }
                    },
                    "excluded_tools": {
                      "type": "array",
                      "items": {
                        "type": "string"
                      }
                    },
                    "system_prompt": {
                      "type": "object",
                      "properties": {
                        "mode": {
                          "type": "string",
                          "enum": [
                            "append",
                            "replace"
                          ]
                        },
                        "content": {
                          "type": "string"
                        }
                      },
                      "required": [
                        "mode",
                        "content"
                      ]
                    },
                    "emit_event": {
                      "type": "string"
                    },
                    "shogun_tools": {
                      "type": "boolean"
                    }
                  },
                  "required": [
                    "max_iterations",
                    "excluded_tools"
                  ]
                },
                "graph": {
                  "type": "object",
                  "properties": {
                    "nodes": {
                      "type": "array",
                      "items": {
                        "type": "object",
                        "properties": {
                          "id": {
                            "type": "string"
                          },
                          "type": {
                            "type": "string",
                            "enum": [
                              "agent_executor",
                              "dynamic",
                              "respond"
                            ]
                          },
                          "label": {
                            "type": "string"
                          },
                          "properties": {
                            "anyOf": [
                              {
                                "type": "object",
                                "properties": {
                                  "persona_mode": {
                                    "type": "string",
                                    "enum": [
                                      "parent",
                                      "named",
                                      "auto"
                                    ]
                                  },
                                  "persona_name": {
                                    "type": "string"
                                  },
                                  "task_prompt": {
                                    "type": "string"
                                  },
                                  "system_prompt": {
                                    "type": "object",
                                    "properties": {
                                      "mode": {
                                        "type": "string",
                                        "enum": [
                                          "append",
                                          "replace"
                                        ]
                                      },
                                      "content": {
                                        "type": "string"
                                      }
                                    },
                                    "required": [
                                      "mode",
                                      "content"
                                    ]
                                  },
                                  "tools": {
                                    "type": "object",
                                    "properties": {
                                      "include": {
                                        "type": "array",
                                        "items": {
                                          "type": "string"
                                        }
                                      },
                                      "exclude": {
                                        "type": "array",
                                        "items": {
                                          "type": "string"
                                        }
                                      }
                                    }
                                  },
                                  "max_iterations": {
                                    "type": "number"
                                  },
                                  "context_requirements": {
                                    "type": "array",
                                    "items": {
                                      "type": "object",
                                      "properties": {
                                        "category": {
                                          "type": "string"
                                        },
                                        "filter": {
                                          "type": "string"
                                        }
                                      },
                                      "required": [
                                        "category"
                                      ]
                                    }
                                  },
                                  "emit_event": {
                                    "type": "string"
                                  }
                                },
                                "required": [
                                  "task_prompt"
                                ]
                              },
                              {
                                "type": "object",
                                "properties": {
                                  "task_prompt": {
                                    "type": "string"
                                  }
                                }
                              }
                            ],
                            "description": "agent_executor: the full step config; dynamic: optional goal only."
                          }
                        },
                        "required": [
                          "id",
                          "type"
                        ]
                      }
                    },
                    "edges": {
                      "type": "array",
                      "items": {
                        "type": "object",
                        "properties": {
                          "from_node": {
                            "type": "string"
                          },
                          "to_node": {
                            "type": "string"
                          },
                          "label": {
                            "type": "string"
                          }
                        },
                        "required": [
                          "from_node",
                          "to_node"
                        ]
                      }
                    }
                  },
                  "required": [
                    "nodes",
                    "edges"
                  ]
                }
              },
              "required": [
                "version",
                "max_refinements",
                "response",
                "graph"
              ]
            }
          },
          "required": [
            "name",
            "definition"
          ]
        },
        "returns": "AgentProcess",
        "safety": "write"
      },
      "list": {
        "description": "list on the agentProcesses resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "AgentProcess[]",
        "safety": "read"
      },
      "get": {
        "description": "get on the agentProcesses resource.",
        "params": {
          "processId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "processId": {
              "type": "string"
            }
          },
          "required": [
            "processId"
          ]
        },
        "returns": "AgentProcess",
        "safety": "read"
      },
      "update": {
        "description": "update on the agentProcesses resource.",
        "params": {
          "processId": "string",
          "params": "UpdateAgentProcessRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "processId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "name": {
                  "type": "string"
                },
                "description": {
                  "type": "string"
                },
                "definition": {
                  "type": "object",
                  "properties": {
                    "version": {
                      "type": "number",
                      "enum": [
                        1
                      ]
                    },
                    "max_refinements": {
                      "type": "number"
                    },
                    "response": {
                      "type": "object",
                      "properties": {
                        "max_iterations": {
                          "type": "number"
                        },
                        "included_tools": {
                          "type": "array",
                          "items": {
                            "type": "string"
                          }
                        },
                        "excluded_tools": {
                          "type": "array",
                          "items": {
                            "type": "string"
                          }
                        },
                        "system_prompt": {
                          "type": "object",
                          "properties": {
                            "mode": {
                              "type": "string",
                              "enum": [
                                "append",
                                "replace"
                              ]
                            },
                            "content": {
                              "type": "string"
                            }
                          },
                          "required": [
                            "mode",
                            "content"
                          ]
                        },
                        "emit_event": {
                          "type": "string"
                        },
                        "shogun_tools": {
                          "type": "boolean"
                        }
                      },
                      "required": [
                        "max_iterations",
                        "excluded_tools"
                      ]
                    },
                    "graph": {
                      "type": "object",
                      "properties": {
                        "nodes": {
                          "type": "array",
                          "items": {
                            "type": "object",
                            "properties": {
                              "id": {
                                "type": "string"
                              },
                              "type": {
                                "type": "string",
                                "enum": [
                                  "agent_executor",
                                  "dynamic",
                                  "respond"
                                ]
                              },
                              "label": {
                                "type": "string"
                              },
                              "properties": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "persona_mode": {
                                        "type": "string",
                                        "enum": [
                                          "parent",
                                          "named",
                                          "auto"
                                        ]
                                      },
                                      "persona_name": {
                                        "type": "string"
                                      },
                                      "task_prompt": {
                                        "type": "string"
                                      },
                                      "system_prompt": {
                                        "type": "object",
                                        "properties": {
                                          "mode": {
                                            "type": "string",
                                            "enum": [
                                              "append",
                                              "replace"
                                            ]
                                          },
                                          "content": {
                                            "type": "string"
                                          }
                                        },
                                        "required": [
                                          "mode",
                                          "content"
                                        ]
                                      },
                                      "tools": {
                                        "type": "object",
                                        "properties": {
                                          "include": {
                                            "type": "array",
                                            "items": {
                                              "type": "string"
                                            }
                                          },
                                          "exclude": {
                                            "type": "array",
                                            "items": {
                                              "type": "string"
                                            }
                                          }
                                        }
                                      },
                                      "max_iterations": {
                                        "type": "number"
                                      },
                                      "context_requirements": {
                                        "type": "array",
                                        "items": {
                                          "type": "object",
                                          "properties": {
                                            "category": {
                                              "type": "string"
                                            },
                                            "filter": {
                                              "type": "string"
                                            }
                                          },
                                          "required": [
                                            "category"
                                          ]
                                        }
                                      },
                                      "emit_event": {
                                        "type": "string"
                                      }
                                    },
                                    "required": [
                                      "task_prompt"
                                    ]
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "task_prompt": {
                                        "type": "string"
                                      }
                                    }
                                  }
                                ],
                                "description": "agent_executor: the full step config; dynamic: optional goal only."
                              }
                            },
                            "required": [
                              "id",
                              "type"
                            ]
                          }
                        },
                        "edges": {
                          "type": "array",
                          "items": {
                            "type": "object",
                            "properties": {
                              "from_node": {
                                "type": "string"
                              },
                              "to_node": {
                                "type": "string"
                              },
                              "label": {
                                "type": "string"
                              }
                            },
                            "required": [
                              "from_node",
                              "to_node"
                            ]
                          }
                        }
                      },
                      "required": [
                        "nodes",
                        "edges"
                      ]
                    }
                  },
                  "required": [
                    "version",
                    "max_refinements",
                    "response",
                    "graph"
                  ]
                }
              }
            }
          },
          "required": [
            "processId",
            "params"
          ]
        },
        "returns": "AgentProcess",
        "safety": "write"
      },
      "delete": {
        "description": "delete on the agentProcesses resource.",
        "params": {
          "processId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "processId": {
              "type": "string"
            }
          },
          "required": [
            "processId"
          ]
        },
        "returns": "{ success: boolean }",
        "safety": "write"
      },
      "validate": {
        "description": "Validate a definition without saving.",
        "params": {
          "definition": "ProcessDefinition | Record<string, unknown>"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "definition": {
              "anyOf": [
                {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                },
                {
                  "type": "object",
                  "properties": {
                    "version": {
                      "type": "number",
                      "enum": [
                        1
                      ]
                    },
                    "max_refinements": {
                      "type": "number"
                    },
                    "response": {
                      "type": "object",
                      "properties": {
                        "max_iterations": {
                          "type": "number"
                        },
                        "included_tools": {
                          "type": "array",
                          "items": {
                            "type": "string"
                          }
                        },
                        "excluded_tools": {
                          "type": "array",
                          "items": {
                            "type": "string"
                          }
                        },
                        "system_prompt": {
                          "type": "object",
                          "properties": {
                            "mode": {
                              "type": "string",
                              "enum": [
                                "append",
                                "replace"
                              ]
                            },
                            "content": {
                              "type": "string"
                            }
                          },
                          "required": [
                            "mode",
                            "content"
                          ]
                        },
                        "emit_event": {
                          "type": "string"
                        },
                        "shogun_tools": {
                          "type": "boolean"
                        }
                      },
                      "required": [
                        "max_iterations",
                        "excluded_tools"
                      ]
                    },
                    "graph": {
                      "type": "object",
                      "properties": {
                        "nodes": {
                          "type": "array",
                          "items": {
                            "type": "object",
                            "properties": {
                              "id": {
                                "type": "string"
                              },
                              "type": {
                                "type": "string",
                                "enum": [
                                  "agent_executor",
                                  "dynamic",
                                  "respond"
                                ]
                              },
                              "label": {
                                "type": "string"
                              },
                              "properties": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "persona_mode": {
                                        "type": "string",
                                        "enum": [
                                          "parent",
                                          "named",
                                          "auto"
                                        ]
                                      },
                                      "persona_name": {
                                        "type": "string"
                                      },
                                      "task_prompt": {
                                        "type": "string"
                                      },
                                      "system_prompt": {
                                        "type": "object",
                                        "properties": {
                                          "mode": {
                                            "type": "string",
                                            "enum": [
                                              "append",
                                              "replace"
                                            ]
                                          },
                                          "content": {
                                            "type": "string"
                                          }
                                        },
                                        "required": [
                                          "mode",
                                          "content"
                                        ]
                                      },
                                      "tools": {
                                        "type": "object",
                                        "properties": {
                                          "include": {
                                            "type": "array",
                                            "items": {
                                              "type": "string"
                                            }
                                          },
                                          "exclude": {
                                            "type": "array",
                                            "items": {
                                              "type": "string"
                                            }
                                          }
                                        }
                                      },
                                      "max_iterations": {
                                        "type": "number"
                                      },
                                      "context_requirements": {
                                        "type": "array",
                                        "items": {
                                          "type": "object",
                                          "properties": {
                                            "category": {
                                              "type": "string"
                                            },
                                            "filter": {
                                              "type": "string"
                                            }
                                          },
                                          "required": [
                                            "category"
                                          ]
                                        }
                                      },
                                      "emit_event": {
                                        "type": "string"
                                      }
                                    },
                                    "required": [
                                      "task_prompt"
                                    ]
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "task_prompt": {
                                        "type": "string"
                                      }
                                    }
                                  }
                                ],
                                "description": "agent_executor: the full step config; dynamic: optional goal only."
                              }
                            },
                            "required": [
                              "id",
                              "type"
                            ]
                          }
                        },
                        "edges": {
                          "type": "array",
                          "items": {
                            "type": "object",
                            "properties": {
                              "from_node": {
                                "type": "string"
                              },
                              "to_node": {
                                "type": "string"
                              },
                              "label": {
                                "type": "string"
                              }
                            },
                            "required": [
                              "from_node",
                              "to_node"
                            ]
                          }
                        }
                      },
                      "required": [
                        "nodes",
                        "edges"
                      ]
                    }
                  },
                  "required": [
                    "version",
                    "max_refinements",
                    "response",
                    "graph"
                  ]
                }
              ]
            }
          },
          "required": [
            "definition"
          ]
        },
        "returns": "ValidateDefinitionResult",
        "safety": "read"
      },
      "copyToAccount": {
        "description": "Copy a system process or system step template into the account library.",
        "params": {
          "params": "CopyToAccountRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "process_id": {
              "type": "string"
            },
            "step_template_id": {
              "type": "string"
            },
            "new_name": {
              "type": "string"
            }
          }
        },
        "returns": "CopyToAccountResult",
        "safety": "write"
      }
    }
  },
  "agentSettings": {
    "description": "Global agent runtime settings — envelope thresholds, retrieval page sizes, bridge spill threshold. Read-only; the values are seeded.",
    "methods": {
      "list": {
        "description": "list on the agentSettings resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "AgentSettingsList",
        "safety": "read"
      }
    }
  },
  "auth": {
    "description": "Authentication and authorization operations.",
    "methods": {
      "revokeSession": {
        "description": "revokeSession on the auth resource.",
        "params": {
          "params": "RevokeSessionRequest (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "session_token": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "session_jwt": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "member_session_id": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            }
          }
        },
        "returns": "void",
        "safety": "write"
      },
      "activateSession": {
        "description": "Activate a claim-less Stytch session — injects server-set account_id + user_id. POST /v1/auth/session/activate (auth class 1).",
        "params": {
          "params": "{\n    account_id?: string;\n    session_token?: string;\n    session_duration_minutes?: number;\n  } (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "session_token": {
              "type": "string"
            },
            "session_duration_minutes": {
              "type": "number"
            }
          }
        },
        "returns": "ActivatedSessionResponse",
        "safety": "write"
      },
      "renewSession": {
        "description": "Renew an activated session from the opaque session_token (auth class 2). Gateway-exempt — the JWT may already be expired. `unauthenticated`: the credential is the `session_token` in the BODY. This is the route a degraded holder recovers through, so requiring a live bearer here would make renewal impossible exactly when it is needed.",
        "params": {
          "params": "{\n    session_token: string;\n    session_duration_minutes?: number;\n  }"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "session_token": {
              "type": "string"
            },
            "session_duration_minutes": {
              "type": "number"
            }
          },
          "required": [
            "session_token"
          ]
        },
        "returns": "ActivatedSessionResponse",
        "safety": "write"
      },
      "getOpenIDConfig": {
        "description": "OIDC discovery document. Gateway-exempt via the `/.well-known/` prefix in `go/internal/api/middleware.go` `authExemptPrefixes` — a discovery document that required a session would defeat its own purpose.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "OIDCConfig",
        "safety": "read"
      },
      "listOrganizations": {
        "description": "List the organizations the authenticated member may switch into.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "OrgMembership[]",
        "safety": "read"
      },
      "createOrganization": {
        "description": "Create an organization via Discovery IST (auth class 4). Caller must Activate the returned claim-less session pair. `unauthenticated`: the credential is the `intermediate_session_token` in the BODY. The caller is mid-Discovery and has no member session yet, so there is no bearer to send. NOTE the method asymmetry — `GET /v1/auth/organizations` (`listOrganizations` above) is a normal authenticated read on the same path; only POST is class 4.",
        "params": {
          "params": "{\n    intermediate_session_token: string;\n    name: string;\n    slug?: string;\n    session_duration_minutes?: number;\n  }"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "intermediate_session_token": {
              "type": "string"
            },
            "name": {
              "type": "string"
            },
            "slug": {
              "type": "string"
            },
            "session_duration_minutes": {
              "type": "number"
            }
          },
          "required": [
            "intermediate_session_token",
            "name"
          ]
        },
        "returns": "CreateOrganizationResponse",
        "safety": "write"
      },
      "switchOrganization": {
        "description": "Switch into another organization via Stytch sessions.Exchange, then re-activate so account_id is re-resolved for the destination org.",
        "params": {
          "params": "{\n    organization_id: string;\n    account_id?: string;\n    session_token?: string;\n    session_duration_minutes?: number;\n  }"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "session_token": {
              "type": "string"
            },
            "session_duration_minutes": {
              "type": "number"
            }
          }
        },
        "returns": "ActivatedSessionResponse",
        "safety": "write"
      },
      "createDesktopHandoff": {
        "description": "Browser → create a single-use desktop handoff code bound to a daemon nonce.",
        "params": {
          "params": "{\n    nonce: string;\n    session_token?: string;\n    session_duration_minutes?: number;\n  }"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "nonce": {
              "type": "string"
            },
            "session_token": {
              "type": "string"
            },
            "session_duration_minutes": {
              "type": "number"
            }
          },
          "required": [
            "nonce"
          ]
        },
        "returns": "{ code: string; expires_at: string }",
        "safety": "write"
      },
      "redeemDesktopHandoff": {
        "description": "Daemon → redeem code+nonce for a one-shot Stytch token pair (auth class 3). `unauthenticated`: the credential is the `code` + `nonce` pair in the BODY. This is the route a daemon with NO credential at all uses to get its first one — a bearer requirement here would be unsatisfiable by construction.",
        "params": {
          "params": "{\n    code: string;\n    nonce: string;\n  }"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "code": {
              "type": "string"
            },
            "nonce": {
              "type": "string"
            }
          },
          "required": [
            "code",
            "nonce"
          ]
        },
        "returns": "RedeemDesktopHandoffResponse",
        "safety": "write"
      },
      "listMembers": {
        "description": "List organization members from Stytch (paginated), decorated with local organization_role_grants. Cursor + limit are required on the wire contract so a page is never silently truncated at the vendor default.",
        "params": {
          "organizationId": "string",
          "params": "{ cursor?: string; limit?: number } (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "cursor": {
              "type": "string"
            },
            "limit": {
              "type": "number"
            }
          }
        },
        "returns": "ListMembersResponse",
        "safety": "read"
      },
      "inviteMember": {
        "description": "Invite a member via Stytch email. Authorization is server-side (organization.invite_member); Stytch rejections surface verbatim.",
        "params": {
          "organizationId": "string",
          "params": "{ email: string }"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "email": {
              "type": "string"
            }
          },
          "required": [
            "email"
          ]
        },
        "returns": "InviteMemberResponse",
        "safety": "write"
      },
      "getInvitePolicy": {
        "description": "Read who may be invited to the organization. Stytch is the store — this value is never mirrored locally (account ADR-012). Owner-only server-side (organization.admin); a non-owner gets 403.",
        "params": {
          "organizationId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "InvitePolicy",
        "safety": "read"
      },
      "setInvitePolicy": {
        "description": "Set who may be invited. `ALL_ALLOWED` admits any address and leaves the domain list stored but unenforced; `RESTRICTED` admits only the listed domains and requires at least one. Disabling invites entirely is not offered. Omitting `email_allowed_domains` preserves the stored list. Stytch rejections (a common consumer domain such as gmail.com) surface verbatim. Returns the stored policy.",
        "params": {
          "organizationId": "string",
          "params": "SetInvitePolicyRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "email_invites": {
              "type": "string",
              "enum": [
                "ALL_ALLOWED",
                "RESTRICTED"
              ]
            },
            "email_allowed_domains": {
              "type": "array",
              "items": {
                "type": "string"
              }
            }
          },
          "required": [
            "email_invites"
          ]
        },
        "returns": "InvitePolicy",
        "safety": "write"
      }
    }
  },
  "automation": {
    "description": "Automation workflows, DAG execution, and condition evaluation.",
    "methods": {
      "create": {
        "description": "create on the automation resource.",
        "params": {
          "params": "CreateAutomationRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string"
            },
            "description": {
              "type": "string"
            },
            "scope": {
              "type": "string"
            },
            "workspace_id": {
              "type": "string"
            },
            "tags": {
              "type": "array",
              "items": {
                "type": "string"
              }
            },
            "max_concurrent_runs": {
              "type": "number"
            }
          },
          "required": [
            "name"
          ]
        },
        "returns": "Automation",
        "safety": "write"
      },
      "list": {
        "description": "list on the automation resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "Automation[]",
        "safety": "read"
      },
      "listFeed": {
        "description": "listFeed on the automation resource.",
        "params": {
          "params": "ListFeedParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "status": {
              "type": "string"
            },
            "limit": {
              "type": "number"
            },
            "offset": {
              "type": "number"
            }
          }
        },
        "returns": "AutomationRun[]",
        "safety": "read"
      },
      "get": {
        "description": "get on the automation resource.",
        "params": {
          "automationId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            }
          },
          "required": [
            "automationId"
          ]
        },
        "returns": "Automation",
        "safety": "read"
      },
      "update": {
        "description": "update on the automation resource.",
        "params": {
          "automationId": "string",
          "params": "UpdateAutomationRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "name": {
                  "type": "string"
                },
                "description": {
                  "type": "string"
                },
                "tags": {
                  "type": "array",
                  "items": {
                    "type": "string"
                  }
                },
                "max_concurrent_runs": {
                  "type": "number"
                }
              }
            }
          },
          "required": [
            "automationId",
            "params"
          ]
        },
        "returns": "Automation",
        "safety": "write"
      },
      "delete": {
        "description": "delete on the automation resource.",
        "params": {
          "automationId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            }
          },
          "required": [
            "automationId"
          ]
        },
        "returns": "Automation",
        "safety": "write"
      },
      "saveDraft": {
        "description": "saveDraft on the automation resource.",
        "params": {
          "automationId": "string",
          "draft": "Record<string, unknown>"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            },
            "draft": {
              "type": "object",
              "properties": {},
              "additionalProperties": {}
            }
          },
          "required": [
            "automationId",
            "draft"
          ]
        },
        "returns": "AutomationDraft",
        "safety": "write"
      },
      "getDraft": {
        "description": "getDraft on the automation resource.",
        "params": {
          "automationId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            }
          },
          "required": [
            "automationId"
          ]
        },
        "returns": "AutomationDraft",
        "safety": "read"
      },
      "validateDraft": {
        "description": "validateDraft on the automation resource.",
        "params": {
          "automationId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            }
          },
          "required": [
            "automationId"
          ]
        },
        "returns": "ValidationResult",
        "safety": "read"
      },
      "publish": {
        "description": "publish on the automation resource.",
        "params": {
          "automationId": "string",
          "params": "PublishVersionRequest (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "changelog": {
                  "type": "string"
                }
              }
            }
          },
          "required": [
            "automationId"
          ]
        },
        "returns": "AutomationVersion",
        "safety": "write"
      },
      "compareVersions": {
        "description": "compareVersions on the automation resource.",
        "params": {
          "automationId": "string",
          "v1": "number",
          "v2": "number"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            },
            "v1": {
              "type": "number"
            },
            "v2": {
              "type": "number"
            }
          },
          "required": [
            "automationId",
            "v1",
            "v2"
          ]
        },
        "returns": "CompareVersionsResult",
        "safety": "read"
      },
      "listVersions": {
        "description": "listVersions on the automation resource.",
        "params": {
          "automationId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            }
          },
          "required": [
            "automationId"
          ]
        },
        "returns": "AutomationVersion[]",
        "safety": "read"
      },
      "getVersion": {
        "description": "getVersion on the automation resource.",
        "params": {
          "automationId": "string",
          "versionNumber": "number"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            },
            "versionNumber": {
              "type": "number"
            }
          },
          "required": [
            "automationId",
            "versionNumber"
          ]
        },
        "returns": "AutomationVersion",
        "safety": "read"
      },
      "rollback": {
        "description": "rollback on the automation resource.",
        "params": {
          "automationId": "string",
          "params": "RollbackRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "target_version": {
                  "type": "number"
                },
                "changelog": {
                  "type": "string"
                }
              },
              "required": [
                "target_version"
              ]
            }
          },
          "required": [
            "automationId",
            "params"
          ]
        },
        "returns": "AutomationVersion",
        "safety": "write"
      },
      "triggerRun": {
        "description": "triggerRun on the automation resource.",
        "params": {
          "automationId": "string",
          "params": "TriggerRunRequest (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "input": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                }
              }
            }
          },
          "required": [
            "automationId"
          ]
        },
        "returns": "AutomationRun",
        "safety": "write"
      },
      "listRuns": {
        "description": "listRuns on the automation resource.",
        "params": {
          "automationId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            }
          },
          "required": [
            "automationId"
          ]
        },
        "returns": "AutomationRun[]",
        "safety": "read"
      },
      "getRun": {
        "description": "getRun on the automation resource.",
        "params": {
          "automationId": "string",
          "runId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            },
            "runId": {
              "type": "string"
            }
          },
          "required": [
            "automationId",
            "runId"
          ]
        },
        "returns": "AutomationRun",
        "safety": "read"
      },
      "cancelRun": {
        "description": "cancelRun on the automation resource.",
        "params": {
          "automationId": "string",
          "runId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            },
            "runId": {
              "type": "string"
            }
          },
          "required": [
            "automationId",
            "runId"
          ]
        },
        "returns": "AutomationRun",
        "safety": "write"
      },
      "listStepExecutions": {
        "description": "listStepExecutions on the automation resource.",
        "params": {
          "automationId": "string",
          "runId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            },
            "runId": {
              "type": "string"
            }
          },
          "required": [
            "automationId",
            "runId"
          ]
        },
        "returns": "AutomationStepExecution[]",
        "safety": "read"
      },
      "dryRun": {
        "description": "dryRun on the automation resource.",
        "params": {
          "automationId": "string",
          "params": "DryRunRequest (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "mock_trigger_input": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                },
                "mock_step_outputs": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                },
                "use_draft": {
                  "type": "boolean"
                }
              }
            }
          },
          "required": [
            "automationId"
          ]
        },
        "returns": "DryRunResult",
        "safety": "write"
      },
      "dispatchEvent": {
        "description": "dispatchEvent on the automation resource.",
        "params": {
          "params": "DispatchEventRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "event_name": {
              "type": "string"
            },
            "event_data": {
              "type": "object",
              "properties": {},
              "additionalProperties": {}
            }
          },
          "required": [
            "event_name"
          ]
        },
        "returns": "DispatchEventResult",
        "safety": "write"
      },
      "createTemplate": {
        "description": "createTemplate on the automation resource.",
        "params": {
          "params": "CreateTemplateRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string"
            },
            "description": {
              "type": "string"
            },
            "category": {
              "type": "string"
            },
            "tags": {
              "type": "array",
              "items": {
                "type": "string"
              }
            }
          },
          "required": [
            "name"
          ]
        },
        "returns": "AutomationTemplate",
        "safety": "write"
      },
      "listTemplates": {
        "description": "listTemplates on the automation resource.",
        "params": {
          "params": "ListTemplatesParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "category": {
              "type": "string"
            },
            "tags": {
              "type": "string"
            },
            "search": {
              "type": "string"
            },
            "sort_by": {
              "type": "string"
            },
            "sort_order": {
              "type": "string"
            }
          }
        },
        "returns": "AutomationTemplate[]",
        "safety": "read"
      },
      "getTemplate": {
        "description": "getTemplate on the automation resource.",
        "params": {
          "templateId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "templateId": {
              "type": "string"
            }
          },
          "required": [
            "templateId"
          ]
        },
        "returns": "AutomationTemplate",
        "safety": "read"
      },
      "updateTemplate": {
        "description": "updateTemplate on the automation resource.",
        "params": {
          "templateId": "string",
          "params": "UpdateTemplateRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "templateId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "name": {
                  "type": "string"
                },
                "description": {
                  "type": "string"
                },
                "category": {
                  "type": "string"
                },
                "tags": {
                  "type": "array",
                  "items": {
                    "type": "string"
                  }
                }
              }
            }
          },
          "required": [
            "templateId",
            "params"
          ]
        },
        "returns": "AutomationTemplate",
        "safety": "write"
      },
      "deleteTemplate": {
        "description": "deleteTemplate on the automation resource.",
        "params": {
          "templateId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "templateId": {
              "type": "string"
            }
          },
          "required": [
            "templateId"
          ]
        },
        "returns": "AutomationTemplate",
        "safety": "write"
      },
      "publishTemplate": {
        "description": "publishTemplate on the automation resource.",
        "params": {
          "templateId": "string",
          "params": "PublishTemplateRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "templateId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "definition": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                },
                "parameters": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                },
                "changelog": {
                  "type": "string"
                }
              },
              "required": [
                "definition"
              ]
            }
          },
          "required": [
            "templateId",
            "params"
          ]
        },
        "returns": "AutomationTemplateVersion",
        "safety": "write"
      },
      "listTemplateVersions": {
        "description": "listTemplateVersions on the automation resource.",
        "params": {
          "templateId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "templateId": {
              "type": "string"
            }
          },
          "required": [
            "templateId"
          ]
        },
        "returns": "AutomationTemplateVersion[]",
        "safety": "read"
      },
      "getTemplateVersion": {
        "description": "getTemplateVersion on the automation resource.",
        "params": {
          "templateId": "string",
          "versionNumber": "number"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "templateId": {
              "type": "string"
            },
            "versionNumber": {
              "type": "number"
            }
          },
          "required": [
            "templateId",
            "versionNumber"
          ]
        },
        "returns": "AutomationTemplateVersion",
        "safety": "read"
      },
      "instantiateTemplate": {
        "description": "instantiateTemplate on the automation resource.",
        "params": {
          "templateId": "string",
          "params": "InstantiateTemplateRequest (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "templateId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "parameter_values": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                },
                "name": {
                  "type": "string"
                },
                "scope": {
                  "type": "string"
                },
                "workspace_id": {
                  "type": "string"
                }
              }
            }
          },
          "required": [
            "templateId"
          ]
        },
        "returns": "Automation",
        "safety": "write"
      },
      "saveAsTemplate": {
        "description": "saveAsTemplate on the automation resource.",
        "params": {
          "automationId": "string",
          "params": "SaveAsTemplateRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "automationId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "name": {
                  "type": "string"
                },
                "description": {
                  "type": "string"
                },
                "category": {
                  "type": "string"
                },
                "tags": {
                  "type": "array",
                  "items": {
                    "type": "string"
                  }
                },
                "parameter_designations": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                }
              },
              "required": [
                "name"
              ]
            }
          },
          "required": [
            "automationId",
            "params"
          ]
        },
        "returns": "AutomationTemplate",
        "safety": "write"
      }
    }
  },
  "billing": {
    "description": "Billing and subscription management.",
    "methods": {
      "listPlans": {
        "description": "listPlans on the billing resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "Plan[]",
        "safety": "read"
      },
      "getSubscription": {
        "description": "getSubscription on the billing resource.",
        "params": {
          "accountId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "Subscription",
        "safety": "read"
      },
      "changePlan": {
        "description": "changePlan on the billing resource.",
        "params": {
          "accountId": "string",
          "params": "ChangePlanRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "subscription_id": {
              "type": "string"
            },
            "new_plan_id": {
              "type": "string"
            }
          },
          "required": [
            "subscription_id",
            "new_plan_id"
          ]
        },
        "returns": "Subscription",
        "safety": "write"
      },
      "assignPlan": {
        "description": "assignPlan on the billing resource.",
        "params": {
          "accountId": "string",
          "params": "AssignPlanRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "plan_id": {
              "type": "string"
            },
            "is_active": {
              "type": "boolean"
            }
          },
          "required": [
            "plan_id",
            "is_active"
          ]
        },
        "returns": "PlanAssignment",
        "safety": "write"
      },
      "listPlanAssignments": {
        "description": "listPlanAssignments on the billing resource.",
        "params": {
          "accountId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "PlanAssignment[]",
        "safety": "read"
      },
      "activatePlanAssignment": {
        "description": "activatePlanAssignment on the billing resource.",
        "params": {
          "accountId": "string",
          "assignmentId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "assignmentId": {
              "type": "string"
            }
          },
          "required": [
            "assignmentId"
          ]
        },
        "returns": "PlanAssignment",
        "safety": "write"
      },
      "planWhatIf": {
        "description": "planWhatIf on the billing resource.",
        "params": {
          "accountId": "string",
          "planId": "string",
          "params": "PlanWhatIfParams"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "planId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "period_start": {
                  "type": "string"
                },
                "period_end": {
                  "type": "string"
                }
              },
              "required": [
                "period_start",
                "period_end"
              ]
            }
          },
          "required": [
            "planId",
            "params"
          ]
        },
        "returns": "unknown",
        "safety": "read"
      },
      "getCreditBalance": {
        "description": "getCreditBalance on the billing resource.",
        "params": {
          "accountId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "CreditBalance",
        "safety": "read"
      },
      "getCreditTransactions": {
        "description": "getCreditTransactions on the billing resource.",
        "params": {
          "accountId": "string",
          "params": "{ limit?: number } (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "limit": {
              "type": "number"
            }
          }
        },
        "returns": "CreditTransactionsResponse",
        "safety": "read"
      },
      "getEntitlements": {
        "description": "getEntitlements on the billing resource.",
        "params": {
          "accountId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "Entitlements",
        "safety": "read"
      },
      "getCurrentPeriod": {
        "description": "Get current billing period position. Returns the account's position for the open (or awaiting-payment) period, including allowance, consumed credits, overage projection, and daily usage.",
        "params": {
          "accountId": "string — - The account identifier"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "CurrentPeriodPosition",
        "safety": "read"
      },
      "listPeriods": {
        "description": "List billing periods. Returns settled and closed periods, newest first. The per-period breakdown includes advance amount, included credits, credits consumed, and overage.",
        "params": {
          "accountId": "string — - The account identifier",
          "params": "ListPeriodsParams (optional) — - Optional pagination params (limit)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "limit": {
              "type": "number"
            }
          }
        },
        "returns": "PeriodSummary[]",
        "safety": "read"
      },
      "retryAdvanceCharge": {
        "description": "Retry a declined advance charge. Signals the period-open workflow to re-attempt the declined advance charge. Only valid when the period is in awaiting_payment status. This method does not charge directly — it signals the workflow to retry.",
        "params": {
          "accountId": "string — - The account identifier"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "RetryAdvanceChargeResponse",
        "safety": "write"
      },
      "cancelSubscription": {
        "description": "Request cancellation of the account's subscription. Initiates a deferred cancellation that takes effect at the end of the current billing period. The subscription remains active until then.",
        "params": {
          "accountId": "string — - The account identifier",
          "request": "CancelSubscriptionRequest (optional) — - Optional cancellation details (reason)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "reason": {
              "type": "string"
            }
          }
        },
        "returns": "CancelSubscriptionResponse",
        "safety": "write"
      },
      "reactivateSubscription": {
        "description": "Reactivate a pending subscription cancellation. Withdraws a pending cancellation before its effective date. Not allowed after the cancellation has already taken effect.",
        "params": {
          "accountId": "string — - The account identifier"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ReactivateSubscriptionResponse",
        "safety": "write"
      },
      "adminAdjustCredits": {
        "description": "adminAdjustCredits on the billing resource.",
        "params": {
          "params": "AdminAdjustCreditsRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "adjustment_amount": {
              "type": "number"
            },
            "reason": {
              "type": "string"
            }
          },
          "required": [
            "adjustment_amount",
            "reason"
          ]
        },
        "returns": "unknown",
        "safety": "write"
      },
      "adminOverrideSubscription": {
        "description": "adminOverrideSubscription on the billing resource.",
        "params": {
          "params": "AdminOverrideSubscriptionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "plan_id": {
              "type": "string"
            },
            "reason": {
              "type": "string"
            }
          },
          "required": [
            "plan_id",
            "reason"
          ]
        },
        "returns": "Subscription",
        "safety": "write"
      },
      "adminCompAccount": {
        "description": "adminCompAccount on the billing resource.",
        "params": {
          "params": "AdminCompAccountRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "comp_type": {
              "type": "string"
            },
            "value": {},
            "reason": {
              "type": "string"
            }
          },
          "required": [
            "comp_type",
            "value",
            "reason"
          ]
        },
        "returns": "unknown",
        "safety": "write"
      },
      "adminGetSubscription": {
        "description": "adminGetSubscription on the billing resource.",
        "params": {
          "params": "AdminAccountParams"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {}
        },
        "returns": "Subscription",
        "safety": "read"
      },
      "adminGetCreditBalance": {
        "description": "adminGetCreditBalance on the billing resource.",
        "params": {
          "params": "AdminAccountParams"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {}
        },
        "returns": "CreditBalance",
        "safety": "read"
      },
      "adminListCreditTransactions": {
        "description": "adminListCreditTransactions on the billing resource.",
        "params": {
          "params": "AdminListCreditTransactionsParams"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "limit": {
              "type": "number"
            }
          }
        },
        "returns": "CreditTransactionsResponse",
        "safety": "read"
      },
      "adminListPlans": {
        "description": "adminListPlans on the billing resource.",
        "params": {
          "params": "AdminListPlansParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "include_inactive": {
              "type": "boolean"
            },
            "include_deleted": {
              "type": "boolean"
            }
          }
        },
        "returns": "Plan[]",
        "safety": "read"
      },
      "adminCancelSubscription": {
        "description": "Platform admin cancellation of a subscription. Supports both deferred and immediate modes. Immediate cancellation takes effect immediately, while deferred cancellation takes effect at the end of the current billing period.",
        "params": {
          "params": "AdminCancelSubscriptionRequest — - Admin cancellation request with account, organization, mode, and optional reason"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "mode": {
              "type": "string",
              "enum": [
                "deferred",
                "immediate"
              ]
            },
            "reason": {
              "type": "string"
            }
          },
          "required": [
            "mode"
          ]
        },
        "returns": "CancelSubscriptionResponse",
        "safety": "write"
      },
      "getOnboardingStatus": {
        "description": "getOnboardingStatus on the billing resource.",
        "params": {
          "accountId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "OnboardingStatus",
        "safety": "read"
      },
      "getSetupIntent": {
        "description": "getSetupIntent on the billing resource.",
        "params": {
          "accountId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "SetupIntentResponse",
        "safety": "read"
      },
      "completeOnboardingPayment": {
        "description": "completeOnboardingPayment on the billing resource.",
        "params": {
          "accountId": "string",
          "params": "CompleteOnboardingPaymentRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "payment_method_id": {
              "type": "string"
            }
          },
          "required": [
            "payment_method_id"
          ]
        },
        "returns": "CompleteOnboardingResponse",
        "safety": "write"
      },
      "completeOnboardingComplimentary": {
        "description": "completeOnboardingComplimentary on the billing resource.",
        "params": {
          "accountId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "CompleteOnboardingResponse",
        "safety": "write"
      }
    }
  },
  "bridge": {
    "description": "Device bridge transport — real-time JSON-RPC connection to desktop/web clients.",
    "methods": {
      "getStatus": {
        "description": "Get the bridge status for a given device instance.",
        "params": {
          "deviceId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            }
          },
          "required": [
            "deviceId"
          ]
        },
        "returns": "BridgeStatus",
        "safety": "read"
      },
      "listConnectedDevices": {
        "description": "List devices currently connected to the bridge for the caller's account. Used by clients to discover available local agents (ShogunChop instances) without talking to ShogunChop directly.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ConnectedDevice[]",
        "safety": "read"
      },
      "isConnected": {
        "description": "Whether the device's bridge connection is live and usable right now. This reads the workflow-backed `connected` flag from {@link getStatus} — the authoritative signal that device-routed requests (file tools, MCP calls, LLM completions) will actually reach the daemon. Note this is stricter than {@link listConnectedDevices}, which reflects the `active_connections` table and can briefly report a device whose connection workflow has not yet come up after a cold start.",
        "params": {
          "deviceId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            }
          },
          "required": [
            "deviceId"
          ]
        },
        "returns": "boolean",
        "safety": "read"
      },
      "waitForConnected": {
        "description": "Poll {@link getStatus} until the device reports `connected`, then resolve with the final status. Throws if the device does not become connected within `timeoutMs`. Intended as a precondition gate for callers that route work to a device (tests exercising local tools, client UI waiting for a daemon to come up). It deliberately fails loudly rather than resolving to a \"not connected\" value, so a never-connecting device surfaces as an explicit error instead of a silently degraded path.",
        "params": {
          "deviceId": "string",
          "opts": "{ timeoutMs?: number; intervalMs?: number } (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            },
            "opts": {
              "type": "object",
              "properties": {
                "timeoutMs": {
                  "type": "number"
                },
                "intervalMs": {
                  "type": "number"
                }
              }
            }
          },
          "required": [
            "deviceId"
          ]
        },
        "returns": "BridgeStatus",
        "safety": "read"
      }
    }
  },
  "context.sources": {
    "description": "Context sources — the user-managed registry of context blocks the agent reads every turn: list, create, update, delete, reorder.",
    "methods": {
      "list": {
        "description": "List every source resolvable for the caller — account-scoped, the caller's own user-scoped, and (when `workspaceId` is given) that workspace's — in render order, enabled and disabled alike. Omitting `workspaceId` is the workspace-less lane: workspace-scoped sources are not resolved at all, matching what a workspace-less session's agent sees.",
        "params": {
          "workspaceId": "string (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "type": "string"
            }
          }
        },
        "returns": "ListContextSourcesResponse",
        "safety": "read"
      },
      "create": {
        "description": "Create a source, appended at the end of its scope bucket. Rejections (all before anything is stored): a deferred v2 placement or a seed-only `data_source`/`render_mode` → 422; a `specific-system-prompt` without a `target_persona` resolving to an account persona → 422; a body over 10000 characters → 422; the 500-sources-per-account cap → 422; a duplicate name within the scope → 409.",
        "params": {
          "params": "CreateContextSourceRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string"
            },
            "scope": {
              "type": "string",
              "enum": [
                "account",
                "user",
                "workspace"
              ]
            },
            "type": {
              "type": "string",
              "enum": [
                "environment",
                "user-info",
                "rules",
                "memory",
                "knowledge"
              ]
            },
            "data_source": {
              "type": "string",
              "enum": [
                "loaded-inline",
                "referenced"
              ],
              "description": "`loader` is rejected with a 422."
            },
            "placement": {
              "type": "string",
              "enum": [
                "pervasive",
                "specific-system-prompt"
              ],
              "description": "The deferred v2 placements are rejected with a 422 (context ADR-005)."
            },
            "render_mode": {
              "type": "string",
              "enum": [
                "propagate"
              ],
              "description": "`per-agent` is rejected with a 422. Defaults to `propagate`."
            },
            "workspace_id": {
              "type": "string"
            },
            "target_persona": {
              "type": "string"
            },
            "body": {
              "type": "string"
            },
            "reference_path": {
              "type": "string"
            },
            "enabled": {
              "type": "boolean"
            }
          },
          "required": [
            "name",
            "scope",
            "type",
            "data_source",
            "placement"
          ]
        },
        "returns": "ContextSource",
        "safety": "write"
      },
      "update": {
        "description": "Patch a source. Omitted fields are unchanged.",
        "params": {
          "sourceId": "string",
          "params": "UpdateContextSourceRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "sourceId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "name": {
                  "type": "string"
                },
                "body": {
                  "type": "string"
                },
                "enabled": {
                  "type": "boolean"
                },
                "reference_path": {
                  "type": "string"
                },
                "target_persona": {
                  "type": "string"
                },
                "scope": {
                  "type": "string",
                  "enum": [
                    "account",
                    "user",
                    "workspace"
                  ]
                },
                "type": {
                  "type": "string",
                  "enum": [
                    "environment",
                    "user-info",
                    "rules",
                    "memory",
                    "knowledge"
                  ]
                },
                "data_source": {
                  "type": "string",
                  "enum": [
                    "loaded-inline",
                    "referenced",
                    "loader"
                  ]
                },
                "placement": {
                  "type": "string",
                  "enum": [
                    "pervasive",
                    "specific-system-prompt",
                    "message-history",
                    "step-scoped"
                  ]
                },
                "render_mode": {
                  "type": "string",
                  "enum": [
                    "propagate",
                    "per-agent"
                  ]
                }
              }
            }
          },
          "required": [
            "sourceId",
            "params"
          ]
        },
        "returns": "ContextSource",
        "safety": "write"
      },
      "delete": {
        "description": "Delete a source and re-densify its scope bucket.",
        "params": {
          "sourceId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "sourceId": {
              "type": "string"
            }
          },
          "required": [
            "sourceId"
          ]
        },
        "returns": "{ deleted: boolean }",
        "safety": "write"
      },
      "move": {
        "description": "Move a source to a 0-based position within its scope bucket's USER BAND and return the re-densified list.",
        "params": {
          "sourceId": "string",
          "params": "MoveContextSourceRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "sourceId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "position": {
                  "type": "number"
                },
                "workspace_id": {
                  "type": "string"
                }
              },
              "required": [
                "position"
              ]
            }
          },
          "required": [
            "sourceId",
            "params"
          ]
        },
        "returns": "ListContextSourcesResponse",
        "safety": "write"
      },
      "health": {
        "description": "Account-scoped latest per-source health from the event-sourced `context_health` store. Never-observed sources are ABSENT — the client derives \"not yet observed\" from that absence. No assemble / substrate I/O.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ListContextSourcesHealthResponse",
        "safety": "read"
      }
    }
  },
  "context.profile": {
    "description": "The caller's own user profile — the typed \"about me\" facts rendered into every system prompt: read (stored + effective) and update.",
    "methods": {
      "get": {
        "description": "Read the caller's stored profile plus the precedence-resolved `effective` view (what the agent is actually told, and which rung each value came from).",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "UserProfileView",
        "safety": "read"
      },
      "update": {
        "description": "Update the caller's OWN profile. Partial: omitted fields are left unchanged, and a field sent as `''` is cleared back to unset.",
        "params": {
          "patch": "UserProfilePatch"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "preferred_name": {
              "type": "string"
            },
            "pronouns": {
              "type": "string"
            },
            "role_title": {
              "type": "string"
            },
            "timezone": {
              "type": "string"
            },
            "expertise": {
              "type": "string"
            },
            "working_hours": {
              "type": "string"
            },
            "bio": {
              "type": "string"
            }
          }
        },
        "returns": "UserProfile",
        "safety": "write"
      }
    }
  },
  "context.workspace": {
    "description": "The live workspace context read — what the agent is told about a workspace: environment facts, framework files in precedence order, advisories, skills, workspace sources, and a fingerprint.",
    "methods": {
      "get": {
        "description": "Read a workspace's agent context: environment facts, the active convention frameworks in precedence order (sot → claude → cursor → codex), the briefing/rule/memory files the agent receives, skipped-file advisories, the skills index, the workspace-scoped context sources, and a `fingerprint`. No client device is needed; the server resolves the workspace's substrate. A sleeping workstation is woken once. A workspace outside the caller's account is a 404. Compare `fingerprint` with the `workspace.contextObserved` bridge notification each agent run publishes (`BridgeEventStream.onWorkspaceContextObserved` on web, the `workspace.contextObserved` event on the desktop `/events` stream) and read again when they differ.",
        "params": {
          "workspaceId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "type": "string"
            }
          },
          "required": [
            "workspaceId"
          ]
        },
        "returns": "WorkspaceContext",
        "safety": "read"
      }
    }
  },
  "device": {
    "description": "Manage devices and workspaces — registration, initialization, and lifecycle operations.",
    "methods": {
      "healthCheck": {
        "description": "Liveness probe. `unauthenticated`: `/healthz` is gateway-exempt (`authExemptPrefixes` in `go/internal/api/middleware.go`) and must answer before a user has any credential — that is what a health check is for.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "{ status: string }",
        "safety": "read"
      },
      "createDevice": {
        "description": "createDevice on the device resource.",
        "params": {
          "params": "CreateDeviceRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "id": {
              "type": "string"
            },
            "name": {
              "type": "string"
            },
            "kind": {
              "type": "string",
              "enum": [
                "desktop",
                "web"
              ]
            }
          },
          "required": [
            "id",
            "name",
            "kind"
          ]
        },
        "returns": "Device",
        "safety": "write"
      },
      "registerDevice": {
        "description": "registerDevice on the device resource.",
        "params": {
          "params": "RegisterDeviceRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "device_id": {
              "type": "string"
            },
            "name": {
              "type": "string"
            },
            "kind": {
              "type": "string",
              "enum": [
                "desktop",
                "web"
              ]
            }
          },
          "required": [
            "device_id",
            "name",
            "kind"
          ]
        },
        "returns": "Device",
        "safety": "write"
      }
    }
  },
  "errors": {
    "description": "Error querying and error analytics.",
    "methods": {
      "listErrors": {
        "description": "List error events for the caller's tenant. Set `apply_mute: true` to drop rows whose `service_origin` the caller has muted via `notifications.setPrefs`, counting `total` under the same predicate. It defaults to false, so a caller that omits it sees every row regardless of mute preferences. Mute is non-lossy — a muted row is still stored and returned once the origin is unmuted.",
        "params": {
          "params": "ListErrorsRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "start_time": {
              "type": "string"
            },
            "end_time": {
              "type": "string"
            },
            "preset": {
              "type": "string",
              "enum": [
                "1h",
                "24h",
                "7d",
                "30d"
              ]
            },
            "severity": {
              "type": "string"
            },
            "service_origin": {
              "type": "string"
            },
            "error_code": {
              "type": "string"
            },
            "workspace_id": {
              "type": "string"
            },
            "limit": {
              "type": "number"
            },
            "offset": {
              "type": "number"
            },
            "apply_mute": {
              "type": "boolean"
            }
          }
        },
        "returns": "ListErrorsResponse",
        "safety": "read"
      },
      "errorCounts": {
        "description": "errorCounts on the errors resource.",
        "params": {
          "params": "ErrorCountsRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "start_time": {
              "type": "string"
            },
            "end_time": {
              "type": "string"
            },
            "preset": {
              "type": "string",
              "enum": [
                "1h",
                "24h",
                "7d",
                "30d"
              ]
            },
            "severity": {
              "type": "string"
            },
            "service_origin": {
              "type": "string"
            },
            "time_bucket": {
              "type": "string"
            }
          }
        },
        "returns": "ErrorCountsResponse",
        "safety": "read"
      },
      "dismiss": {
        "description": "Dismiss (clear) error events so they no longer surface in listErrors.",
        "params": {
          "params": "DismissErrorsRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "event_ids": {
              "type": "array",
              "items": {
                "type": "string"
              }
            },
            "all": {
              "type": "boolean"
            }
          }
        },
        "returns": "MutateErrorsResponse",
        "safety": "write"
      },
      "markRead": {
        "description": "Set read/unread state on error events.",
        "params": {
          "params": "SetErrorsReadRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "event_ids": {
              "type": "array",
              "items": {
                "type": "string"
              }
            },
            "all": {
              "type": "boolean"
            },
            "read": {
              "type": "boolean"
            }
          },
          "required": [
            "read"
          ]
        },
        "returns": "MutateErrorsResponse",
        "safety": "write"
      }
    }
  },
  "events": {
    "description": "Event stream and event management.",
    "methods": {
      "ingest": {
        "description": "ingest on the events resource.",
        "params": {
          "params": "IngestEventsRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "events": {
              "type": "array",
              "items": {
                "type": "object",
                "properties": {},
                "additionalProperties": {}
              }
            }
          },
          "required": [
            "events"
          ]
        },
        "returns": "IngestEventsResponse",
        "safety": "write"
      },
      "getSummary": {
        "description": "getSummary on the events resource.",
        "params": {
          "params": "EventSummaryParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "time_range": {
              "type": "string"
            },
            "time_bucket": {
              "type": "string"
            }
          }
        },
        "returns": "Record<string, unknown>",
        "safety": "read"
      },
      "getCounts": {
        "description": "getCounts on the events resource.",
        "params": {
          "params": "EventCountsParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "time_range": {
              "type": "string"
            },
            "event_name": {
              "type": "string"
            },
            "time_bucket": {
              "type": "string"
            },
            "limit": {
              "type": "number"
            }
          }
        },
        "returns": "Record<string, unknown>",
        "safety": "read"
      },
      "getTopEvents": {
        "description": "getTopEvents on the events resource.",
        "params": {
          "params": "TopEventsParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "time_range": {
              "type": "string"
            },
            "limit": {
              "type": "number"
            }
          }
        },
        "returns": "Record<string, unknown>",
        "safety": "read"
      },
      "getActiveUsers": {
        "description": "getActiveUsers on the events resource.",
        "params": {
          "params": "ActiveUsersParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "time_range": {
              "type": "string"
            },
            "time_bucket": {
              "type": "string"
            },
            "limit": {
              "type": "number"
            }
          }
        },
        "returns": "Record<string, unknown>",
        "safety": "read"
      }
    }
  },
  "feed": {
    "description": "Live Feed HTTP — publish, list the home timeline, get a post or thread, engage, mark seen, delete. connect() returns a LiveFeed handle.",
    "methods": {
      "publish": {
        "description": "Publish an original, reply, quote, or repost. Poster is the authenticated user.",
        "params": {
          "params": "PublishFeedPostRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "kind": {
              "type": "string",
              "enum": [
                "original",
                "reply",
                "quote",
                "repost"
              ]
            },
            "body": {
              "type": "string"
            },
            "parent_id": {
              "type": "string"
            },
            "quote_of_id": {
              "type": "string"
            },
            "repost_of_id": {
              "type": "string"
            },
            "source_kind": {
              "type": "string",
              "enum": [
                "session",
                "ticket",
                "automation_run",
                "context_source",
                "media_bulk"
              ]
            },
            "source_id": {
              "type": "string"
            }
          },
          "required": [
            "kind"
          ]
        },
        "returns": "FeedPost",
        "safety": "write"
      },
      "listHome": {
        "description": "One bidirectional keyset page of the account home timeline. Cache consult: a stored page window serves instantly and revalidates behind the return.",
        "params": {
          "params": "ListFeedHomeRequest (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "after": {
              "type": "object",
              "properties": {
                "created_at": {
                  "type": "string"
                },
                "id": {
                  "type": "string"
                }
              },
              "required": [
                "created_at",
                "id"
              ]
            },
            "before": {
              "type": "object",
              "properties": {
                "created_at": {
                  "type": "string"
                },
                "id": {
                  "type": "string"
                }
              },
              "required": [
                "created_at",
                "id"
              ]
            },
            "limit": {
              "type": "number"
            }
          }
        },
        "returns": "ListFeedHomeResponse",
        "safety": "read"
      },
      "getPost": {
        "description": "Hydrate one post by permalink id.",
        "params": {
          "postId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "postId": {
              "type": "string"
            }
          },
          "required": [
            "postId"
          ]
        },
        "returns": "FeedPost",
        "safety": "read"
      },
      "getThread": {
        "description": "One-shot thread: root, ancestors, focused post, latest 20 replies.",
        "params": {
          "postId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "postId": {
              "type": "string"
            }
          },
          "required": [
            "postId"
          ]
        },
        "returns": "FeedThread",
        "safety": "read"
      },
      "engage": {
        "description": "Toggle like, bookmark, or repost on a post.",
        "params": {
          "postId": "string",
          "params": "EngageFeedPostRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "postId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "op": {
                  "type": "string",
                  "enum": [
                    "repost",
                    "like",
                    "bookmark"
                  ]
                }
              },
              "required": [
                "op"
              ]
            }
          },
          "required": [
            "postId",
            "params"
          ]
        },
        "returns": "FeedPost",
        "safety": "write"
      },
      "markSeen": {
        "description": "Advance the visit watermark; returns remaining unseen home count.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "MarkFeedSeenResponse",
        "safety": "write"
      },
      "delete": {
        "description": "Soft-delete. Author / account owner / org take-down.",
        "params": {
          "postId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "postId": {
              "type": "string"
            }
          },
          "required": [
            "postId"
          ]
        },
        "returns": "FeedPost",
        "safety": "write"
      },
      "subscribe": {
        "description": "Kick Feed.Replay to this device after the optional cursor.",
        "params": {
          "params": "FeedSubscribeRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "device_id": {
              "type": "string"
            },
            "after": {
              "type": "object",
              "properties": {
                "created_at": {
                  "type": "string"
                },
                "id": {
                  "type": "string"
                }
              },
              "required": [
                "created_at",
                "id"
              ]
            }
          },
          "required": [
            "device_id"
          ]
        },
        "returns": "{ status: string }",
        "safety": "write"
      },
      "unsubscribe": {
        "description": "Drop replay interest for this device.",
        "params": {
          "deviceId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            }
          },
          "required": [
            "deviceId"
          ]
        },
        "returns": "{ status: string }",
        "safety": "write"
      },
      "connect": {
        "description": "Open a LiveFeed handle: bounded home window, loadOlder/loadNewer, publish apply, unseen watermark, and subscribe/replay owned by the host that calls `subscribe` after connect (feed ADR-008).",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "LiveFeed",
        "safety": "read"
      }
    }
  },
  "flags": {
    "description": "Evaluated feature flags for the caller — server-evaluated booleans only; no client-facing write surface.",
    "methods": {
      "evaluate": {
        "description": "Evaluate every published frontend/fullstack flag for the caller (identity from the session claims). An absent key is off; an evaluation failure on the server surfaces as a thrown error, never a silent false (flags ADR-001).",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "EvaluatedFlags",
        "safety": "read"
      },
      "definitions": {
        "description": "Read the published definitions document — generation, flag definitions, cohorts and the publish-history ring — for the platform-admin Releases viewer. Requires platform-admin scope; a non-admin principal is refused by the backend.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "PlatformFlagsDocument",
        "safety": "read"
      }
    }
  },
  "integrations": {
    "description": "Third-party integrations, webhooks, and external service connections.",
    "methods": {
      "createDefinition": {
        "description": "createDefinition on the integrations resource.",
        "params": {
          "params": "CreateDefinitionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "slug": {
              "type": "string"
            },
            "name": {
              "type": "string"
            },
            "description": {
              "type": "string"
            },
            "category": {
              "type": "string"
            },
            "auth_type": {
              "type": "string"
            },
            "icon_url": {
              "type": "string"
            }
          },
          "required": [
            "slug",
            "name"
          ]
        },
        "returns": "IntegrationDefinition",
        "safety": "write"
      },
      "listDefinitions": {
        "description": "listDefinitions on the integrations resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "IntegrationDefinition[]",
        "safety": "read"
      },
      "getDefinition": {
        "description": "getDefinition on the integrations resource.",
        "params": {
          "definitionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "definitionId": {
              "type": "string"
            }
          },
          "required": [
            "definitionId"
          ]
        },
        "returns": "IntegrationDefinition",
        "safety": "read"
      },
      "updateDefinition": {
        "description": "updateDefinition on the integrations resource.",
        "params": {
          "definitionId": "string",
          "params": "UpdateDefinitionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "definitionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "name": {
                  "type": "string"
                },
                "description": {
                  "type": "string"
                },
                "category": {
                  "type": "string"
                },
                "icon_url": {
                  "type": "string"
                }
              }
            }
          },
          "required": [
            "definitionId",
            "params"
          ]
        },
        "returns": "IntegrationDefinition",
        "safety": "write"
      },
      "deleteDefinition": {
        "description": "deleteDefinition on the integrations resource.",
        "params": {
          "definitionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "definitionId": {
              "type": "string"
            }
          },
          "required": [
            "definitionId"
          ]
        },
        "returns": "IntegrationDefinition",
        "safety": "write"
      },
      "publishVersion": {
        "description": "publishVersion on the integrations resource.",
        "params": {
          "definitionId": "string",
          "params": "IntegrationPublishVersionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "definitionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "dsl_content": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                },
                "changelog": {
                  "type": "string"
                }
              },
              "required": [
                "dsl_content"
              ]
            }
          },
          "required": [
            "definitionId",
            "params"
          ]
        },
        "returns": "DefinitionVersion",
        "safety": "write"
      },
      "listVersions": {
        "description": "listVersions on the integrations resource.",
        "params": {
          "definitionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "definitionId": {
              "type": "string"
            }
          },
          "required": [
            "definitionId"
          ]
        },
        "returns": "DefinitionVersion[]",
        "safety": "read"
      },
      "getVersion": {
        "description": "getVersion on the integrations resource.",
        "params": {
          "definitionId": "string",
          "versionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "definitionId": {
              "type": "string"
            },
            "versionId": {
              "type": "string"
            }
          },
          "required": [
            "definitionId",
            "versionId"
          ]
        },
        "returns": "DefinitionVersion",
        "safety": "read"
      },
      "createConnection": {
        "description": "createConnection on the integrations resource.",
        "params": {
          "params": "CreateConnectionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "definition_id": {
              "type": "string"
            },
            "name": {
              "type": "string"
            },
            "workspace_id": {
              "type": "string"
            },
            "base_url_override": {
              "type": "string"
            },
            "slug_override": {
              "type": "string"
            }
          },
          "required": [
            "definition_id",
            "name"
          ]
        },
        "returns": "IntegrationConnection",
        "safety": "write"
      },
      "getConnection": {
        "description": "getConnection on the integrations resource.",
        "params": {
          "connectionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "connectionId": {
              "type": "string"
            }
          },
          "required": [
            "connectionId"
          ]
        },
        "returns": "IntegrationConnection",
        "safety": "read"
      },
      "listConnections": {
        "description": "listConnections on the integrations resource.",
        "params": {
          "params": "ListConnectionsParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "status": {
              "type": "string"
            },
            "workspace_id": {
              "type": "string"
            }
          }
        },
        "returns": "IntegrationConnection[]",
        "safety": "read"
      },
      "updateConnection": {
        "description": "updateConnection on the integrations resource.",
        "params": {
          "connectionId": "string",
          "params": "UpdateConnectionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "connectionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "name": {
                  "type": "string"
                },
                "base_url_override": {
                  "type": "string"
                },
                "rate_limit_rps_override": {
                  "type": "number"
                },
                "rate_limit_burst_override": {
                  "type": "number"
                }
              }
            }
          },
          "required": [
            "connectionId",
            "params"
          ]
        },
        "returns": "IntegrationConnection",
        "safety": "write"
      },
      "deleteConnection": {
        "description": "deleteConnection on the integrations resource.",
        "params": {
          "connectionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "connectionId": {
              "type": "string"
            }
          },
          "required": [
            "connectionId"
          ]
        },
        "returns": "IntegrationConnection",
        "safety": "write"
      },
      "disableConnection": {
        "description": "disableConnection on the integrations resource.",
        "params": {
          "connectionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "connectionId": {
              "type": "string"
            }
          },
          "required": [
            "connectionId"
          ]
        },
        "returns": "IntegrationConnection",
        "safety": "write"
      },
      "enableConnection": {
        "description": "enableConnection on the integrations resource.",
        "params": {
          "connectionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "connectionId": {
              "type": "string"
            }
          },
          "required": [
            "connectionId"
          ]
        },
        "returns": "IntegrationConnection",
        "safety": "write"
      },
      "reauthenticateConnection": {
        "description": "reauthenticateConnection on the integrations resource.",
        "params": {
          "connectionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "connectionId": {
              "type": "string"
            }
          },
          "required": [
            "connectionId"
          ]
        },
        "returns": "IntegrationConnection",
        "safety": "write"
      },
      "upgradeConnection": {
        "description": "upgradeConnection on the integrations resource.",
        "params": {
          "connectionId": "string",
          "params": "UpgradeConnectionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "connectionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "target_version_id": {
                  "type": "string"
                }
              },
              "required": [
                "target_version_id"
              ]
            }
          },
          "required": [
            "connectionId",
            "params"
          ]
        },
        "returns": "IntegrationConnection",
        "safety": "write"
      },
      "authenticateAPIKey": {
        "description": "authenticateAPIKey on the integrations resource.",
        "params": {
          "connectionId": "string",
          "params": "AuthenticateAPIKeyRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "connectionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "api_key": {
                  "type": "string"
                }
              },
              "required": [
                "api_key"
              ]
            }
          },
          "required": [
            "connectionId",
            "params"
          ]
        },
        "returns": "IntegrationConnection",
        "safety": "write"
      },
      "authenticateClientCredentials": {
        "description": "authenticateClientCredentials on the integrations resource.",
        "params": {
          "connectionId": "string",
          "params": "AuthenticateClientCredsRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "connectionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "client_id": {
                  "type": "string"
                },
                "client_secret": {
                  "type": "string"
                }
              },
              "required": [
                "client_id",
                "client_secret"
              ]
            }
          },
          "required": [
            "connectionId",
            "params"
          ]
        },
        "returns": "IntegrationConnection",
        "safety": "write"
      },
      "initiateOAuth2": {
        "description": "initiateOAuth2 on the integrations resource.",
        "params": {
          "connectionId": "string",
          "params": "InitiateOAuth2Request"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "connectionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "redirect_uri": {
                  "type": "string"
                }
              },
              "required": [
                "redirect_uri"
              ]
            }
          },
          "required": [
            "connectionId",
            "params"
          ]
        },
        "returns": "InitiateOAuth2Response",
        "safety": "write"
      },
      "testAction": {
        "description": "testAction on the integrations resource.",
        "params": {
          "connectionId": "string",
          "params": "TestActionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "connectionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "action_key": {
                  "type": "string"
                },
                "parameters": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                }
              },
              "required": [
                "action_key"
              ]
            }
          },
          "required": [
            "connectionId",
            "params"
          ]
        },
        "returns": "ActionExecutionResponse",
        "safety": "read"
      },
      "getConnectionLogs": {
        "description": "getConnectionLogs on the integrations resource.",
        "params": {
          "connectionId": "string",
          "params": "GetConnectionLogsParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "connectionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "limit": {
                  "type": "number"
                },
                "offset": {
                  "type": "number"
                },
                "status": {
                  "type": "string"
                }
              }
            }
          },
          "required": [
            "connectionId"
          ]
        },
        "returns": "ExecutionLogEntry[]",
        "safety": "read"
      },
      "getConnectionHealth": {
        "description": "getConnectionHealth on the integrations resource.",
        "params": {
          "connectionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "connectionId": {
              "type": "string"
            }
          },
          "required": [
            "connectionId"
          ]
        },
        "returns": "IntegrationConnection",
        "safety": "read"
      },
      "searchCatalog": {
        "description": "searchCatalog on the integrations resource.",
        "params": {
          "params": "SearchCatalogParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "q": {
              "type": "string"
            },
            "category": {
              "type": "string"
            },
            "scope": {
              "type": "string"
            }
          }
        },
        "returns": "CatalogSearchResponse",
        "safety": "read"
      },
      "getCatalogEntry": {
        "description": "getCatalogEntry on the integrations resource.",
        "params": {
          "slug": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "slug": {
              "type": "string"
            }
          },
          "required": [
            "slug"
          ]
        },
        "returns": "CatalogEntry",
        "safety": "read"
      },
      "createWebhookConfig": {
        "description": "createWebhookConfig on the integrations resource.",
        "params": {
          "connectionId": "string",
          "params": "CreateWebhookConfigRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "connectionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "webhook_events": {
                  "type": "array",
                  "items": {
                    "type": "string"
                  }
                },
                "signature_header": {
                  "type": "string"
                },
                "signature_algorithm": {
                  "type": "string"
                }
              },
              "required": [
                "webhook_events"
              ]
            }
          },
          "required": [
            "connectionId",
            "params"
          ]
        },
        "returns": "WebhookConfig",
        "safety": "write"
      },
      "getWebhookConfig": {
        "description": "getWebhookConfig on the integrations resource.",
        "params": {
          "connectionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "connectionId": {
              "type": "string"
            }
          },
          "required": [
            "connectionId"
          ]
        },
        "returns": "WebhookConfig",
        "safety": "read"
      },
      "deleteWebhookConfig": {
        "description": "deleteWebhookConfig on the integrations resource.",
        "params": {
          "connectionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "connectionId": {
              "type": "string"
            }
          },
          "required": [
            "connectionId"
          ]
        },
        "returns": "{ deleted: boolean }",
        "safety": "write"
      },
      "listWebhookDeliveries": {
        "description": "listWebhookDeliveries on the integrations resource.",
        "params": {
          "connectionId": "string",
          "params": "ListWebhookDeliveriesParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "connectionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "limit": {
                  "type": "number"
                },
                "offset": {
                  "type": "number"
                }
              }
            }
          },
          "required": [
            "connectionId"
          ]
        },
        "returns": "WebhookDelivery[]",
        "safety": "read"
      },
      "adminStartHealthScheduler": {
        "description": "adminStartHealthScheduler on the integrations resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "unknown",
        "safety": "write"
      },
      "adminMetrics": {
        "description": "adminMetrics on the integrations resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "IntegrationMetrics",
        "safety": "read"
      }
    }
  },
  "mcp": {
    "description": "MCP (Model Context Protocol) server and transport management.",
    "methods": {
      "register": {
        "description": "register on the mcp resource.",
        "params": {
          "deviceId": "string",
          "params": "AppRegisterMCPRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "mcp_id": {
                  "type": "string"
                },
                "name": {
                  "type": "string"
                },
                "description": {
                  "type": "string"
                },
                "transport": {
                  "type": "string",
                  "enum": [
                    "backend-local",
                    "http",
                    "bridge",
                    "integration",
                    "provider-native"
                  ]
                },
                "config": {
                  "type": "object",
                  "properties": {
                    "url": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "string"
                        }
                      ]
                    },
                    "command": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "string"
                        }
                      ]
                    },
                    "args": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "array",
                          "items": {
                            "type": "string"
                          }
                        }
                      ]
                    },
                    "env": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "object",
                          "properties": {},
                          "additionalProperties": {
                            "type": "string"
                          }
                        }
                      ]
                    },
                    "headers": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "object",
                          "properties": {},
                          "additionalProperties": {
                            "type": "string"
                          }
                        }
                      ]
                    },
                    "tools_enabled": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "array",
                          "items": {
                            "type": "string"
                          }
                        }
                      ]
                    }
                  }
                }
              },
              "required": [
                "mcp_id",
                "name",
                "transport",
                "config"
              ]
            }
          },
          "required": [
            "deviceId",
            "params"
          ]
        },
        "returns": "MCPServer",
        "safety": "write"
      },
      "list": {
        "description": "list on the mcp resource.",
        "params": {
          "deviceId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            }
          },
          "required": [
            "deviceId"
          ]
        },
        "returns": "MCPServer[]",
        "safety": "read"
      },
      "update": {
        "description": "update on the mcp resource.",
        "params": {
          "deviceId": "string",
          "mcpId": "string",
          "params": "AppUpdateMCPRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            },
            "mcpId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "enabled": {
                  "type": "boolean"
                },
                "config": {
                  "type": "object",
                  "properties": {
                    "url": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "string"
                        }
                      ]
                    },
                    "command": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "string"
                        }
                      ]
                    },
                    "args": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "array",
                          "items": {
                            "type": "string"
                          }
                        }
                      ]
                    },
                    "env": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "object",
                          "properties": {},
                          "additionalProperties": {
                            "type": "string"
                          }
                        }
                      ]
                    },
                    "headers": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "object",
                          "properties": {},
                          "additionalProperties": {
                            "type": "string"
                          }
                        }
                      ]
                    },
                    "tools_enabled": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "array",
                          "items": {
                            "type": "string"
                          }
                        }
                      ]
                    }
                  }
                }
              }
            }
          },
          "required": [
            "deviceId",
            "mcpId",
            "params"
          ]
        },
        "returns": "MCPServer",
        "safety": "write"
      },
      "delete": {
        "description": "delete on the mcp resource.",
        "params": {
          "deviceId": "string",
          "mcpId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            },
            "mcpId": {
              "type": "string"
            }
          },
          "required": [
            "deviceId",
            "mcpId"
          ]
        },
        "returns": "MCPServer",
        "safety": "write"
      },
      "getToolCatalog": {
        "description": "getToolCatalog on the mcp resource.",
        "params": {
          "deviceId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            }
          },
          "required": [
            "deviceId"
          ]
        },
        "returns": "MCPToolCatalog",
        "safety": "read"
      },
      "listTools": {
        "description": "listTools on the mcp resource.",
        "params": {
          "deviceId": "string",
          "mcpId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            },
            "mcpId": {
              "type": "string"
            }
          },
          "required": [
            "deviceId",
            "mcpId"
          ]
        },
        "returns": "MCPTool[]",
        "safety": "read"
      },
      "listTenantMCPs": {
        "description": "listTenantMCPs on the mcp resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "MCPServer[]",
        "safety": "read"
      },
      "listTenantMCPTools": {
        "description": "listTenantMCPTools on the mcp resource.",
        "params": {
          "mcpId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "mcpId": {
              "type": "string"
            }
          },
          "required": [
            "mcpId"
          ]
        },
        "returns": "MCPTool[]",
        "safety": "read"
      },
      "getTenantToolCatalog": {
        "description": "getTenantToolCatalog on the mcp resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "MCPToolCatalog",
        "safety": "read"
      }
    }
  },
  "media": {
    "description": "Account-global versioned artifacts — list, get, content, upload, restore, Trash (delete/undelete/purge/emptyTrash), bulk, and storage occupancy (?account_id=). A miss is 404, never 403.",
    "methods": {
      "list": {
        "description": "Living (or trashed) page for the caller account.",
        "params": {
          "params": "ListMediaParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "trashed": {
              "type": "boolean"
            },
            "kind": {
              "type": "string",
              "enum": [
                "diagram",
                "upload"
              ]
            },
            "session_id": {
              "type": "string"
            },
            "project_id": {
              "type": "string"
            },
            "limit": {
              "type": "number"
            },
            "cursor": {
              "type": "string"
            }
          }
        },
        "returns": "ListMediaResponse",
        "safety": "read"
      },
      "get": {
        "description": "One object by id. Unknown or cross-account ids are 404.",
        "params": {
          "mediaId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "mediaId": {
              "type": "string"
            }
          },
          "required": [
            "mediaId"
          ]
        },
        "returns": "MediaObject",
        "safety": "read"
      },
      "usage": {
        "description": "Occupancy for an account. Omit `accountId` for the caller. Any org member may name another same-org account (`?account_id=`).",
        "params": {
          "accountId": "string (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "MediaUsage",
        "safety": "read"
      },
      "bulk": {
        "description": "Move many artifacts to Trash, undelete them, or purge them. Completion is a product Live Feed notice (`poster_kind=system`).",
        "params": {
          "params": "MediaBulkParams"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "action": {
              "type": "string",
              "enum": [
                "trash",
                "undelete",
                "purge"
              ]
            },
            "ids": {
              "type": "array",
              "items": {
                "type": "string"
              }
            }
          },
          "required": [
            "action",
            "ids"
          ]
        },
        "returns": "MediaBulkResult",
        "safety": "write"
      },
      "content": {
        "description": "Live-checked bytes. No version = latest. Living path 404s for Trash.",
        "params": {
          "mediaId": "string",
          "params": "MediaContentParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "mediaId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "format": {
                  "type": "string",
                  "enum": [
                    "svg",
                    "html",
                    "png",
                    "pdf",
                    "jpeg",
                    "webp"
                  ]
                },
                "version": {
                  "type": "number"
                }
              }
            }
          },
          "required": [
            "mediaId"
          ]
        },
        "returns": "ArrayBuffer",
        "safety": "read"
      },
      "restore": {
        "description": "Roll a prior version forward as a new latest.",
        "params": {
          "mediaId": "string",
          "version": "number"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "mediaId": {
              "type": "string"
            },
            "version": {
              "type": "number"
            }
          },
          "required": [
            "mediaId",
            "version"
          ]
        },
        "returns": "MediaRestoreResult",
        "safety": "write"
      },
      "upload": {
        "description": "Upload bytes as a new artifact, or append a version when `mediaId` is set. `bytes.type` is the file type and must be set.",
        "params": {
          "bytes": "Blob — The file itself (a File, a Blob, or raw bytes).",
          "opts": "MediaUploadParams"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "bytes": {
              "type": "string",
              "format": "binary"
            },
            "opts": {
              "type": "object",
              "properties": {
                "label": {
                  "type": "string"
                },
                "sessionId": {
                  "type": "string"
                },
                "projectId": {
                  "type": "string"
                },
                "mediaId": {
                  "type": "string"
                }
              },
              "required": [
                "label"
              ]
            }
          },
          "required": [
            "bytes",
            "opts"
          ]
        },
        "returns": "MediaRestoreResult",
        "safety": "write"
      },
      "delete": {
        "description": "Soft-delete: move the artifact into Trash.",
        "params": {
          "mediaId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "mediaId": {
              "type": "string"
            }
          },
          "required": [
            "mediaId"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "undelete": {
        "description": "Restore a Trash item into the living library.",
        "params": {
          "mediaId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "mediaId": {
              "type": "string"
            }
          },
          "required": [
            "mediaId"
          ]
        },
        "returns": "MediaObject",
        "safety": "write"
      },
      "purge": {
        "description": "Hard-delete one Trash item immediately.",
        "params": {
          "mediaId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "mediaId": {
              "type": "string"
            }
          },
          "required": [
            "mediaId"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "emptyTrash": {
        "description": "Hard-delete every Trash item for the caller account.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "void",
        "safety": "write"
      }
    }
  },
  "notifications": {
    "description": "User notifications — list, per-user read/dismiss, and mute preferences.",
    "methods": {
      "getPrefs": {
        "description": "Get the caller's own mute preferences.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "MutePrefsResponse",
        "safety": "read"
      },
      "setPrefs": {
        "description": "Replace the caller's own mute preferences.",
        "params": {
          "params": "MutePrefsRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "muted_kinds": {
              "type": "array",
              "items": {
                "type": "string"
              }
            },
            "muted_origins": {
              "type": "array",
              "items": {
                "type": "string"
              }
            }
          },
          "required": [
            "muted_kinds",
            "muted_origins"
          ]
        },
        "returns": "MutePrefsResponse",
        "safety": "write"
      },
      "subscribe": {
        "description": "Subscribe a connection to the notification/error push stream + trigger a reconnect gap-fill replay. Live delivery rides AccountScope/UserScope fanout directly (no subscription row gates it); this call (a) authorizes the caller-owned `deviceId` (403 on mismatch) and (b) drains the durable backlog after `after` over the bridge as `notification.events`/`error.events` frames. Omit `after` for a first subscribe (full backlog); on reconnect pass the last `(created_at, id)` cursor so the SDK's `event_id` dedup collapses any live/replay overlap.",
        "params": {
          "deviceId": "string",
          "after": "NotificationCursor (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            },
            "after": {
              "type": "object",
              "properties": {
                "created_at": {
                  "type": "string"
                },
                "id": {
                  "type": "string"
                }
              },
              "required": [
                "created_at",
                "id"
              ]
            }
          },
          "required": [
            "deviceId"
          ]
        },
        "returns": "NotificationSubscribeResponse",
        "safety": "write"
      },
      "unsubscribe": {
        "description": "Unsubscribe a connection from the notification/error push stream.",
        "params": {
          "deviceId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            }
          },
          "required": [
            "deviceId"
          ]
        },
        "returns": "NotificationSubscribeResponse",
        "safety": "write"
      }
    }
  },
  "orgChart": {
    "description": "Organizational chart — positions, hierarchy, and reporting structure.",
    "methods": {
      "getOrgChart": {
        "description": "getOrgChart on the orgChart resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "PositionTree",
        "safety": "read"
      },
      "listPositions": {
        "description": "listPositions on the orgChart resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "Position[]",
        "safety": "read"
      },
      "getPosition": {
        "description": "getPosition on the orgChart resource.",
        "params": {
          "positionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "positionId": {
              "type": "string"
            }
          },
          "required": [
            "positionId"
          ]
        },
        "returns": "Position",
        "safety": "read"
      },
      "createPosition": {
        "description": "Create an OCCUPIED org-chart seat. A position is occupied by exactly one of: a person (`user_id`), or an agent (`persona_id` AND `agent_process_id`). There are no vacant seats and no half-staffed seats — omitting an occupant, or naming a persona without its agent process, is a 422 (work_management ADR-032). The persona must already exist and name a provider, a model, a prompt and a process (persona ADR-020). To create and staff in one call and have the agent process resolved for you, use {@link OrgChartResource.staffPosition}.",
        "params": {
          "params": "CreatePositionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "title": {
              "type": "string"
            },
            "role": {
              "type": "string",
              "enum": [
                "ceo",
                "vp",
                "director",
                "manager",
                "ic"
              ]
            },
            "parent_id": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ],
              "description": "UUID of the parent position, from `listPositions` / `getOrgChart`. Not a title."
            },
            "team_id": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ],
              "description": "Team UUID, from `workManagement.listTeams`. Not a team name."
            },
            "persona_id": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ],
              "description": "Persona UUID, from `persona.listPersonas` or `persona.getPersonaByName`. Not a persona name."
            },
            "agent_process_id": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ],
              "description": "Agent-process UUID, from `agentProcesses.list`. Not a process name."
            }
          },
          "required": [
            "title",
            "role"
          ]
        },
        "returns": "Position",
        "safety": "write"
      },
      "staffPosition": {
        "description": "Make a role executable: resolve an EXISTING persona, resolve an agent process the same way `chat.create` does (explicit id → persona default → seeded Simple), and write both occupants onto the position. This call does NOT invent a persona. A `persona_name` that does not already exist is a 422 naming `persona.createPersonaWithConfig` — create the persona first, with a complete config, then pass its `persona_id` (work_management ADR-032, persona ADR-020). Pass `position_id` to occupy an existing row, or `title` + `role` + `team_id` (and `parent_id` for non-CEO) to create and staff in one call. Never returns a vacant or half-staffed position.",
        "params": {
          "params": "StaffPositionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "position_id": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ],
              "description": "Existing position to occupy. Omit to create a new staffed seat (`title`, `role`, `team_id`, and `parent_id` for non-CEO)."
            },
            "title": {
              "type": "string"
            },
            "role": {
              "type": "string",
              "enum": [
                "ceo",
                "vp",
                "director",
                "manager",
                "ic"
              ]
            },
            "parent_id": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ],
              "description": "UUID of the parent position. Required when creating a non-CEO seat."
            },
            "team_id": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ],
              "description": "Team UUID, from `workManagement.listTeams`. Required when creating."
            },
            "persona_id": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ],
              "description": "Existing persona UUID. Mutually exclusive with `persona_name`."
            },
            "persona_name": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ],
              "description": "Reuse the tenant persona with this name, or create one. When both `persona_id` and `persona_name` are omitted, the position title is used."
            },
            "agent_process_id": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ],
              "description": "Explicit agent-process UUID. When omitted, the backend resolves persona default → seeded Simple. An invalid id fails; there is no fall-through."
            }
          }
        },
        "returns": "Position",
        "safety": "write"
      },
      "updatePosition": {
        "description": "updatePosition on the orgChart resource.",
        "params": {
          "positionId": "string",
          "params": "UpdatePositionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "positionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "title": {
                  "anyOf": [
                    {
                      "type": "null"
                    },
                    {
                      "type": "string"
                    }
                  ]
                },
                "team_id": {
                  "anyOf": [
                    {
                      "type": "null"
                    },
                    {
                      "type": "string"
                    }
                  ],
                  "description": "Team UUID, from `workManagement.listTeams`. Not a team name."
                },
                "persona_id": {
                  "anyOf": [
                    {
                      "type": "null"
                    },
                    {
                      "type": "string"
                    }
                  ],
                  "description": "Persona UUID, from `persona.listPersonas` or `persona.getPersonaByName`. Not a persona name."
                },
                "agent_process_id": {
                  "anyOf": [
                    {
                      "type": "null"
                    },
                    {
                      "type": "string"
                    }
                  ],
                  "description": "Agent-process UUID, from `agentProcesses.list`. Not a process name."
                },
                "clear_occupant": {
                  "type": "boolean"
                },
                "clear_team": {
                  "type": "boolean"
                }
              }
            }
          },
          "required": [
            "positionId",
            "params"
          ]
        },
        "returns": "Position",
        "safety": "write"
      },
      "movePosition": {
        "description": "movePosition on the orgChart resource.",
        "params": {
          "positionId": "string",
          "params": "MovePositionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "positionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "new_parent_id": {
                  "type": "string"
                }
              },
              "required": [
                "new_parent_id"
              ]
            }
          },
          "required": [
            "positionId",
            "params"
          ]
        },
        "returns": "Position",
        "safety": "write"
      },
      "getChildren": {
        "description": "getChildren on the orgChart resource.",
        "params": {
          "positionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "positionId": {
              "type": "string"
            }
          },
          "required": [
            "positionId"
          ]
        },
        "returns": "Position[]",
        "safety": "read"
      },
      "deletePosition": {
        "description": "deletePosition on the orgChart resource.",
        "params": {
          "positionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "positionId": {
              "type": "string"
            }
          },
          "required": [
            "positionId"
          ]
        },
        "returns": "{ deleted: boolean }",
        "safety": "write"
      }
    }
  },
  "persona": {
    "description": "Manage personas, persona groups, persona configs, system personas, and the model catalog.",
    "methods": {
      "getOptions": {
        "description": "getOptions on the persona resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "PersonaOptionsResponse",
        "safety": "read"
      },
      "setOptions": {
        "description": "setOptions on the persona resource.",
        "params": {
          "params": "PersonaOptionsUpdate"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "providers": {
              "type": "array",
              "items": {
                "type": "string"
              }
            },
            "system_prompt_templates": {
              "type": "array",
              "items": {
                "type": "string"
              }
            }
          },
          "required": [
            "providers",
            "system_prompt_templates"
          ]
        },
        "returns": "PersonaOptionsResponse",
        "safety": "write"
      },
      "listGroups": {
        "description": "listGroups on the persona resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "PersonaGroup[]",
        "safety": "read"
      },
      "getGroup": {
        "description": "getGroup on the persona resource.",
        "params": {
          "params": "{ group_id: string }"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "group_id": {
              "type": "string"
            }
          },
          "required": [
            "group_id"
          ]
        },
        "returns": "PersonaGroup",
        "safety": "read"
      },
      "getGroupByName": {
        "description": "getGroupByName on the persona resource.",
        "params": {
          "params": "{ name: string }"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string"
            }
          },
          "required": [
            "name"
          ]
        },
        "returns": "PersonaGroup",
        "safety": "read"
      },
      "createGroup": {
        "description": "createGroup on the persona resource.",
        "params": {
          "params": "CreatePersonaGroupRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string"
            },
            "description": {
              "type": "string"
            }
          },
          "required": [
            "name"
          ]
        },
        "returns": "PersonaGroup",
        "safety": "write"
      },
      "updateGroup": {
        "description": "updateGroup on the persona resource.",
        "params": {
          "params": "UpdatePersonaGroupRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "group_id": {
              "type": "string"
            },
            "name": {
              "type": "string"
            },
            "description": {
              "type": "string"
            }
          },
          "required": [
            "group_id"
          ]
        },
        "returns": "PersonaGroup",
        "safety": "write"
      },
      "deleteGroup": {
        "description": "deleteGroup on the persona resource.",
        "params": {
          "params": "DeletePersonaGroupRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "group_id": {
              "type": "string"
            }
          },
          "required": [
            "group_id"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "listPersonas": {
        "description": "listPersonas on the persona resource.",
        "params": {
          "params": "ListPersonasRequest (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "group_name": {
              "type": "string"
            },
            "include_configs": {
              "type": "boolean"
            }
          }
        },
        "returns": "Persona[]",
        "safety": "read"
      },
      "getPersona": {
        "description": "getPersona on the persona resource.",
        "params": {
          "params": "GetPersonaRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "persona_id": {
              "type": "string"
            }
          },
          "required": [
            "persona_id"
          ]
        },
        "returns": "Persona",
        "safety": "read"
      },
      "getPersonaByName": {
        "description": "getPersonaByName on the persona resource.",
        "params": {
          "params": "GetPersonaByNameRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string"
            }
          },
          "required": [
            "name"
          ]
        },
        "returns": "Persona",
        "safety": "read"
      },
      "createPersonaWithConfig": {
        "description": "Create a persona and its first config in one call. `agent_config` must carry `provider_type`, `model_name`, `system_prompt`, `completion_params` (`{}` inherits the model's defaults) and a process — either `agent_process_id`, or `automatic_process_selection: true` with a NON-EMPTY `automatic_process_ids`. An incomplete config is refused here, before the request leaves the client. `fingerprint` is not a value to invent or to ask a user for: call {@link PersonaResource.getParamSchema} with the SAME `provider_type` and `model_name` the config names, and echo the `fingerprint` it returns. An empty echo is a 422; a stale one is a 409 meaning the model's parameters changed since you read them — read again and retry. Prefer {@link PersonaResource.copySystemPersona} when a catalog persona is close to the role you want: the copy already carries a working provider and model, so nothing has to be chosen.",
        "params": {
          "params": "CreatePersonaWithConfigRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string"
            },
            "agent_config": {
              "type": "object",
              "properties": {
                "system_prompt": {
                  "type": "string"
                },
                "provider_type": {
                  "type": "string",
                  "enum": [
                    "openai",
                    "groq",
                    "lmstudio",
                    "anthropic",
                    "fireworks",
                    "grok",
                    "test"
                  ]
                },
                "model_name": {
                  "type": "string"
                },
                "response_format": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                },
                "completion_params": {
                  "type": "object",
                  "properties": {
                    "temperature": {
                      "type": "number"
                    },
                    "top_p": {
                      "type": "number"
                    },
                    "reasoning_effort": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "string"
                        }
                      ]
                    },
                    "stop": {
                      "type": "array",
                      "items": {
                        "type": "string"
                      }
                    }
                  },
                  "additionalProperties": {},
                  "description": "`{}` is legal and means full inheritance of the model's defaults."
                },
                "agent_process_id": {
                  "type": "string",
                  "description": "Default agent process for this persona: the process-mode session-create resolution chain reads it (explicit request → this default → seeded Simple). Persona create/update validates the reference (visible system or same-account process)."
                },
                "automatic_process_selection": {
                  "type": "boolean"
                },
                "automatic_process_ids": {
                  "type": "array",
                  "items": {
                    "type": "string"
                  }
                }
              },
              "required": [
                "system_prompt",
                "provider_type",
                "model_name",
                "completion_params",
                "automatic_process_selection",
                "automatic_process_ids"
              ]
            },
            "description": {
              "type": "string"
            },
            "group_name": {
              "type": "string"
            },
            "metadata": {
              "type": "object",
              "properties": {},
              "additionalProperties": {}
            },
            "config_metadata": {
              "type": "object",
              "properties": {},
              "additionalProperties": {}
            },
            "fingerprint": {
              "type": "string",
              "description": "Param-schema fingerprint from `getParamSchema`. Unconditional now that every config names a provider/model (persona ADR-020): missing → 422, stale → 409."
            }
          },
          "required": [
            "name",
            "agent_config",
            "fingerprint"
          ]
        },
        "returns": "PersonaConfig",
        "safety": "write"
      },
      "updatePersona": {
        "description": "updatePersona on the persona resource.",
        "params": {
          "params": "UpdatePersonaRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "persona_id": {
              "type": "string"
            },
            "name": {
              "type": "string"
            },
            "description": {
              "type": "string"
            },
            "group_name": {
              "type": "string"
            },
            "metadata": {
              "type": "object",
              "properties": {},
              "additionalProperties": {}
            }
          },
          "required": [
            "persona_id"
          ]
        },
        "returns": "Persona",
        "safety": "write"
      },
      "deletePersona": {
        "description": "deletePersona on the persona resource.",
        "params": {
          "params": "DeletePersonaRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "persona_id": {
              "type": "string"
            }
          },
          "required": [
            "persona_id"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "getConfig": {
        "description": "getConfig on the persona resource.",
        "params": {
          "params": "GetPersonaConfigRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "config_id": {
              "type": "string"
            }
          },
          "required": [
            "config_id"
          ]
        },
        "returns": "PersonaConfig",
        "safety": "read"
      },
      "createNewConfig": {
        "description": "Add a new config version to an existing persona. Configs are append-only — this supersedes the persona's latest config rather than editing it. `agent_config` must carry `provider_type`, `model_name`, `system_prompt`, `completion_params` (`{}` inherits the model's defaults) and a process — either `agent_process_id`, or `automatic_process_selection: true` with a NON-EMPTY `automatic_process_ids`. An incomplete config is refused here, before the request leaves the client. `fingerprint` is not a value to invent or to ask a user for: call {@link PersonaResource.getParamSchema} with the SAME `provider_type` and `model_name` the config names, and echo the `fingerprint` it returns. An empty echo is a 422; a stale one is a 409 meaning the model's parameters changed since you read them — read again and retry.",
        "params": {
          "params": "CreateNewPersonaConfigRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "persona_id": {
              "type": "string"
            },
            "agent_config": {
              "type": "object",
              "properties": {
                "system_prompt": {
                  "type": "string"
                },
                "provider_type": {
                  "type": "string",
                  "enum": [
                    "openai",
                    "groq",
                    "lmstudio",
                    "anthropic",
                    "fireworks",
                    "grok",
                    "test"
                  ]
                },
                "model_name": {
                  "type": "string"
                },
                "response_format": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                },
                "completion_params": {
                  "type": "object",
                  "properties": {
                    "temperature": {
                      "type": "number"
                    },
                    "top_p": {
                      "type": "number"
                    },
                    "reasoning_effort": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "string"
                        }
                      ]
                    },
                    "stop": {
                      "type": "array",
                      "items": {
                        "type": "string"
                      }
                    }
                  },
                  "additionalProperties": {},
                  "description": "`{}` is legal and means full inheritance of the model's defaults."
                },
                "agent_process_id": {
                  "type": "string",
                  "description": "Default agent process for this persona: the process-mode session-create resolution chain reads it (explicit request → this default → seeded Simple). Persona create/update validates the reference (visible system or same-account process)."
                },
                "automatic_process_selection": {
                  "type": "boolean"
                },
                "automatic_process_ids": {
                  "type": "array",
                  "items": {
                    "type": "string"
                  }
                }
              },
              "required": [
                "system_prompt",
                "provider_type",
                "model_name",
                "completion_params",
                "automatic_process_selection",
                "automatic_process_ids"
              ]
            },
            "metadata": {
              "type": "object",
              "properties": {},
              "additionalProperties": {}
            },
            "fingerprint": {
              "type": "string",
              "description": "Param-schema fingerprint from `getParamSchema`. Unconditional now that every config names a provider/model (persona ADR-020): missing → 422, stale → 409."
            }
          },
          "required": [
            "persona_id",
            "agent_config",
            "fingerprint"
          ]
        },
        "returns": "PersonaConfig",
        "safety": "write"
      },
      "getLatestConfig": {
        "description": "getLatestConfig on the persona resource.",
        "params": {
          "params": "GetLatestPersonaConfigRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "persona_id": {
              "type": "string"
            }
          },
          "required": [
            "persona_id"
          ]
        },
        "returns": "PersonaConfig",
        "safety": "read"
      },
      "getLatestConfigByName": {
        "description": "getLatestConfigByName on the persona resource.",
        "params": {
          "params": "GetLatestPersonaConfigByNameRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string"
            }
          },
          "required": [
            "name"
          ]
        },
        "returns": "PersonaConfig",
        "safety": "read"
      },
      "listActiveSystemPersonas": {
        "description": "The system persona catalog: every ready-made role persona this account may copy, with its config. Read this before authoring a persona by hand — the catalog covers most org roles, and copying one is how a new persona gets a working provider and model without anything being chosen.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "SystemPersonaWithConfig[]",
        "safety": "read"
      },
      "copySystemPersona": {
        "description": "Copy a catalog persona from {@link PersonaResource.listActiveSystemPersonas} into this account under `new_name`, returning the new `persona_id`. The copy carries the catalog entry's provider, model and completion_params, so it is runnable as-is. To make it a specific role, read it back with {@link PersonaResource.getLatestConfig}, change only `system_prompt` and `agent_process_id`, and write it with {@link PersonaResource.createNewConfig}.",
        "params": {
          "params": "CopySystemPersonaRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "system_persona_id": {
              "type": "string"
            },
            "new_name": {
              "type": "string"
            }
          },
          "required": [
            "system_persona_id"
          ]
        },
        "returns": "CopySystemPersonaResponse",
        "safety": "write"
      },
      "listProviders": {
        "description": "listProviders on the persona resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ListProvidersResponse",
        "safety": "read"
      },
      "listProviderModels": {
        "description": "listProviderModels on the persona resource.",
        "params": {
          "providerType": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "providerType": {
              "type": "string"
            }
          },
          "required": [
            "providerType"
          ]
        },
        "returns": "ListProviderModelsResponse",
        "safety": "read"
      },
      "getVisionModel": {
        "description": "Which model reads images for this account, and whether it can actually run. The image-reading model is one stack-wide platform choice, so this read has no setter: there is deliberately no way to change it from the product. `eligible` is false when the model is not marked vision-capable or its provider adapter cannot send image parts, and `ineligible_reason` names which. `key_policy_blocks` is the separate case where the model is fine but the account's own-keys-only policy for that provider has no key stored — the agent's `image_read` tool reports the same condition, from the same computation, so settings and chat cannot disagree.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "VisionModelInfo",
        "safety": "read"
      },
      "getParamSchema": {
        "description": "The ONE schema a client renders from. The backend resolves this with the same function its three write guards use, which is what makes \"the client and the server cannot disagree\" true rather than aspirational — they run one implementation over the same rows. Hold the returned `fingerprint` and echo it on every config write. A stale echo is a 409, so the user is told \"the parameters changed since you opened this editor — reload\" instead of being handed a confusing param-level rejection naming a control they never touched.",
        "params": {
          "providerType": "string",
          "modelName": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "providerType": {
              "type": "string"
            },
            "modelName": {
              "type": "string"
            }
          },
          "required": [
            "providerType",
            "modelName"
          ]
        },
        "returns": "ParamSchema",
        "safety": "read"
      },
      "setProviderParamDefaults": {
        "description": "Write provider-tier default VALUES — user-owned, and separate from the discovery-owned capability spec. Previously these were the same write through the same route, and discovery OWNED the blob: a sync overwrote `supported_params` wholesale, silently erasing a tuned default. `default_params` survives a sync by construction. Returns the RE-RESOLVED schema, so the caller's next save echoes a current fingerprint rather than the one it just invalidated.",
        "params": {
          "providerType": "string",
          "defaults": "Record<string, unknown>",
          "fingerprint": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "providerType": {
              "type": "string"
            },
            "defaults": {
              "type": "object",
              "properties": {},
              "additionalProperties": {}
            },
            "fingerprint": {
              "type": "string"
            }
          },
          "required": [
            "providerType",
            "defaults",
            "fingerprint"
          ]
        },
        "returns": "ParamSchema",
        "safety": "write"
      },
      "setModelParamDefaults": {
        "description": "Write model-tier default VALUES. Overrides the provider tier per key.",
        "params": {
          "providerType": "string",
          "modelName": "string",
          "defaults": "Record<string, unknown>",
          "fingerprint": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "providerType": {
              "type": "string"
            },
            "modelName": {
              "type": "string"
            },
            "defaults": {
              "type": "object",
              "properties": {},
              "additionalProperties": {}
            },
            "fingerprint": {
              "type": "string"
            }
          },
          "required": [
            "providerType",
            "modelName",
            "defaults",
            "fingerprint"
          ]
        },
        "returns": "ParamSchema",
        "safety": "write"
      },
      "listDefaultOverrides": {
        "description": "Which agents PIN these params, and will therefore NOT receive a defaults change. Under sparse pins that is the whole blast radius: an unpinned agent picks the new value up on its next turn, so the interesting set is exactly the ones that will not. Call it BEFORE committing a defaults write — the point is that the user learns the impact while they can still decide. `modelName` is optional. Omitting it asks about every model on the provider, which is what a provider-tier change actually affects.",
        "params": {
          "providerType": "string",
          "params": "string[]",
          "modelName": "string (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "providerType": {
              "type": "string"
            },
            "params": {
              "type": "array",
              "items": {
                "type": "string"
              }
            },
            "modelName": {
              "type": "string"
            }
          },
          "required": [
            "providerType",
            "params"
          ]
        },
        "returns": "DefaultOverridesResult",
        "safety": "read"
      },
      "createCatalogEntry": {
        "description": "createCatalogEntry on the persona resource.",
        "params": {
          "providerType": "string",
          "params": "CreateCatalogEntryParams"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "providerType": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "name": {
                  "type": "string"
                },
                "model_id": {
                  "type": "string"
                },
                "display_name": {
                  "type": "string"
                },
                "context_window": {
                  "type": "number"
                },
                "max_output_tokens": {
                  "type": "number"
                },
                "param_template": {
                  "type": "string",
                  "enum": [
                    "standard",
                    "reasoning",
                    "thinking"
                  ]
                },
                "copy_from": {
                  "type": "string"
                }
              },
              "required": [
                "name",
                "model_id",
                "context_window",
                "max_output_tokens"
              ]
            }
          },
          "required": [
            "providerType",
            "params"
          ]
        },
        "returns": "ModelCatalogEntry",
        "safety": "write"
      },
      "updateCatalogEntry": {
        "description": "updateCatalogEntry on the persona resource.",
        "params": {
          "providerType": "string",
          "params": "UpdateCatalogEntryParams"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "providerType": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "name": {
                  "type": "string"
                },
                "model_id": {
                  "type": "string"
                },
                "display_name": {
                  "type": "string"
                },
                "context_window": {
                  "type": "number"
                },
                "max_output_tokens": {
                  "type": "number"
                },
                "param_template": {
                  "type": "string",
                  "enum": [
                    "standard",
                    "reasoning",
                    "thinking"
                  ]
                },
                "params": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {
                    "anyOf": [
                      {
                        "type": "null"
                      },
                      {
                        "type": "object",
                        "properties": {
                          "type": {
                            "type": "string"
                          },
                          "label": {
                            "type": "string"
                          },
                          "description": {
                            "type": "string"
                          },
                          "ui_hint": {
                            "type": "string"
                          },
                          "sort_order": {
                            "type": "number"
                          },
                          "default_value": {
                            "anyOf": [
                              {
                                "type": "null"
                              },
                              {
                                "type": "string"
                              },
                              {
                                "type": "number"
                              }
                            ]
                          },
                          "min": {
                            "anyOf": [
                              {
                                "type": "null"
                              },
                              {
                                "type": "number"
                              }
                            ]
                          },
                          "max": {
                            "anyOf": [
                              {
                                "type": "null"
                              },
                              {
                                "type": "number"
                              }
                            ]
                          },
                          "step": {
                            "anyOf": [
                              {
                                "type": "null"
                              },
                              {
                                "type": "number"
                              }
                            ]
                          },
                          "labels": {
                            "anyOf": [
                              {
                                "type": "null"
                              },
                              {
                                "type": "array",
                                "items": {
                                  "type": "string"
                                }
                              }
                            ]
                          },
                          "values": {
                            "anyOf": [
                              {
                                "type": "null"
                              },
                              {
                                "type": "array",
                                "items": {
                                  "type": "string"
                                }
                              }
                            ]
                          },
                          "wire": {
                            "anyOf": [
                              {
                                "type": "null"
                              },
                              {
                                "type": "object",
                                "properties": {
                                  "field": {
                                    "type": "string"
                                  },
                                  "kind": {
                                    "type": "string",
                                    "enum": [
                                      "object",
                                      "scalar"
                                    ]
                                  },
                                  "value_key": {
                                    "type": "string"
                                  }
                                },
                                "required": [
                                  "kind"
                                ]
                              }
                            ],
                            "description": "How this scalar is projected onto the provider's request body. Present only on params whose wire shape differs from `{[key]: value}` — nearly every param omits it. The client NEVER applies this. It is rendered from and written back as a scalar; the projection happens server-side, once, at the MakeLLMCall seam (go/internal/context/modelcatalog/project.go). It appears in the SDK type because the spec is the API contract and consumers read `values`/`labels` off the same object — an untyped field would force every reader through an `as unknown` cast, which is how a contract quietly stops being one."
                          },
                          "required": {
                            "type": "boolean"
                          },
                          "send_policy": {
                            "anyOf": [
                              {
                                "type": "null"
                              },
                              {
                                "type": "string",
                                "enum": [
                                  "always"
                                ]
                              },
                              {
                                "type": "string",
                                "enum": [
                                  "on_explicit_set"
                                ]
                              }
                            ]
                          },
                          "status": {
                            "anyOf": [
                              {
                                "type": "null"
                              },
                              {
                                "type": "string",
                                "enum": [
                                  "active"
                                ]
                              },
                              {
                                "type": "string",
                                "enum": [
                                  "deprecated"
                                ]
                              },
                              {
                                "type": "string",
                                "enum": [
                                  "retired"
                                ]
                              }
                            ]
                          },
                          "status_note": {
                            "anyOf": [
                              {
                                "type": "null"
                              },
                              {
                                "type": "string"
                              }
                            ]
                          }
                        },
                        "required": [
                          "type",
                          "label",
                          "ui_hint",
                          "sort_order"
                        ]
                      }
                    ]
                  }
                },
                "supports_tools": {
                  "type": "boolean",
                  "description": "Admin tri-state tool-capability write. Omit to leave unchanged; `true`/`false` sets the account row's `supports_tools`, which `MakeLLMCall` consults before dispatching a tools-bearing call. Setting it `false` on a non-tool model surfaces a targeted error instead of a raw provider 400."
                },
                "supports_vision": {
                  "type": "boolean",
                  "description": "The same tri-state write for the vision label. Setting either capability flag RECORDS the column in `capability_overrides`, which is what stops the next catalog sync overwriting the correction with the provider's value."
                },
                "clear_capability_overrides": {
                  "type": "array",
                  "items": {
                    "type": "string",
                    "enum": [
                      "supports_tools",
                      "supports_vision",
                      "input_modalities",
                      "output_modalities"
                    ]
                  },
                  "description": "Hand a provider-owned capability column back to the provider: the override is dropped and the master catalog's value restored. It is a separate field rather than \"write null\" because the backend write COALESCEs — it has no way to express \"the provider is silent about this\"."
                }
              },
              "required": [
                "name"
              ]
            }
          },
          "required": [
            "providerType",
            "params"
          ]
        },
        "returns": "ModelCatalogEntry",
        "safety": "write"
      },
      "deleteCatalogEntry": {
        "description": "deleteCatalogEntry on the persona resource.",
        "params": {
          "providerType": "string",
          "name": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "providerType": {
              "type": "string"
            },
            "name": {
              "type": "string"
            }
          },
          "required": [
            "providerType",
            "name"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "listEntryReferences": {
        "description": "listEntryReferences on the persona resource.",
        "params": {
          "providerType": "string",
          "name": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "providerType": {
              "type": "string"
            },
            "name": {
              "type": "string"
            }
          },
          "required": [
            "providerType",
            "name"
          ]
        },
        "returns": "EntryReferencesResponse",
        "safety": "read"
      },
      "reassignEntryReferences": {
        "description": "reassignEntryReferences on the persona resource.",
        "params": {
          "providerType": "string",
          "params": "ReassignEntryParams"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "providerType": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "from_name": {
                  "type": "string"
                },
                "to_provider_type": {
                  "type": "string",
                  "enum": [
                    "openai",
                    "groq",
                    "lmstudio",
                    "anthropic",
                    "fireworks",
                    "grok",
                    "test"
                  ]
                },
                "to_name": {
                  "type": "string"
                }
              },
              "required": [
                "from_name",
                "to_provider_type",
                "to_name"
              ]
            }
          },
          "required": [
            "providerType",
            "params"
          ]
        },
        "returns": "ReassignEntryResponse",
        "safety": "write"
      },
      "setProviderParams": {
        "description": "setProviderParams on the persona resource.",
        "params": {
          "providerType": "string",
          "params": "Record<string, unknown>"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "providerType": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {},
              "additionalProperties": {}
            }
          },
          "required": [
            "providerType",
            "params"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "syncCatalog": {
        "description": "syncCatalog on the persona resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "SyncResult",
        "safety": "write"
      },
      "discoverProviderModels": {
        "description": "Discover a provider's live model list. With no `modelIds` it PREVIEWS — fetch + return `candidates`, persist nothing. With `modelIds` it IMPORTS only those candidates (by `name`) as origin='discovered' entries and returns the account's `models`. `deviceId` is required only for `lmstudio` (bridge routing); cloud providers ignore it.",
        "params": {
          "provider": "ProviderType",
          "opts": "{ deviceId?: string; modelIds?: string[] } (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "provider": {
              "type": "string",
              "enum": [
                "openai",
                "groq",
                "lmstudio",
                "anthropic",
                "fireworks",
                "grok",
                "test"
              ]
            },
            "opts": {
              "type": "object",
              "properties": {
                "deviceId": {
                  "type": "string"
                },
                "modelIds": {
                  "type": "array",
                  "items": {
                    "type": "string"
                  }
                }
              }
            }
          },
          "required": [
            "provider"
          ]
        },
        "returns": "DiscoverProviderResult",
        "safety": "read"
      },
      "getProviderCredentialStatus": {
        "description": "getProviderCredentialStatus on the persona resource.",
        "params": {
          "provider": "ProviderType"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "provider": {
              "type": "string",
              "enum": [
                "openai",
                "groq",
                "lmstudio",
                "anthropic",
                "fireworks",
                "grok",
                "test"
              ]
            }
          },
          "required": [
            "provider"
          ]
        },
        "returns": "ProviderCredentialStatus",
        "safety": "read"
      },
      "setProviderCredential": {
        "description": "Validate (live, against the provider's models endpoint) and store the account's API key for a provider. An invalid key is a 422 carrying the provider's message; an unreachable provider is a 502 — the key is stored on neither. lmstudio (keyless) is always 422.",
        "params": {
          "provider": "ProviderType",
          "apiKey": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "provider": {
              "type": "string",
              "enum": [
                "openai",
                "groq",
                "lmstudio",
                "anthropic",
                "fireworks",
                "grok",
                "test"
              ]
            },
            "apiKey": {
              "type": "string"
            }
          },
          "required": [
            "provider",
            "apiKey"
          ]
        },
        "returns": "ProviderCredentialStatus",
        "safety": "write"
      },
      "deleteProviderCredential": {
        "description": "deleteProviderCredential on the persona resource.",
        "params": {
          "provider": "ProviderType"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "provider": {
              "type": "string",
              "enum": [
                "openai",
                "groq",
                "lmstudio",
                "anthropic",
                "fireworks",
                "grok",
                "test"
              ]
            }
          },
          "required": [
            "provider"
          ]
        },
        "returns": "ProviderCredentialStatus",
        "safety": "write"
      },
      "setByokMode": {
        "description": "Set the per-provider key-resolution policy (context ADR-032).",
        "params": {
          "provider": "ProviderType",
          "mode": "ByokMode"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "provider": {
              "type": "string",
              "enum": [
                "openai",
                "groq",
                "lmstudio",
                "anthropic",
                "fireworks",
                "grok",
                "test"
              ]
            },
            "mode": {
              "type": "string",
              "enum": [
                "byok_preferred",
                "byok_only"
              ]
            }
          },
          "required": [
            "provider",
            "mode"
          ]
        },
        "returns": "ProviderCredentialStatus",
        "safety": "write"
      }
    }
  },
  "accountRoles": {
    "description": "Account role management — list the account roster with effective roles, assign and revoke the account-owner/account-member role.",
    "methods": {
      "list": {
        "description": "List an account's explicit memberships and effective roles. When invoked via the Shogun `platform_call` executor, `accountId` arrives UNDEFINED at runtime: the account id is an identity field stripped from the generated argSchema, so the executor maps zero positional args and the agent supplies none. In that case the caller's OWN account is resolved from the authenticated session token (`actingAccountId`), scoping the read to the acting account exactly as the roster gate intends (accountRoles.sweep). Direct SDK callers always pass an explicit account. The parameter type stays `string` (its documented contract) — only the agent-dispatch path omits it — hence the narrowing cast to reflect that the value can genuinely be undefined at runtime.",
        "params": {
          "accountId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "AccountMemberRole[]",
        "safety": "read"
      },
      "assign": {
        "description": "Assign (upsert) a principal's account-scoped role. An upsert on the `(org, account, user_id)` key, so `assign(..., 'account_member')` on a sitting owner is a demotion; a demotion that would zero the account's owners is rejected as a {@link LastOwnerError} (409). Returns the fresh roster.",
        "params": {
          "accountId": "string",
          "userId": "string",
          "role": "AccountRole"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "role": {
              "type": "string",
              "enum": [
                "account_owner",
                "account_member"
              ]
            }
          },
          "required": [
            "role"
          ]
        },
        "returns": "AccountMemberRole[]",
        "safety": "write"
      },
      "revoke": {
        "description": "Revoke a principal's role row (they remain on the roster as `account_member`). Revoking the last owner is rejected as a {@link LastOwnerError} (409). Returns the fresh roster.",
        "params": {
          "accountId": "string",
          "userId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "AccountMemberRole[]",
        "safety": "write"
      }
    }
  },
  "reasoning": {
    "description": "Reasoning engine — cognitive tool execution and graph-based reasoning.",
    "methods": {
      "sendMessage": {
        "description": "Workspace-scoped when a workspaceId is supplied, else the workspace-less sibling route.",
        "params": {
          "workspaceId": "string | null | undefined",
          "params": "SendMessageRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "session_id": {
                  "type": "string"
                },
                "content": {
                  "type": "string"
                },
                "message_id": {
                  "type": "string"
                },
                "attachments": {
                  "type": "array",
                  "items": {
                    "anyOf": [
                      {
                        "type": "object",
                        "properties": {
                          "type": {
                            "type": "string",
                            "enum": [
                              "file",
                              "directory"
                            ]
                          },
                          "path": {
                            "type": "string"
                          },
                          "label": {
                            "type": "string"
                          }
                        },
                        "required": [
                          "type",
                          "path",
                          "label"
                        ]
                      },
                      {
                        "type": "object",
                        "properties": {
                          "type": {
                            "type": "string",
                            "enum": [
                              "image"
                            ]
                          },
                          "image_id": {
                            "type": "string"
                          },
                          "label": {
                            "type": "string"
                          }
                        },
                        "required": [
                          "type",
                          "image_id",
                          "label"
                        ]
                      }
                    ]
                  },
                  "description": "Things that precede the user text in the assembled turn, in array order. `file`/`directory` carry a workspace-relative `path` and require a workspace-scoped session; `image` carries an `image_id` from `sessions.uploadImage` and is legal on every session, including a workspace-less web chat."
                },
                "continue_as_new_threshold": {
                  "type": "number"
                },
                "view_context": {
                  "type": "object",
                  "properties": {
                    "route": {
                      "type": "string"
                    },
                    "surface": {
                      "type": "string"
                    },
                    "primary_type": {
                      "type": "string"
                    },
                    "scope": {
                      "type": "array",
                      "items": {
                        "type": "object",
                        "properties": {
                          "type": {
                            "type": "string"
                          },
                          "id": {
                            "type": "string"
                          },
                          "label": {
                            "type": "string"
                          },
                          "state": {
                            "type": "string",
                            "description": "The record's RENDERED state — the lane the user sees it in, which is not always the raw enum (app-shell ADR-011)."
                          },
                          "note": {
                            "type": "string",
                            "description": "One badge that changes the answer: blocked-by, unassigned, orphan, session running."
                          }
                        },
                        "required": [
                          "type",
                          "id"
                        ]
                      },
                      "description": "The ancestor chain the view is scoped to, outermost first (app-shell ADR-014)."
                    },
                    "counts": {
                      "type": "array",
                      "items": {
                        "type": "object",
                        "properties": {
                          "lane": {
                            "type": "string"
                          },
                          "total": {
                            "type": "number"
                          }
                        },
                        "required": [
                          "lane",
                          "total"
                        ]
                      },
                      "description": "Per-lane true totals — never truncated, so the agent knows the real numbers."
                    },
                    "filters": {
                      "type": "array",
                      "items": {
                        "type": "object",
                        "properties": {
                          "name": {
                            "type": "string"
                          },
                          "value": {
                            "type": "string"
                          }
                        },
                        "required": [
                          "name",
                          "value"
                        ]
                      }
                    },
                    "visible": {
                      "type": "array",
                      "items": {
                        "type": "object",
                        "properties": {
                          "type": {
                            "type": "string"
                          },
                          "id": {
                            "type": "string"
                          },
                          "label": {
                            "type": "string"
                          },
                          "state": {
                            "type": "string",
                            "description": "The record's RENDERED state — the lane the user sees it in, which is not always the raw enum (app-shell ADR-011)."
                          },
                          "note": {
                            "type": "string",
                            "description": "One badge that changes the answer: blocked-by, unassigned, orphan, session running."
                          }
                        },
                        "required": [
                          "type",
                          "id"
                        ]
                      },
                      "description": "Every record actually rendered. The client does not sample (app-shell ADR-014)."
                    },
                    "selected": {
                      "type": "object",
                      "properties": {
                        "type": {
                          "type": "string"
                        },
                        "id": {
                          "type": "string"
                        },
                        "label": {
                          "type": "string"
                        },
                        "state": {
                          "type": "string",
                          "description": "The record's RENDERED state — the lane the user sees it in, which is not always the raw enum (app-shell ADR-011)."
                        },
                        "note": {
                          "type": "string",
                          "description": "One badge that changes the answer: blocked-by, unassigned, orphan, session running."
                        }
                      },
                      "required": [
                        "type",
                        "id"
                      ]
                    },
                    "captured_at": {
                      "type": "string",
                      "description": "When the snapshot was taken — stamped at composer submit (app-shell ADR-015)."
                    },
                    "truncated": {
                      "type": "boolean",
                      "description": "Set when the CLIENT dropped anything from this snapshot before sending it."
                    }
                  },
                  "description": "Per-turn description of what the user is currently viewing. Injected into the agent's system prompt as a `<user_view_context>` block so it can resolve \"this\"/\"the current item\". Optional — omitting it sends no block."
                },
                "client_context": {
                  "type": "object",
                  "properties": {
                    "timezone": {
                      "type": "string",
                      "description": "The caller's IANA timezone, e.g. `America/New_York`, read from `Intl.DateTimeFormat().resolvedOptions().timeZone`. This is the FRESHER rung of the dual-source timezone precedence (client-supplied → stored profile → omit): the browser knows where the user is right now, while the stored profile records where they usually are. An absent or unresolvable value is omitted rather than guessed, so the precedence falls through to the profile structurally."
                    }
                  },
                  "description": "The client's own environment for this turn. Currently just the IANA timezone, which wins over the stored profile timezone when rendering the `user-info` block. Inert data. Optional — omitting it falls through to the stored profile value."
                }
              },
              "required": [
                "session_id",
                "content"
              ]
            }
          },
          "required": [
            "workspaceId",
            "params"
          ]
        },
        "returns": "SendMessageResponse",
        "safety": "write"
      },
      "generateSessionName": {
        "description": "Workspace-scoped when a workspaceId is supplied, else the workspace-less sibling route — a workspace-less chat's title is generated on the account-scoped lane.",
        "params": {
          "workspaceId": "string | null | undefined",
          "params": "GenerateSessionNameRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "user_message": {
                  "type": "string"
                },
                "assistant_message": {
                  "type": "string"
                }
              },
              "required": [
                "user_message",
                "assistant_message"
              ]
            }
          },
          "required": [
            "workspaceId",
            "params"
          ]
        },
        "returns": "GenerateSessionNameResponse",
        "safety": "write"
      },
      "submitClarificationResponse": {
        "description": "submitClarificationResponse on the reasoning resource.",
        "params": {
          "workspaceId": "string",
          "params": "ClarificationResponseRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "session_id": {
                  "type": "string"
                },
                "question_id": {
                  "type": "string"
                },
                "selected_option": {
                  "anyOf": [
                    {
                      "type": "null"
                    },
                    {
                      "type": "string"
                    }
                  ]
                },
                "open_response": {
                  "anyOf": [
                    {
                      "type": "null"
                    },
                    {
                      "type": "string"
                    }
                  ]
                },
                "skipped": {
                  "type": "boolean"
                }
              },
              "required": [
                "session_id",
                "question_id"
              ]
            }
          },
          "required": [
            "workspaceId",
            "params"
          ]
        },
        "returns": "ClarificationResponseOutput",
        "safety": "write"
      },
      "listAgents": {
        "description": "listAgents on the reasoning resource.",
        "params": {
          "workspaceId": "string",
          "params": "ListAgentsRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "session_persona_name": {
                  "anyOf": [
                    {
                      "type": "null"
                    },
                    {
                      "type": "string"
                    }
                  ]
                }
              }
            }
          },
          "required": [
            "workspaceId",
            "params"
          ]
        },
        "returns": "AgentListResponse",
        "safety": "read"
      },
      "stopExecution": {
        "description": "stopExecution on the reasoning resource.",
        "params": {
          "workspaceId": "string",
          "sessionId": "string",
          "params": "StopExecutionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "type": "string"
            },
            "sessionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "workflow_id": {
                  "type": "string"
                }
              },
              "required": [
                "workflow_id"
              ]
            }
          },
          "required": [
            "workspaceId",
            "sessionId",
            "params"
          ]
        },
        "returns": "StopExecutionResponse",
        "safety": "write"
      },
      "redirectExecution": {
        "description": "redirectExecution on the reasoning resource.",
        "params": {
          "workspaceId": "string",
          "sessionId": "string",
          "params": "RedirectExecutionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "type": "string"
            },
            "sessionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "workflow_id": {
                  "type": "string"
                },
                "content": {
                  "type": "string"
                }
              },
              "required": [
                "workflow_id",
                "content"
              ]
            }
          },
          "required": [
            "workspaceId",
            "sessionId",
            "params"
          ]
        },
        "returns": "RedirectExecutionResponse",
        "safety": "write"
      },
      "validateDSL": {
        "description": "validateDSL on the reasoning resource.",
        "params": {
          "params": "ValidateDSLRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "graph_dsl": {
              "type": "object",
              "properties": {},
              "additionalProperties": {}
            }
          },
          "required": [
            "graph_dsl"
          ]
        },
        "returns": "ValidateDSLResponse",
        "safety": "read"
      }
    }
  },
  "reservedBlocks": {
    "description": "Global platform-reserved floor policy blocks composed at the LLM provider seam. Read-only and seeded; list requires the platform admin role (localagent_admin).",
    "methods": {
      "list": {
        "description": "list on the reservedBlocks resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ReservedBlocksList",
        "safety": "read"
      }
    }
  },
  "search": {
    "description": "Unified search across account entities and workspace file chunks.",
    "methods": {
      "search": {
        "description": "Run a search query against the authenticated account's index. Any authenticated user with a valid JWT for the account may call this — there is no per-role distinction in v1.",
        "params": {
          "req": "SearchRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "query": {
              "type": "string"
            },
            "mode": {
              "type": "string",
              "enum": [
                "hybrid",
                "semantic",
                "lexical"
              ]
            },
            "sort": {
              "type": "string",
              "enum": [
                "relevance",
                "updated_at_desc",
                "updated_at_asc"
              ]
            },
            "scope": {
              "type": "string",
              "enum": [
                "all",
                "catalog",
                "files"
              ]
            },
            "entity_types": {
              "type": "array",
              "items": {
                "type": "string",
                "enum": [
                  "workspace",
                  "session",
                  "ticket",
                  "integration",
                  "file",
                  "directory",
                  "team",
                  "project",
                  "initiative",
                  "persona",
                  "automation",
                  "tool_endpoint",
                  "agent_process",
                  "session_message",
                  "session_event",
                  "feed_post",
                  "account_skill",
                  "account_artifact",
                  "account_secret"
                ]
              }
            },
            "workspace_id": {
              "type": "array",
              "items": {
                "type": "string"
              },
              "description": "Restrict to a set of workspaces (a list). Absent = default scope."
            },
            "limit": {
              "type": "number"
            },
            "offset": {
              "type": "number"
            }
          },
          "required": [
            "query"
          ]
        },
        "returns": "SearchResponse",
        "safety": "read"
      },
      "recent": {
        "description": "Typed convenience wrapper for the recency-sorted browse view. Calls /v1/search with the canonical \"recent\" parameters (empty query, updated_at_desc, default limit). The helper narrows the contract for callers; it is NOT a backwards-compat shim.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "SearchResponse",
        "safety": "read"
      },
      "adminSearch": {
        "description": "Admin cross-tenant search. Admin-scoped; INTENTIONALLY bypasses the mandatory org+account tenant filter, taking a target organization_id/account_id (or none to span tenants). Used by the tenancy suite to confirm another account's points DO exist in the shared collection — proving the filter (not absence of data) hides them.",
        "params": {
          "req": "SearchRequest & { organization_id?: string; account_id?: string }"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "query": {
              "type": "string"
            },
            "mode": {
              "type": "string",
              "enum": [
                "hybrid",
                "semantic",
                "lexical"
              ]
            },
            "sort": {
              "type": "string",
              "enum": [
                "relevance",
                "updated_at_desc",
                "updated_at_asc"
              ]
            },
            "scope": {
              "type": "string",
              "enum": [
                "all",
                "catalog",
                "files"
              ]
            },
            "entity_types": {
              "type": "array",
              "items": {
                "type": "string",
                "enum": [
                  "workspace",
                  "session",
                  "ticket",
                  "integration",
                  "file",
                  "directory",
                  "team",
                  "project",
                  "initiative",
                  "persona",
                  "automation",
                  "tool_endpoint",
                  "agent_process",
                  "session_message",
                  "session_event",
                  "feed_post",
                  "account_skill",
                  "account_artifact",
                  "account_secret"
                ]
              }
            },
            "workspace_id": {
              "type": "array",
              "items": {
                "type": "string"
              },
              "description": "Restrict to a set of workspaces (a list). Absent = default scope."
            },
            "limit": {
              "type": "number"
            },
            "offset": {
              "type": "number"
            }
          },
          "required": [
            "query"
          ]
        },
        "returns": "SearchResponse",
        "safety": "read"
      },
      "reindex": {
        "description": "Re-index the authenticated account (non-destructive; catalog from DB).",
        "params": {
          "req": "{ scope?: 'all' | 'catalog' | 'files' } (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "scope": {
              "type": "string",
              "enum": [
                "all",
                "catalog",
                "files"
              ]
            }
          }
        },
        "returns": "{\n    catalog: string;\n    files: string;\n  }",
        "safety": "write"
      },
      "ingestChunks": {
        "description": "Ingest file-content chunks into the central tier. Desktop path: account JWT + a body-supplied desktop-local workspace_id (the endpoint authorizes by account_id). op defaults to upsert.",
        "params": {
          "req": "{\n    workspace_id: string;\n    assignment_epoch: number;\n    op?:\n      | 'upsert'\n      | 'delete'\n      | 'replace-file'\n      | 'upsert-directories'\n      | 'index-status';\n    chunks: Array<{\n      path: string;\n      chunk_type?: string;\n      content: string;\n      symbols?: string[];\n      start_line?: number;\n      end_line?: number;\n    }>;\n    directories?: Array<{\n      path: string;\n      parent_path?: string;\n      name: string;\n      updated_at?: string;\n    }>;\n    ready?: boolean;\n    indexed?: number;\n    queued?: number;\n    embedding?: number;\n    health?: 'ok' | 'degraded';\n    wait_paths?: string[];\n  }"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "workspace_id": {
              "type": "string"
            },
            "assignment_epoch": {
              "type": "number"
            },
            "op": {
              "type": "string",
              "enum": [
                "upsert",
                "delete",
                "replace-file",
                "upsert-directories",
                "index-status"
              ]
            },
            "chunks": {
              "type": "array",
              "items": {
                "type": "object",
                "properties": {
                  "path": {
                    "type": "string"
                  },
                  "chunk_type": {
                    "type": "string"
                  },
                  "content": {
                    "type": "string"
                  },
                  "symbols": {
                    "type": "array",
                    "items": {
                      "type": "string"
                    }
                  },
                  "start_line": {
                    "type": "number"
                  },
                  "end_line": {
                    "type": "number"
                  }
                },
                "required": [
                  "path",
                  "content"
                ]
              }
            },
            "directories": {
              "type": "array",
              "items": {
                "type": "object",
                "properties": {
                  "path": {
                    "type": "string"
                  },
                  "parent_path": {
                    "type": "string"
                  },
                  "name": {
                    "type": "string"
                  },
                  "updated_at": {
                    "type": "string"
                  }
                },
                "required": [
                  "path",
                  "name"
                ]
              }
            },
            "ready": {
              "type": "boolean"
            },
            "indexed": {
              "type": "number"
            },
            "queued": {
              "type": "number"
            },
            "embedding": {
              "type": "number"
            },
            "health": {
              "type": "string",
              "enum": [
                "ok",
                "degraded"
              ]
            },
            "wait_paths": {
              "type": "array",
              "items": {
                "type": "string"
              }
            }
          },
          "required": [
            "workspace_id",
            "assignment_epoch",
            "chunks"
          ]
        },
        "returns": "{ accepted: number; directories_accepted?: number; op: string }",
        "safety": "write"
      }
    }
  },
  "secrets": {
    "description": "Account-global secrets — named encrypted values available to every session on the account. List and get are masked; reveal returns the plaintext value. workspace_id is an organizational tag and never scopes access.",
    "methods": {
      "list": {
        "description": "List the account's secrets, masked. Values are never returned here.",
        "params": {
          "params": "ListSecretsParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "workspace_id": {
              "type": "string",
              "description": "Narrows the listing to secrets carrying this tag. It does not scope access."
            },
            "limit": {
              "type": "number"
            }
          }
        },
        "returns": "ListAccountSecretsResponse",
        "safety": "read"
      },
      "get": {
        "description": "Fetch one secret's metadata, masked.",
        "params": {
          "secretId": "string — The secret's id."
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "secretId": {
              "type": "string"
            }
          },
          "required": [
            "secretId"
          ]
        },
        "returns": "AccountSecret",
        "safety": "read"
      },
      "create": {
        "description": "Create an account-global secret.",
        "params": {
          "params": "CreateAccountSecretRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string",
              "description": "Uppercase letters, digits and underscore, starting with a letter."
            },
            "value": {
              "type": "string"
            },
            "description": {
              "type": "string"
            },
            "workspace_id": {
              "type": "string"
            }
          },
          "required": [
            "name",
            "value"
          ]
        },
        "returns": "AccountSecret",
        "safety": "write"
      },
      "update": {
        "description": "Replace a secret's value, description or workspace tag.",
        "params": {
          "secretId": "string — The secret's id.",
          "params": "UpdateAccountSecretRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "secretId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "value": {
                  "type": "string",
                  "description": "Omit to keep the current value."
                },
                "description": {
                  "type": "string"
                },
                "workspace_id": {
                  "type": "string",
                  "description": "Empty string clears the tag."
                }
              }
            }
          },
          "required": [
            "secretId",
            "params"
          ]
        },
        "returns": "AccountSecret",
        "safety": "write"
      },
      "reveal": {
        "description": "Return one secret's plaintext value. Authorized exactly as `get` is — there is no per-secret governance.",
        "params": {
          "secretId": "string — The secret's id."
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "secretId": {
              "type": "string"
            }
          },
          "required": [
            "secretId"
          ]
        },
        "returns": "RevealSecretResponse",
        "safety": "read"
      },
      "delete": {
        "description": "Permanently delete a secret. The value cannot be recovered.",
        "params": {
          "secretId": "string — The secret's id."
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "secretId": {
              "type": "string"
            }
          },
          "required": [
            "secretId"
          ]
        },
        "returns": "DeleteAccountSecretResponse",
        "safety": "destructive"
      }
    }
  },
  "sessionList": {
    "description": "Account session directory — browse a recency page, search the session catalog, project/flatten the loaded window, and bulk-delete named refs.",
    "methods": {
      "browsePage": {
        "description": "One recency page of the account-wide session catalog (Postgres), plus the work-item batch for those ids. Catalogs load when `showEmptyGroups` is on, or when grouping needs catalog names for headers (`workspace`, `team`) — a catalog error fails the page (sdk ADR-035). Cache consult: a stored page window serves instantly and revalidates behind the return; the replacement is atomic and the next read sees it.",
        "params": {
          "query": "SessionListQuery",
          "page": "{ offset: number }"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "query": {
              "type": "object",
              "properties": {
                "grouping": {
                  "type": "string",
                  "enum": [
                    "workspace",
                    "team",
                    "goal",
                    "user-initiated"
                  ]
                },
                "roleFilter": {
                  "type": "string"
                },
                "goalFilter": {
                  "type": "string"
                },
                "statusFilter": {
                  "type": "string",
                  "enum": [
                    "active",
                    "all"
                  ]
                },
                "workspaceFilter": {
                  "type": "string"
                },
                "sort": {
                  "type": "string",
                  "enum": [
                    "last_activity",
                    "created",
                    "name"
                  ]
                },
                "showEmptyGroups": {
                  "type": "boolean"
                }
              },
              "required": [
                "grouping",
                "roleFilter",
                "goalFilter",
                "statusFilter",
                "workspaceFilter",
                "sort",
                "showEmptyGroups"
              ]
            },
            "page": {
              "type": "object",
              "properties": {
                "offset": {
                  "type": "number"
                }
              },
              "required": [
                "offset"
              ]
            }
          },
          "required": [
            "query",
            "page"
          ]
        },
        "returns": "SessionListBrowsePage",
        "safety": "read"
      },
      "searchPage": {
        "description": "One Qdrant page of session-catalog hits. `entity_types` is pinned to `session` — empty-query search is not the conversation list (sdk ADR-035). Cache consult as in {@link browsePage} (search inclusion confirmed).",
        "params": {
          "params": "{\n    query: string;\n    offset: number;\n    workspace_id?: string[];\n  }"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "query": {
              "type": "string"
            },
            "offset": {
              "type": "number"
            },
            "workspace_id": {
              "type": "array",
              "items": {
                "type": "string"
              }
            }
          },
          "required": [
            "query",
            "offset"
          ]
        },
        "returns": "SearchResponse",
        "safety": "read"
      },
      "deleteMany": {
        "description": "Delete the named refs through the account-scoped bulk route, chunked at the server cap. Does not loop `sessions.delete` (sessions ADR-022). The list windows invalidate at the mutation point. A live chat handle on any of the refs is dropped and its event subscription released before the first delete is sent.",
        "params": {
          "items": "SessionRef[]"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "items": {
              "type": "array",
              "items": {
                "type": "object",
                "properties": {
                  "session_id": {
                    "type": "string"
                  },
                  "workspace_id": {
                    "anyOf": [
                      {
                        "type": "null"
                      },
                      {
                        "type": "string"
                      }
                    ]
                  }
                },
                "required": [
                  "session_id",
                  "workspace_id"
                ]
              }
            }
          },
          "required": [
            "items"
          ]
        },
        "returns": "DeleteManyResult",
        "safety": "destructive"
      }
    }
  },
  "sessions": {
    "description": "Manage sessions and their messages, checkpoints, user configs, and events within a workspace.",
    "methods": {
      "create": {
        "description": "create on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "params": "CreateSessionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "name": {
                  "type": "string",
                  "description": "Session title. Omitted/empty ⇒ the auto-nameable sentinel (`New Chat`), which the once-only auto-namer replaces after the first exchange (sessions ADR-024). A supplied name is a real title and disables auto-naming — never fabricate a placeholder here."
                },
                "agent_config": {
                  "type": "object",
                  "properties": {
                    "system_prompt": {
                      "type": "string"
                    },
                    "provider_type": {
                      "type": "string",
                      "enum": [
                        "openai",
                        "groq",
                        "lmstudio",
                        "anthropic",
                        "fireworks",
                        "grok",
                        "test"
                      ]
                    },
                    "model_name": {
                      "type": "string"
                    },
                    "completion_params": {
                      "type": "object",
                      "properties": {
                        "temperature": {
                          "type": "number"
                        },
                        "top_p": {
                          "type": "number"
                        },
                        "reasoning_effort": {
                          "anyOf": [
                            {
                              "type": "null"
                            },
                            {
                              "type": "string"
                            }
                          ]
                        },
                        "stop": {
                          "type": "array",
                          "items": {
                            "type": "string"
                          }
                        }
                      },
                      "additionalProperties": {}
                    },
                    "automatic_process_selection": {
                      "type": "boolean"
                    },
                    "automatic_process_ids": {
                      "type": "array",
                      "items": {
                        "type": "string"
                      }
                    }
                  }
                },
                "user_config": {
                  "type": "object",
                  "properties": {
                    "id": {
                      "type": "string"
                    },
                    "preferences": {
                      "type": "object",
                      "properties": {},
                      "additionalProperties": {}
                    },
                    "settings": {
                      "type": "object",
                      "properties": {},
                      "additionalProperties": {}
                    },
                    "custom_data": {
                      "type": "object",
                      "properties": {},
                      "additionalProperties": {}
                    }
                  }
                },
                "description": {
                  "type": "string"
                },
                "persona_id": {
                  "type": "string"
                },
                "session_mode": {
                  "type": "string"
                },
                "device_id": {
                  "type": "string"
                },
                "agent_process_id": {
                  "type": "string",
                  "description": "Explicit agent process for the session. When omitted the backend resolves the process unconditionally at create time: persona default → seeded Simple. There is no legacy create path."
                },
                "pin_model_to_turn": {
                  "type": "boolean",
                  "description": "Pin the session's model to every execution in the turn. Omitted ⇒ the ratified default, `false`."
                },
                "automatic_process_selection": {
                  "type": "boolean"
                },
                "internal_agent_visibility": {
                  "type": "string",
                  "enum": [
                    "friendly",
                    "exact"
                  ]
                },
                "fingerprint": {
                  "type": "string",
                  "description": "Param-schema fingerprint from `getParamSchema`. Required when agent_config changes params or the provider/model pair."
                }
              }
            }
          },
          "required": [
            "workspaceId",
            "params"
          ]
        },
        "returns": "Session",
        "safety": "write"
      },
      "list": {
        "description": "Sessions for this lane, most recent transcript message first. The route is BOUNDED: `limit` defaults to 200 server-side and caps at 500, and ordering is `(last_accessed DESC, id DESC)` so `offset` is stable across pages. The response stays a bare array, so to know whether the bound cut anything off, ask for one more row than you intend to render — a returned length greater than your page size is the signal, and is exact at the boundary in a way `length === limit` is not.",
        "params": {
          "workspaceId": "string | null | undefined — Workspace to scope to, or null/undefined for the account-wide list (both lanes).",
          "params": "{ limit?: number; offset?: number } (optional) — Page bounds: `limit` (1-500) and `offset` (>= 0)."
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "limit": {
                  "type": "number"
                },
                "offset": {
                  "type": "number"
                }
              }
            }
          },
          "required": [
            "workspaceId"
          ]
        },
        "returns": "Session[]",
        "safety": "read"
      },
      "get": {
        "description": "get on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            }
          },
          "required": [
            "workspaceId",
            "sessionId"
          ]
        },
        "returns": "Session",
        "safety": "read"
      },
      "getMostRecent": {
        "description": "getMostRecent on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            }
          },
          "required": [
            "workspaceId"
          ]
        },
        "returns": "Session",
        "safety": "read"
      },
      "update": {
        "description": "update on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string",
          "params": "UpdateSessionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "name": {
                  "type": "string"
                },
                "name_only_if_default": {
                  "type": "boolean",
                  "description": "Once-only auto-name write (sessions ADR-024): applies `name` only while the session's `is_default_name` is true; otherwise a no-op success. Never set this on a user-initiated rename."
                },
                "persona_id": {
                  "anyOf": [
                    {
                      "type": "null"
                    },
                    {
                      "type": "string"
                    }
                  ]
                },
                "session_metadata": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                },
                "agent_config": {
                  "type": "object",
                  "properties": {
                    "system_prompt": {
                      "type": "string"
                    },
                    "provider_type": {
                      "type": "string",
                      "enum": [
                        "openai",
                        "groq",
                        "lmstudio",
                        "anthropic",
                        "fireworks",
                        "grok",
                        "test"
                      ]
                    },
                    "model_name": {
                      "type": "string"
                    },
                    "completion_params": {
                      "type": "object",
                      "properties": {
                        "temperature": {
                          "type": "number"
                        },
                        "top_p": {
                          "type": "number"
                        },
                        "reasoning_effort": {
                          "anyOf": [
                            {
                              "type": "null"
                            },
                            {
                              "type": "string"
                            }
                          ]
                        },
                        "stop": {
                          "type": "array",
                          "items": {
                            "type": "string"
                          }
                        }
                      },
                      "additionalProperties": {}
                    },
                    "automatic_process_selection": {
                      "type": "boolean"
                    },
                    "automatic_process_ids": {
                      "type": "array",
                      "items": {
                        "type": "string"
                      }
                    }
                  }
                },
                "agent_process_id": {
                  "type": "string",
                  "description": "Switch the session's agent process (takes effect next run). Conversion is one-way — clearing (empty string / null) is rejected; every session must carry a resolved agent process."
                },
                "pin_model_to_turn": {
                  "type": "boolean",
                  "description": "Flip the session's model pin (takes effect on the next run — a mid-turn flip never changes the in-flight turn)."
                },
                "automatic_process_selection": {
                  "anyOf": [
                    {
                      "type": "null"
                    },
                    {
                      "type": "boolean"
                    }
                  ]
                },
                "internal_agent_visibility": {
                  "type": "string",
                  "enum": [
                    "friendly",
                    "exact"
                  ]
                },
                "context_rag_enabled": {
                  "type": "boolean",
                  "description": "Per-chat override for the run-start context-RAG step. Omitted = no change; the unset (NULL) row state resolves through the account default, then the platform default (sessions ADR-5a6be)."
                },
                "context_rag_child_runs_enabled": {
                  "type": "boolean",
                  "description": "Additionally gates the child-run RAG step; can never fire while the root flag resolves off."
                },
                "fingerprint": {
                  "type": "string",
                  "description": "Param-schema fingerprint from `getParamSchema`. Required when agent_config changes params or the provider/model pair."
                }
              }
            }
          },
          "required": [
            "workspaceId",
            "sessionId",
            "params"
          ]
        },
        "returns": "Session",
        "safety": "write"
      },
      "delete": {
        "description": "delete on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            }
          },
          "required": [
            "workspaceId",
            "sessionId"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "createMessage": {
        "description": "createMessage on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string",
          "params": "CreateMessageRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "role": {
                  "type": "string",
                  "enum": [
                    "user",
                    "assistant",
                    "system"
                  ]
                },
                "content": {
                  "type": "string"
                },
                "metadata": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                }
              },
              "required": [
                "role",
                "content"
              ]
            }
          },
          "required": [
            "workspaceId",
            "sessionId",
            "params"
          ]
        },
        "returns": "Message",
        "safety": "write"
      },
      "getMessages": {
        "description": "getMessages on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            }
          },
          "required": [
            "workspaceId",
            "sessionId"
          ]
        },
        "returns": "Message[]",
        "safety": "read"
      },
      "getMessagesPage": {
        "description": "Keyset-paged transcript read. Exactly one window mode per call: - `{ latest: true, limit }` — the newest `limit` visible messages, ascending (the tail page). - `{ beforeMessageIndex, limit }` — up to `limit` visible messages below `beforeMessageIndex`, ascending (backward extension). - `{ afterMessageIndex, limit }` — up to `limit` visible messages above `afterMessageIndex`, ascending (live-tail catch-up). Every row carries `message_index` — the lane's own monotonic cursor the caller forms the next page from. Windowing params require `limit > 0` (422 otherwise); the unparameterized {@link getMessages} stays the byte-compatible full read.",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string",
          "opts": "| { latest: true; limit: number }\n      | { beforeMessageIndex: number; limit: number }\n      | { afterMessageIndex: number; limit: number }"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            },
            "opts": {
              "anyOf": [
                {
                  "type": "object",
                  "properties": {
                    "latest": {
                      "type": "boolean"
                    },
                    "limit": {
                      "type": "number"
                    }
                  },
                  "required": [
                    "latest",
                    "limit"
                  ]
                },
                {
                  "type": "object",
                  "properties": {
                    "beforeMessageIndex": {
                      "type": "number"
                    },
                    "limit": {
                      "type": "number"
                    }
                  },
                  "required": [
                    "beforeMessageIndex",
                    "limit"
                  ]
                },
                {
                  "type": "object",
                  "properties": {
                    "afterMessageIndex": {
                      "type": "number"
                    },
                    "limit": {
                      "type": "number"
                    }
                  },
                  "required": [
                    "afterMessageIndex",
                    "limit"
                  ]
                }
              ]
            }
          },
          "required": [
            "workspaceId",
            "sessionId",
            "opts"
          ]
        },
        "returns": "Message[]",
        "safety": "read"
      },
      "clearMessages": {
        "description": "clearMessages on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            }
          },
          "required": [
            "workspaceId",
            "sessionId"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "editMessageAndRewind": {
        "description": "editMessageAndRewind on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string",
          "messageId": "string",
          "content": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            },
            "messageId": {
              "type": "string"
            },
            "content": {
              "type": "string"
            }
          },
          "required": [
            "workspaceId",
            "sessionId",
            "messageId",
            "content"
          ]
        },
        "returns": "Message[]",
        "safety": "write"
      },
      "uploadImage": {
        "description": "Upload one image to a session and get back its `image://` handle. The bytes go up as the request body under the blob's own content type — the server SNIFFS them, so a declared type is advisory and a lying one is corrected in the response. Server-side the upload refuses a non-image (415), an animated GIF (415), a body over the 5 MiB per-image cap (413), a header declaring more than 40 megapixels (413), and anything past the session's storage ceilings (413); it strips EXIF/GPS/colour profiles by re-encoding into the read-quality copy the vision model reads. Pass the returned `image_id` to `ChatSession.send` as `{ type: 'image', image_id, label }` to attach it to a message. Nothing image-shaped is persisted: the read-quality copy expires from the cache, and the message keeps a text placeholder. The routes live under the reasoning prefix, not the sessions prefix, because attachment binding is part of the send path.",
        "params": {
          "sessionId": "string — Session to attach the image to.",
          "bytes": "Blob — The image itself (a File, a Blob, or raw bytes).",
          "label": "string — User-facing name; neutralized and length-capped server-side.",
          "workspaceId": "string | null (optional) — Workspace for a workspace-attached session; omit for a workspace-less (web) session.",
          "source": "'paste' | 'upload' (optional) — How the user added it — `paste` or `upload`."
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "sessionId": {
              "type": "string"
            },
            "bytes": {
              "type": "string",
              "format": "binary"
            },
            "label": {
              "type": "string"
            },
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "source": {
              "type": "string",
              "enum": [
                "upload",
                "paste"
              ]
            }
          },
          "required": [
            "sessionId",
            "bytes",
            "label"
          ]
        },
        "returns": "SessionImageUpload",
        "safety": "write"
      },
      "listCheckpoints": {
        "description": "listCheckpoints on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            }
          },
          "required": [
            "workspaceId",
            "sessionId"
          ]
        },
        "returns": "Checkpoint[]",
        "safety": "read"
      },
      "createCheckpoint": {
        "description": "createCheckpoint on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            }
          },
          "required": [
            "workspaceId",
            "sessionId"
          ]
        },
        "returns": "Checkpoint",
        "safety": "write"
      },
      "getCheckpoint": {
        "description": "getCheckpoint on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "checkpointId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "checkpointId": {
              "type": "string"
            }
          },
          "required": [
            "workspaceId",
            "checkpointId"
          ]
        },
        "returns": "Checkpoint",
        "safety": "read"
      },
      "deleteCheckpoint": {
        "description": "deleteCheckpoint on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "checkpointId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "checkpointId": {
              "type": "string"
            }
          },
          "required": [
            "workspaceId",
            "checkpointId"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "restoreCheckpoint": {
        "description": "restoreCheckpoint on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string",
          "checkpointId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            },
            "checkpointId": {
              "type": "string"
            }
          },
          "required": [
            "workspaceId",
            "sessionId",
            "checkpointId"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "getUserConfig": {
        "description": "getUserConfig on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            }
          },
          "required": [
            "workspaceId",
            "sessionId"
          ]
        },
        "returns": "UserConfig",
        "safety": "read"
      },
      "updateUserConfig": {
        "description": "updateUserConfig on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string",
          "params": "UpdateUserConfigRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "preferences": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                },
                "settings": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                },
                "custom_data": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                }
              }
            }
          },
          "required": [
            "workspaceId",
            "sessionId",
            "params"
          ]
        },
        "returns": "UserConfig",
        "safety": "write"
      },
      "getUserConfigById": {
        "description": "getUserConfigById on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "userConfigId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "userConfigId": {
              "type": "string"
            }
          },
          "required": [
            "workspaceId",
            "userConfigId"
          ]
        },
        "returns": "UserConfig",
        "safety": "read"
      },
      "getEvents": {
        "description": "getEvents on the sessions resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string",
          "afterSequence": "number (optional)",
          "limit": "number (optional)",
          "opts": "{\n      /** Newest `limit` events, ascending. Combines with neither cursor. */\n      latest?: boolean;\n      /** The `limit` events immediately below this sequence, ascending. */\n      beforeSequence?: number;\n      /** Heavy-stripped rows with handle + size_bytes above the inline threshold. */\n      skeleton?: boolean;\n    } (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            },
            "afterSequence": {
              "type": "number"
            },
            "limit": {
              "type": "number"
            },
            "opts": {
              "type": "object",
              "properties": {
                "latest": {
                  "type": "boolean",
                  "description": "Newest `limit` events, ascending. Combines with neither cursor."
                },
                "beforeSequence": {
                  "type": "number",
                  "description": "The `limit` events immediately below this sequence, ascending."
                },
                "skeleton": {
                  "type": "boolean",
                  "description": "Heavy-stripped rows with handle + size_bytes above the inline threshold."
                }
              }
            }
          },
          "required": [
            "workspaceId",
            "sessionId"
          ]
        },
        "returns": "SessionEvent[]",
        "safety": "read"
      },
      "readEventPayload": {
        "description": "Hydrate ONE skeletonized event's full `data`, looping `has_more` to completion so the returned object is the COMPLETE, byte-identical event data — never a partial page. The server pages the event's `data` TEXT rune-safely; this reassembles the fragments and JSON-parses the whole. Rejects loudly on a non-2xx (404 → the event is no longer available in the caller's lane; the caller maps that to hydrationError='not_available'). A page whose reassembled text fails to parse is a loud error, not a silent empty (no-fallbacks).",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string",
          "sequenceNum": "number",
          "maxBytes": "number (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            },
            "sequenceNum": {
              "type": "number"
            },
            "maxBytes": {
              "type": "number"
            }
          },
          "required": [
            "workspaceId",
            "sessionId",
            "sequenceNum"
          ]
        },
        "returns": "HydratedEventData",
        "safety": "read"
      },
      "readEventPayloads": {
        "description": "Hydrate a WINDOW of events in one request (one server query). Each returned entry is a byte-bounded first page: a COMPLETE entry (`has_more` false) is parsed to `data`; an incomplete one carries `data: null` + `has_more: true` and the caller completes it with {@link readEventPayload}. `sequenceNums` is server-capped — an over-cap request rejects 422.",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string",
          "sequenceNums": "number[]",
          "maxBytes": "number (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            },
            "sequenceNums": {
              "type": "array",
              "items": {
                "type": "number"
              }
            },
            "maxBytes": {
              "type": "number"
            }
          },
          "required": [
            "workspaceId",
            "sessionId",
            "sequenceNums"
          ]
        },
        "returns": "HydratedEventWindowEntry[]",
        "safety": "read"
      },
      "subscribeSessionEvents": {
        "description": "Register the caller's live bridge connection as a `SessionScope` fanout target for `sessionId` and trigger a replay of persisted events after `params.after_sequence`. Server-managed subscription (no client-sent bridge frame, ADR-003): this HTTP action writes the `bridge_subscriptions` row as a side effect. Rejects loudly on a non-2xx (403 device-ownership / 404 session / 422 bad cursor). Workspace-scoped when a workspaceId is supplied, else the workspace-less sibling route.",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string",
          "params": "SubscribeSessionEventsRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "device_id": {
                  "type": "string"
                },
                "after_sequence": {
                  "type": "number"
                }
              },
              "required": [
                "device_id",
                "after_sequence"
              ]
            }
          },
          "required": [
            "workspaceId",
            "sessionId",
            "params"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "unsubscribeSessionEvents": {
        "description": "Delete the `(device_id, \"session\", sessionId)` subscription row. Runs the same session-authz + device-ownership checks as {@link subscribeSessionEvents} (a caller may only unsubscribe a device that is provably their own live connection). Rejects loudly on a non-2xx.",
        "params": {
          "workspaceId": "string | null | undefined",
          "sessionId": "string",
          "params": "UnsubscribeSessionEventsRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "sessionId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "device_id": {
                  "type": "string"
                }
              },
              "required": [
                "device_id"
              ]
            }
          },
          "required": [
            "workspaceId",
            "sessionId",
            "params"
          ]
        },
        "returns": "void",
        "safety": "write"
      }
    }
  },
  "shogunchop": {
    "description": "Local ShogunChop desktop daemon control (loopback) — status, auth, endpoints.",
    "methods": {
      "signIn": {
        "description": "SDK-orchestrated browser-bounce sign-in (account ADR-021). Opens the cloud login page in the user's system browser with a `?return=<loopback>` query string pointing at the daemon's `/auth/callback` route. The cloud login page handles the sign-in flow (Stytch OTP today; SAML/SSO/OAuth in the future — same code path on the desktop side) and on success redirects to the loopback URL with `?token=<API_JWT>` appended. Resolves after the system browser has been opened — fire-and-forget, matching the tray's `start_browser_signin`. Completion is observed via the daemon's `auth:changed` event stream (`phase: 'usable'`), not the return of this call. Callers that need to await completion can use {@link waitForSignInComplete}. Safe to call again while a sign-in is in flight: the daemon's `POST /auth/begin-signin` reuses the in-flight nonce within the handoff TTL and returns the same `login_url`, so a second click opens another browser tab without invalidating the first. Required SDK config: - `openExternal(url)` — passed via `SDKConfig.openExternal`. In the Electron main process this wraps `shell.openExternal`. In the renderer it wraps an IPC invoke. In Playwright fixtures it points at a `BrowserContext` so the test can drive the browser side. Options: - `cloudLoginUrl` — base URL of the cloud login page. If not passed, callers can read it from `sdk.shogunchop.getEndpoints()` and pass it in. cloud login URL. The cloud login page POSTs it to `/internal/test-tickets/verify` instead of rendering the Stytch OTP form. Used only by the Track 1 Playwright fixture. Per shogunchop ADR-003 \"Security considerations\": the daemon binds `/auth/callback` to 127.0.0.1 only; the cloud login page validates `?return=` is a loopback URL before redirecting.",
        "params": {
          "opts": "{ cloudLoginUrl?: string } (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "cloudLoginUrl": {
              "type": "string"
            }
          }
        },
        "returns": "void",
        "safety": "write"
      },
      "waitForSignInComplete": {
        "description": "Wait for an `auth:changed` frame carrying `phase: 'usable'`. Optional helper for callers that need to block until sign-in completes (e.g. Playwright fixtures). The desktop renderer does not use this — it mirrors the daemon event stream in `DaemonSessionGate`. Rejects with `SignInTimeoutError` after `timeoutMs` (default 5 minutes), and rejects immediately on a frame with no numeric `seq`.",
        "params": {
          "opts": "{ timeoutMs?: number } (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "timeoutMs": {
              "type": "number"
            }
          }
        },
        "returns": "void",
        "safety": "read"
      },
      "authStatus": {
        "description": "authStatus on the shogunchop resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ShogunChopAuthStatus",
        "safety": "read"
      },
      "authState": {
        "description": "The daemon's current phase, in the shape `/events` pushes — the boot snapshot the client is published on (sdk ADR-013). Unlike `authInfo()` it answers for EVERY phase, signed out included, and carries no bearer.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ShogunChopAuthSnapshot",
        "safety": "read"
      },
      "authInfo": {
        "description": "Get the full auth payload (JWT + tenant claims) the daemon persisted after the browser-bounce callback. The Electron renderer uses this to hydrate its auth atoms once the daemon has finished sign-in, so the user never sees an in-app OTP form (account ADR-021). Returns `null` when the daemon has no tokens yet (the 401 case) — callers should treat that as \"still pre-auth\", not an error.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ShogunChopAuthInfo | null",
        "safety": "read"
      },
      "logout": {
        "description": "logout on the shogunchop resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "void",
        "safety": "destructive"
      },
      "bridgeStatus": {
        "description": "bridgeStatus on the shogunchop resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ShogunChopBridgeStatus",
        "safety": "read"
      },
      "mcpServers": {
        "description": "mcpServers on the shogunchop resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ShogunChopMcpServer[]",
        "safety": "read"
      },
      "addMcpServer": {
        "description": "addMcpServer on the shogunchop resource.",
        "params": {
          "req": "AddShogunChopMcpServerRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string"
            },
            "command": {
              "type": "string"
            },
            "args": {
              "type": "array",
              "items": {
                "type": "string"
              }
            }
          },
          "required": [
            "name",
            "command",
            "args"
          ]
        },
        "returns": "ShogunChopMcpServer",
        "safety": "write"
      },
      "removeMcpServer": {
        "description": "removeMcpServer on the shogunchop resource.",
        "params": {
          "id": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "id": {
              "type": "string"
            }
          },
          "required": [
            "id"
          ]
        },
        "returns": "void",
        "safety": "destructive"
      },
      "mcpTools": {
        "description": "mcpTools on the shogunchop resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ShogunChopMcpTool[]",
        "safety": "read"
      },
      "indexStart": {
        "description": "indexStart on the shogunchop resource.",
        "params": {
          "req": "ShogunChopIndexStartRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "workspace_id": {
              "type": "string"
            },
            "workspace_path": {
              "type": "string"
            },
            "assignment_epoch": {
              "type": "number"
            }
          },
          "required": [
            "workspace_id",
            "workspace_path",
            "assignment_epoch"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "indexStop": {
        "description": "indexStop on the shogunchop resource.",
        "params": {
          "workspaceId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "type": "string"
            }
          },
          "required": [
            "workspaceId"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "indexStatus": {
        "description": "indexStatus on the shogunchop resource.",
        "params": {
          "workspaceId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "type": "string"
            }
          },
          "required": [
            "workspaceId"
          ]
        },
        "returns": "ShogunChopIndexStatus",
        "safety": "read"
      },
      "executeLocalTool": {
        "description": "executeLocalTool on the shogunchop resource.",
        "params": {
          "toolId": "string",
          "params": "Record<string, unknown>"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "toolId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {},
              "additionalProperties": {}
            }
          },
          "required": [
            "toolId",
            "params"
          ]
        },
        "returns": "ShogunChopToolResult",
        "safety": "destructive"
      },
      "approvePlatformCall": {
        "description": "Record the user's confirm-UI approval for a destructive `platform_call`. The daemon holds the authoritative approval store; the agent's re-issued call then executes. Keyed by the server-stamped `idempotency_key` (the ONLY value the renderer can supply — it cannot recompute the sha256).",
        "params": {
          "idempotencyKey": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "idempotencyKey": {
              "type": "string"
            }
          },
          "required": [
            "idempotencyKey"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "postPlatformCallResult": {
        "description": "Relay the renderer executor's outcome for a forwarded `platform_call` back to the awaiting daemon bridge handler, correlated by `request_id`. Exactly one of `result` / `error` is set — an error is surfaced verbatim to the agent (no fabricated success).",
        "params": {
          "requestId": "string",
          "outcome": "{ result: unknown } | { error: string }"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "requestId": {
              "type": "string"
            },
            "outcome": {
              "anyOf": [
                {
                  "type": "object",
                  "properties": {
                    "result": {}
                  },
                  "required": [
                    "result"
                  ]
                },
                {
                  "type": "object",
                  "properties": {
                    "error": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "error"
                  ]
                }
              ]
            }
          },
          "required": [
            "requestId",
            "outcome"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "info": {
        "description": "info on the shogunchop resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ShogunChopInfo",
        "safety": "read"
      },
      "health": {
        "description": "health on the shogunchop resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "{ status: string }",
        "safety": "read"
      },
      "version": {
        "description": "Daemon + bundle versions. `version` is always the running crate version; `bundle_version` is `null` when the daemon runs outside a macOS app bundle (dev mode), and the CFBundleShortVersionString otherwise. The About panel renders both side-by-side.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ShogunChopVersion",
        "safety": "read"
      },
      "resetLocalState": {
        "description": "Wipe ALL local ShogunChop state — tokens, indexes, MCP server registrations, cached config — except `device_id`. Emits an `auth:changed` event so any UI subscribed to events transitions to the signed-out state immediately. The Electron app's Settings → \"Reset local state\" calls this.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "void",
        "safety": "destructive"
      },
      "restoreTestAuth": {
        "description": "Restore the daemon to a known-good authenticated state as its **configured** identity, returning the identity it restored. Exists because the in-cluster pod daemon is a shared mutable resource across two test tiers: the SDK integration tier deliberately exercises `logout()`, `resetLocalState()`, and a handoff sign-in (all real product surfaces), and those mutations outlive the file that made them. Without a restore path, a consumer's result depends on which tier ran last. **Assert the returned identity, not merely `logged_in`.** The failure this exists to catch is a *re-tenanted* daemon — signed in as a different member of the same account — which a bare `logged_in` check passes. That is why the daemon returns its claims rather than just a status. Available only on a daemon built with the `test-auth` cargo feature AND running with `SHOGUNCHOP_AUTH_MODE=test`. The route does not exist in a production build (404) and refuses outside test mode (403) — both are loud, neither is a silent no-op.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ShogunChopRestoredAuth",
        "safety": "destructive"
      },
      "shutdown": {
        "description": "Graceful shutdown of the daemon process. The tray's Quit menu item calls this BEFORE terminating the Electron app, so launchd's `KeepAlive { SuccessfulExit = false }` lets the daemon stay quit instead of respawning it.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "void",
        "safety": "destructive"
      },
      "getEndpoints": {
        "description": "Cloud endpoint configuration the daemon was seeded with (per-stack). The Electron main process calls this once at SDK construction to learn which cloud the daemon is bound to, rather than hardcoding a cloud URL in JavaScript. See shogunchop ADR-001.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ShogunChopEndpoints",
        "safety": "read"
      }
    }
  },
  "skills": {
    "description": "Account catalog skills — list, get, create, update, and delete shared instruction text addressed as shogun::name.",
    "methods": {
      "list": {
        "description": "list on the skills resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ListAccountSkillsResponse",
        "safety": "read"
      },
      "get": {
        "description": "get on the skills resource.",
        "params": {
          "skillId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "skillId": {
              "type": "string"
            }
          },
          "required": [
            "skillId"
          ]
        },
        "returns": "AccountSkill",
        "safety": "read"
      },
      "create": {
        "description": "create on the skills resource.",
        "params": {
          "params": "CreateAccountSkillRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string"
            },
            "description": {
              "type": "string"
            },
            "body": {
              "type": "string"
            }
          },
          "required": [
            "name",
            "description",
            "body"
          ]
        },
        "returns": "AccountSkill",
        "safety": "write"
      },
      "update": {
        "description": "update on the skills resource.",
        "params": {
          "skillId": "string",
          "params": "UpdateAccountSkillRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "skillId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "description": {
                  "type": "string"
                },
                "body": {
                  "type": "string"
                }
              }
            }
          },
          "required": [
            "skillId",
            "params"
          ]
        },
        "returns": "AccountSkill",
        "safety": "write"
      },
      "delete": {
        "description": "delete on the skills resource.",
        "params": {
          "skillId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "skillId": {
              "type": "string"
            }
          },
          "required": [
            "skillId"
          ]
        },
        "returns": "DeleteAccountSkillResponse",
        "safety": "write"
      }
    }
  },
  "support": {
    "description": "Support tickets — create, list, get, comment, attach, mark read, unseen count, and operator admin routes.",
    "methods": {
      "create": {
        "description": "create on the support resource.",
        "params": {
          "params": "CreateSupportTicketRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "title": {
              "type": "string"
            },
            "description": {
              "type": "string"
            },
            "priority": {
              "type": "string",
              "enum": [
                "urgent",
                "high",
                "medium",
                "low"
              ]
            },
            "category": {
              "type": "string",
              "enum": [
                "account",
                "bug",
                "billing",
                "question",
                "other"
              ]
            },
            "audience": {
              "type": "string",
              "enum": [
                "account",
                "platform"
              ]
            },
            "session_id": {
              "type": "string"
            }
          },
          "required": [
            "title",
            "description"
          ]
        },
        "returns": "SupportTicket",
        "safety": "write"
      },
      "list": {
        "description": "list on the support resource.",
        "params": {
          "params": "ListSupportTicketsParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "search": {
              "type": "string"
            },
            "status": {
              "type": "string",
              "enum": [
                "open",
                "pending_response",
                "pending",
                "resolve",
                "closed"
              ]
            },
            "priority": {
              "type": "string",
              "enum": [
                "urgent",
                "high",
                "medium",
                "low"
              ]
            },
            "category": {
              "type": "string",
              "enum": [
                "account",
                "bug",
                "billing",
                "question",
                "other"
              ]
            },
            "audience": {
              "type": "string",
              "enum": [
                "account",
                "all",
                "platform"
              ]
            },
            "assignee_user_id": {
              "type": "string"
            },
            "this_account": {
              "type": "boolean"
            },
            "assigned_to_me": {
              "type": "boolean"
            },
            "limit": {
              "type": "number"
            },
            "cursor": {
              "type": "string"
            }
          }
        },
        "returns": "ListSupportTicketsResponse",
        "safety": "read"
      },
      "get": {
        "description": "get on the support resource.",
        "params": {
          "ticketId": "string",
          "params": "GetSupportTicketParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "include_internal": {
                  "type": "boolean"
                }
              }
            }
          },
          "required": [
            "ticketId"
          ]
        },
        "returns": "SupportTicketDetail",
        "safety": "read"
      },
      "comment": {
        "description": "comment on the support resource.",
        "params": {
          "ticketId": "string",
          "params": "AddSupportCommentRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "body": {
                  "type": "string"
                },
                "visibility": {
                  "type": "string",
                  "enum": [
                    "public",
                    "internal"
                  ]
                }
              },
              "required": [
                "body"
              ]
            }
          },
          "required": [
            "ticketId",
            "params"
          ]
        },
        "returns": "AddSupportCommentResponse",
        "safety": "write"
      },
      "attach": {
        "description": "attach on the support resource.",
        "params": {
          "ticketId": "string",
          "params": "AttachSupportArtifactRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "artifact_id": {
                  "type": "string"
                },
                "comment_id": {
                  "type": "string"
                }
              },
              "required": [
                "artifact_id"
              ]
            }
          },
          "required": [
            "ticketId",
            "params"
          ]
        },
        "returns": "SupportAttachment",
        "safety": "write"
      },
      "markRead": {
        "description": "markRead on the support resource.",
        "params": {
          "ticketId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            }
          },
          "required": [
            "ticketId"
          ]
        },
        "returns": "SupportUnseenCount",
        "safety": "write"
      },
      "unseen": {
        "description": "unseen on the support resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "SupportUnseenCount",
        "safety": "read"
      },
      "content": {
        "description": "content on the support resource.",
        "params": {
          "ticketId": "string",
          "attachmentId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            },
            "attachmentId": {
              "type": "string"
            }
          },
          "required": [
            "ticketId",
            "attachmentId"
          ]
        },
        "returns": "ArrayBuffer",
        "safety": "read"
      },
      "adminList": {
        "description": "adminList on the support resource.",
        "params": {
          "params": "ListSupportTicketsParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "search": {
              "type": "string"
            },
            "status": {
              "type": "string",
              "enum": [
                "open",
                "pending_response",
                "pending",
                "resolve",
                "closed"
              ]
            },
            "priority": {
              "type": "string",
              "enum": [
                "urgent",
                "high",
                "medium",
                "low"
              ]
            },
            "category": {
              "type": "string",
              "enum": [
                "account",
                "bug",
                "billing",
                "question",
                "other"
              ]
            },
            "audience": {
              "type": "string",
              "enum": [
                "account",
                "all",
                "platform"
              ]
            },
            "assignee_user_id": {
              "type": "string"
            },
            "this_account": {
              "type": "boolean"
            },
            "assigned_to_me": {
              "type": "boolean"
            },
            "limit": {
              "type": "number"
            },
            "cursor": {
              "type": "string"
            }
          }
        },
        "returns": "ListSupportTicketsResponse",
        "safety": "read"
      },
      "adminGet": {
        "description": "adminGet on the support resource.",
        "params": {
          "ticketId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            }
          },
          "required": [
            "ticketId"
          ]
        },
        "returns": "SupportTicketDetail",
        "safety": "read"
      },
      "adminUpdate": {
        "description": "adminUpdate on the support resource.",
        "params": {
          "ticketId": "string",
          "params": "UpdateSupportTicketRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "status": {
                  "type": "string",
                  "enum": [
                    "open",
                    "pending_response",
                    "pending",
                    "resolve",
                    "closed"
                  ]
                },
                "priority": {
                  "type": "string",
                  "enum": [
                    "urgent",
                    "high",
                    "medium",
                    "low"
                  ]
                },
                "category": {
                  "type": "string",
                  "enum": [
                    "account",
                    "bug",
                    "billing",
                    "question",
                    "other"
                  ]
                },
                "assignee_user_id": {
                  "anyOf": [
                    {
                      "type": "null"
                    },
                    {
                      "type": "string"
                    }
                  ]
                },
                "title": {
                  "type": "string"
                }
              }
            }
          },
          "required": [
            "ticketId",
            "params"
          ]
        },
        "returns": "SupportTicket",
        "safety": "write"
      },
      "adminComment": {
        "description": "adminComment on the support resource.",
        "params": {
          "ticketId": "string",
          "params": "AddSupportCommentRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "body": {
                  "type": "string"
                },
                "visibility": {
                  "type": "string",
                  "enum": [
                    "public",
                    "internal"
                  ]
                }
              },
              "required": [
                "body"
              ]
            }
          },
          "required": [
            "ticketId",
            "params"
          ]
        },
        "returns": "AddSupportCommentResponse",
        "safety": "write"
      },
      "adminAttach": {
        "description": "adminAttach on the support resource.",
        "params": {
          "ticketId": "string",
          "params": "AttachSupportArtifactRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "artifact_id": {
                  "type": "string"
                },
                "comment_id": {
                  "type": "string"
                }
              },
              "required": [
                "artifact_id"
              ]
            }
          },
          "required": [
            "ticketId",
            "params"
          ]
        },
        "returns": "SupportAttachment",
        "safety": "write"
      },
      "adminMarkRead": {
        "description": "adminMarkRead on the support resource.",
        "params": {
          "ticketId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            }
          },
          "required": [
            "ticketId"
          ]
        },
        "returns": "SupportUnseenCount",
        "safety": "write"
      }
    }
  },
  "tools": {
    "description": "Tool registry, MCP tools, and schema management.",
    "methods": {
      "registerMCP": {
        "description": "registerMCP on the tools resource.",
        "params": {
          "deviceId": "string",
          "params": "RegisterMCPRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "mcp_id": {
                  "type": "string"
                },
                "name": {
                  "type": "string"
                },
                "description": {
                  "anyOf": [
                    {
                      "type": "null"
                    },
                    {
                      "type": "string"
                    }
                  ]
                },
                "transport": {
                  "type": "string",
                  "enum": [
                    "backend-local",
                    "http",
                    "bridge",
                    "integration",
                    "provider-native"
                  ]
                },
                "config": {
                  "type": "object",
                  "properties": {
                    "url": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "string"
                        }
                      ]
                    },
                    "command": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "string"
                        }
                      ]
                    },
                    "args": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "array",
                          "items": {
                            "type": "string"
                          }
                        }
                      ]
                    },
                    "env": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "object",
                          "properties": {},
                          "additionalProperties": {
                            "type": "string"
                          }
                        }
                      ]
                    },
                    "headers": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "object",
                          "properties": {},
                          "additionalProperties": {
                            "type": "string"
                          }
                        }
                      ]
                    },
                    "tools_enabled": {
                      "anyOf": [
                        {
                          "type": "null"
                        },
                        {
                          "type": "array",
                          "items": {
                            "type": "string"
                          }
                        }
                      ]
                    }
                  }
                }
              },
              "required": [
                "mcp_id",
                "name",
                "transport",
                "config"
              ]
            }
          },
          "required": [
            "deviceId",
            "params"
          ]
        },
        "returns": "RegisterMCPResponse",
        "safety": "write"
      },
      "listMCPs": {
        "description": "listMCPs on the tools resource.",
        "params": {
          "deviceId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            }
          },
          "required": [
            "deviceId"
          ]
        },
        "returns": "ListMCPsResponse",
        "safety": "read"
      },
      "updateMCP": {
        "description": "updateMCP on the tools resource.",
        "params": {
          "deviceId": "string",
          "mcpId": "string",
          "params": "UpdateMCPRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            },
            "mcpId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "enabled": {
                  "anyOf": [
                    {
                      "type": "null"
                    },
                    {
                      "type": "boolean"
                    }
                  ]
                },
                "config": {
                  "anyOf": [
                    {
                      "type": "null"
                    },
                    {
                      "type": "object",
                      "properties": {
                        "url": {
                          "anyOf": [
                            {
                              "type": "null"
                            },
                            {
                              "type": "string"
                            }
                          ]
                        },
                        "command": {
                          "anyOf": [
                            {
                              "type": "null"
                            },
                            {
                              "type": "string"
                            }
                          ]
                        },
                        "args": {
                          "anyOf": [
                            {
                              "type": "null"
                            },
                            {
                              "type": "array",
                              "items": {
                                "type": "string"
                              }
                            }
                          ]
                        },
                        "env": {
                          "anyOf": [
                            {
                              "type": "null"
                            },
                            {
                              "type": "object",
                              "properties": {},
                              "additionalProperties": {
                                "type": "string"
                              }
                            }
                          ]
                        },
                        "headers": {
                          "anyOf": [
                            {
                              "type": "null"
                            },
                            {
                              "type": "object",
                              "properties": {},
                              "additionalProperties": {
                                "type": "string"
                              }
                            }
                          ]
                        },
                        "tools_enabled": {
                          "anyOf": [
                            {
                              "type": "null"
                            },
                            {
                              "type": "array",
                              "items": {
                                "type": "string"
                              }
                            }
                          ]
                        }
                      }
                    }
                  ]
                }
              }
            }
          },
          "required": [
            "deviceId",
            "mcpId",
            "params"
          ]
        },
        "returns": "UpdateMCPResponse",
        "safety": "write"
      },
      "deleteMCP": {
        "description": "deleteMCP on the tools resource.",
        "params": {
          "deviceId": "string",
          "mcpId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            },
            "mcpId": {
              "type": "string"
            }
          },
          "required": [
            "deviceId",
            "mcpId"
          ]
        },
        "returns": "DeleteMCPResponse",
        "safety": "write"
      },
      "reconcileDeviceMcp": {
        "description": "Converge a device's persisted MCP registrations onto the live set its daemon is actually running: adopt the reported servers, reap the rest. `endpoints` is the device's COMPLETE live set, and the empty array is meaningful — it reaps every registration for this device. Omitting the field is a 4xx, never an implicit reap-all, so a caller that failed to enumerate its servers can never be read as a caller with none. The server owns `scope`/`location` (derived from `transport`) and pins `device_id` to the path; those fields may be sent, but a value that disagrees is rejected by name rather than silently corrected. `enabled` and `description` are platform-owned and are never written by a reconcile, so an admin-disabled server stays disabled across daemon restarts. Not agent-reachable: it is excluded from the `platform_call` dispatch allowlist. The reconcile carries a daemon-authoritative live set with a whole-device reap, and an agent has no live set to report — a dispatchable reconcile could only ever destroy registrations.",
        "params": {
          "deviceId": "string",
          "endpoints": "ReconcileMCPEndpoint[]"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            },
            "endpoints": {
              "type": "array",
              "items": {
                "type": "object",
                "properties": {
                  "mcp_id": {
                    "type": "string"
                  },
                  "name": {
                    "type": "string"
                  },
                  "transport": {
                    "type": "string",
                    "enum": [
                      "backend-local",
                      "http",
                      "bridge",
                      "integration",
                      "provider-native"
                    ]
                  },
                  "config": {
                    "type": "object",
                    "properties": {
                      "url": {
                        "anyOf": [
                          {
                            "type": "null"
                          },
                          {
                            "type": "string"
                          }
                        ]
                      },
                      "command": {
                        "anyOf": [
                          {
                            "type": "null"
                          },
                          {
                            "type": "string"
                          }
                        ]
                      },
                      "args": {
                        "anyOf": [
                          {
                            "type": "null"
                          },
                          {
                            "type": "array",
                            "items": {
                              "type": "string"
                            }
                          }
                        ]
                      },
                      "env": {
                        "anyOf": [
                          {
                            "type": "null"
                          },
                          {
                            "type": "object",
                            "properties": {},
                            "additionalProperties": {
                              "type": "string"
                            }
                          }
                        ]
                      },
                      "headers": {
                        "anyOf": [
                          {
                            "type": "null"
                          },
                          {
                            "type": "object",
                            "properties": {},
                            "additionalProperties": {
                              "type": "string"
                            }
                          }
                        ]
                      },
                      "tools_enabled": {
                        "anyOf": [
                          {
                            "type": "null"
                          },
                          {
                            "type": "array",
                            "items": {
                              "type": "string"
                            }
                          }
                        ]
                      }
                    }
                  },
                  "scope": {
                    "type": "string"
                  },
                  "location": {
                    "type": "string"
                  },
                  "device_id": {
                    "type": "string"
                  },
                  "workspace_id": {
                    "type": "string"
                  }
                },
                "required": [
                  "mcp_id",
                  "name",
                  "transport"
                ]
              }
            }
          },
          "required": [
            "deviceId",
            "endpoints"
          ]
        },
        "returns": "ReconcileDeviceMcpResult",
        "safety": "write"
      },
      "getToolCatalog": {
        "description": "getToolCatalog on the tools resource.",
        "params": {
          "deviceId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            }
          },
          "required": [
            "deviceId"
          ]
        },
        "returns": "ToolCatalogResponse",
        "safety": "read"
      },
      "listMCPTools": {
        "description": "listMCPTools on the tools resource.",
        "params": {
          "deviceId": "string",
          "mcpId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            },
            "mcpId": {
              "type": "string"
            }
          },
          "required": [
            "deviceId",
            "mcpId"
          ]
        },
        "returns": "ListMCPToolsResponse",
        "safety": "read"
      },
      "listTenantMCPs": {
        "description": "listTenantMCPs on the tools resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ListMCPsResponse",
        "safety": "read"
      },
      "listTenantMCPTools": {
        "description": "listTenantMCPTools on the tools resource.",
        "params": {
          "mcpId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "mcpId": {
              "type": "string"
            }
          },
          "required": [
            "mcpId"
          ]
        },
        "returns": "ListMCPToolsResponse",
        "safety": "read"
      },
      "getTenantToolCatalog": {
        "description": "getTenantToolCatalog on the tools resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ToolCatalogResponse",
        "safety": "read"
      },
      "listAvailableTools": {
        "description": "listAvailableTools on the tools resource.",
        "params": {
          "workspaceId": "string | null (optional)",
          "deviceId": "string (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "deviceId": {
              "type": "string"
            }
          }
        },
        "returns": "ListAvailableToolsResponse",
        "safety": "read"
      },
      "executeTool": {
        "description": "executeTool on the tools resource.",
        "params": {
          "workspaceId": "string | null | undefined",
          "params": "ExecuteToolRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "string"
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "tool_name": {
                  "type": "string"
                },
                "arguments": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                },
                "device_id": {
                  "anyOf": [
                    {
                      "type": "null"
                    },
                    {
                      "type": "string"
                    }
                  ]
                },
                "session_id": {
                  "anyOf": [
                    {
                      "type": "null"
                    },
                    {
                      "type": "string"
                    }
                  ]
                },
                "agent_invoked": {
                  "anyOf": [
                    {
                      "type": "null"
                    },
                    {
                      "type": "boolean"
                    }
                  ],
                  "description": "Declares an agent-context invocation for tools that gate on the trigger locus (workspace_init / platform_onboarding session-busy check): true skips the gate, false forces it. Omit to let the backend infer from the invocation path (the direct execute path is user-triggered)."
                },
                "direct_invocation_override": {
                  "anyOf": [
                    {
                      "type": "null"
                    },
                    {
                      "type": "object",
                      "properties": {
                        "idempotency_scope": {
                          "type": "string"
                        },
                        "confirm_destructive": {
                          "type": "boolean"
                        }
                      }
                    }
                  ],
                  "description": "Trusted override for a direct (non-agent) `platform_call`. Rides the executeTool request (the trusted channel) — an agent turn never constructs this request, so the override is structurally unreachable to agents. Ignored for non-`platform_call` tools and on the agent path."
                }
              },
              "required": [
                "tool_name",
                "arguments"
              ]
            }
          },
          "required": [
            "workspaceId",
            "params"
          ]
        },
        "returns": "ExecuteToolResponse",
        "safety": "destructive"
      },
      "listAvailableSkills": {
        "description": "Discover the workspace's `.shogunchop/skills/` index. A thin read mirroring listAvailableTools; the palette merges these skill-kind capabilities with listAvailableTools' coded tools. `deviceId` selects the local (daemon) substrate for workspaces with no cloud repository — same optional threading as listAvailableTools.",
        "params": {
          "workspaceId": "string",
          "deviceId": "string (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "type": "string"
            },
            "deviceId": {
              "type": "string"
            }
          },
          "required": [
            "workspaceId"
          ]
        },
        "returns": "ListAvailableSkillsResponse",
        "safety": "read"
      }
    }
  },
  "user": {
    "description": "Per-user preferences — get and update UI/editor preference settings.",
    "methods": {
      "getPreferences": {
        "description": "Read the authenticated user's preferences. Always resolves to an object.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "UserPreferences",
        "safety": "read"
      },
      "updatePreferences": {
        "description": "Shallow-merge a partial preferences patch into the stored blob. Top-level keys in `patch` overwrite; keys not present are preserved. Returns the merged preferences.",
        "params": {
          "patch": "Partial<UserPreferences>"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "chat_file_panel_position": {
              "type": "string",
              "enum": [
                "left",
                "right",
                "top",
                "bottom"
              ]
            },
            "chat_file_panel_size": {
              "type": "number"
            },
            "editor_autosave_enabled": {
              "type": "boolean",
              "description": "Idle autosave for the workspace file editor. Default is TRUE when unset — only an explicit `false` disables autosave."
            },
            "shogun_dock_mode": {
              "type": "string",
              "enum": [
                "left",
                "right",
                "top",
                "bottom",
                "floating"
              ],
              "description": "Shogun panel dock mode. Semantic placement follows the user across devices; geometry (floating rect) stays device-local in localStorage. Default is `floating` when unset."
            },
            "shogun_open_on_login": {
              "type": "boolean",
              "description": "Open the Shogun panel at authenticated session start. Default is TRUE when unset — only an explicit `false` keeps the panel closed on login. Kept in step with the user's last open/close of the panel (close ⇒ false, open ⇒ true)."
            },
            "session_duration_minutes": {
              "type": "number",
              "description": "Preferred Stytch Member Session duration in minutes. Default 43200 (30 days); clamp to Stytch's [5, 527040] at write time."
            }
          },
          "additionalProperties": {}
        },
        "returns": "UserPreferences",
        "safety": "write"
      },
      "getUiPreferences": {
        "description": "getUiPreferences on the user resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "UserUiPreferences",
        "safety": "read"
      },
      "updateUiPreferences": {
        "description": "updateUiPreferences on the user resource.",
        "params": {
          "patch": "UserUiPreferences"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "experience_mode": {
              "type": "string",
              "enum": [
                "normal",
                "advanced"
              ]
            }
          }
        },
        "returns": "UserUiPreferences",
        "safety": "write"
      }
    }
  },
  "workManagement": {
    "description": "Work items, tasks, and project management.",
    "methods": {
      "createTeam": {
        "description": "Create a team — omit workspace_id to provision a new cloud workspace, or pass workspace_id to bind an existing active workspace (local or cloud) not already owned by another team. Does not create or staff a manager. A later {@link WorkManagementResource.createGoal} 422s until {@link OrgChartResource.staffPosition} occupies a `manager` seat on this team (persona + agent process). Non-CEO seats need a `parent_id`.",
        "params": {
          "params": "CreateTeamRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string"
            },
            "identifier_prefix": {
              "type": "string",
              "description": "Ticket-key prefix: 2-5 uppercase letters A-Z only, no digits, spaces or punctuation (e.g. `ENG`)."
            },
            "description": {
              "type": "string"
            },
            "workspace_id": {
              "type": "string",
              "description": "Existing active workspace to bind as the team's sole workspace. Omit to provision a new cloud workspace (default)."
            }
          },
          "required": [
            "name",
            "identifier_prefix"
          ]
        },
        "returns": "Team",
        "safety": "write"
      },
      "listTeams": {
        "description": "listTeams on the workManagement resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "Team[]",
        "safety": "read"
      },
      "getTeam": {
        "description": "getTeam on the workManagement resource.",
        "params": {
          "teamId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "teamId": {
              "type": "string"
            }
          },
          "required": [
            "teamId"
          ]
        },
        "returns": "Team",
        "safety": "read"
      },
      "updateTeam": {
        "description": "updateTeam on the workManagement resource.",
        "params": {
          "teamId": "string",
          "params": "UpdateTeamRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "teamId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "name": {
                  "type": "string"
                },
                "description": {
                  "type": "string"
                }
              }
            }
          },
          "required": [
            "teamId",
            "params"
          ]
        },
        "returns": "Team",
        "safety": "write"
      },
      "deleteTeam": {
        "description": "deleteTeam on the workManagement resource.",
        "params": {
          "teamId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "teamId": {
              "type": "string"
            }
          },
          "required": [
            "teamId"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "listTeamRoster": {
        "description": "List the org-chart positions assigned to a team — the team's roster. A team's membership IS its slice of the org chart: a position carries the `team_id` and the occupant (`user_id` or `persona_id`), so there is no separate member record to read. Returns every position whose `team_id` is `teamId`, occupied or vacant.",
        "params": {
          "teamId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "teamId": {
              "type": "string"
            }
          },
          "required": [
            "teamId"
          ]
        },
        "returns": "Position[]",
        "safety": "read"
      },
      "createGoal": {
        "description": "Create a goal for a team to execute. `schedule_kind` decides when the team picks it up: `immediate` starts decomposition now, `at` requires `schedule_at` (RFC3339), and `recurring` requires `schedule_cron`. The goal is returned in its initial state; the manager session and automation attach asynchronously. The team's manager position must already be staffed (`persona_id` and `agent_process_id`). A vacant manager is a 422 naming {@link OrgChartResource.staffPosition}.",
        "params": {
          "params": "CreateGoalRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "team_id": {
              "type": "string"
            },
            "objective": {
              "type": "string"
            },
            "schedule_kind": {
              "type": "string",
              "enum": [
                "immediate",
                "at",
                "recurring"
              ]
            },
            "schedule_at": {
              "type": "string"
            },
            "schedule_cron": {
              "type": "string"
            }
          },
          "required": [
            "team_id",
            "objective",
            "schedule_kind"
          ]
        },
        "returns": "Goal",
        "safety": "write"
      },
      "listGoals": {
        "description": "List goals, optionally narrowed to one team and/or one state.",
        "params": {
          "params": "ListGoalsParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "team_id": {
              "type": "string"
            },
            "state": {
              "type": "string",
              "enum": [
                "draft",
                "scheduled",
                "decomposing",
                "executing",
                "verifying",
                "completed",
                "paused",
                "cancelled",
                "failed"
              ]
            }
          }
        },
        "returns": "Goal[]",
        "safety": "read"
      },
      "getGoal": {
        "description": "Read one goal. 404s when no goal with that id exists in the caller's tenant.",
        "params": {
          "goalId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "goalId": {
              "type": "string"
            }
          },
          "required": [
            "goalId"
          ]
        },
        "returns": "Goal",
        "safety": "read"
      },
      "setGoalSchedule": {
        "description": "Replace a goal's schedule, returning the updated goal. The same `schedule_kind` rules as {@link WorkManagementResource.createGoal} apply: `at` requires `schedule_at`, `recurring` requires `schedule_cron`.",
        "params": {
          "goalId": "string",
          "params": "SetGoalScheduleRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "goalId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "schedule_kind": {
                  "type": "string",
                  "enum": [
                    "immediate",
                    "at",
                    "recurring"
                  ]
                },
                "schedule_at": {
                  "type": "string"
                },
                "schedule_cron": {
                  "type": "string"
                }
              },
              "required": [
                "schedule_kind"
              ]
            }
          },
          "required": [
            "goalId",
            "params"
          ]
        },
        "returns": "Goal",
        "safety": "write"
      },
      "cancelGoal": {
        "description": "Cancel a goal, returning it in the `cancelled` state. Terminal — a cancelled goal is not rescheduled, and its work stops being advanced.",
        "params": {
          "goalId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "goalId": {
              "type": "string"
            }
          },
          "required": [
            "goalId"
          ]
        },
        "returns": "Goal",
        "safety": "write"
      },
      "restartGoal": {
        "description": "Restart a goal: cancel it, stop everything it has running, and create a NEW goal with the same objective, team, schedule and creator. Returns the {@link GoalRestartResult} envelope, NOT a flat `Goal` — the new goal is under `goal`, and `previous_goal_id` names the run it replaces. Nothing is deleted: the prior goal, its initiatives and its projects stay readable, which is what makes one run comparable against the next. Its unfinished projects and tickets are moved to `cancelled`, so the prior run stops being advanced. This is how a prompt is iterated on — a product action, not a database edit. 404s when no goal with that id exists in the caller's tenant.",
        "params": {
          "goalId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "goalId": {
              "type": "string"
            }
          },
          "required": [
            "goalId"
          ]
        },
        "returns": "GoalRestartResult",
        "safety": "write"
      },
      "reconcileGoalState": {
        "description": "Re-derive a goal's label from the actual state of its projects and write it if it has fallen behind, returning the reconcile result. The label may only advance (`scheduled`/`decomposing` → `executing`/ `verifying`) — it never moves backwards. A goal with no projects, a `draft` goal, or a goal already settled (`completed`/`cancelled`/`failed`) or `paused` is returned unchanged with a `reason` naming why nothing moved. When every project is settled, the completion rollup is re-driven, which is what lets a stalled goal finish. This is the recovery lever when the label no longer describes the work — the state is derived from the census, never asserted. 404s when no goal with that id exists in the caller's tenant.",
        "params": {
          "goalId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "goalId": {
              "type": "string"
            }
          },
          "required": [
            "goalId"
          ]
        },
        "returns": "GoalReconcileResult",
        "safety": "write"
      },
      "continueGoal": {
        "description": "Re-kick a stalled but active goal: reconcile its label, clear the plan cycles that stopped its non-terminal projects, restart each one's run, and fire the schedule sweep so due triggers run now. This is the recovery lever when the team is not working the tickets — the exhausted-bound case nothing else un-sticks. A settled goal is returned with `status: \"skipped\"` and nothing runs. Idempotent: safe to call again after a partial recovery.",
        "params": {
          "goalId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "goalId": {
              "type": "string"
            }
          },
          "required": [
            "goalId"
          ]
        },
        "returns": "GoalContinueResult",
        "safety": "write"
      },
      "getGoalRoadmap": {
        "description": "Read a goal together with its ordering edges — the goal's roadmap. Returns the {@link GoalRoadmap} envelope (`{ goal, orderings }`), NOT a flat goal: one round trip for the observation surface that renders a goal and the dependency edges between its work items.",
        "params": {
          "goalId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "goalId": {
              "type": "string"
            }
          },
          "required": [
            "goalId"
          ]
        },
        "returns": "GoalRoadmap",
        "safety": "read"
      },
      "getGoalActivity": {
        "description": "Read one goal together with everything happening beneath it — its initiatives, their projects, and those projects' tasks — as ONE assembled snapshot. Returns the {@link GoalActivity} envelope (`{ goal, initiatives }`), NOT a flat `Goal`. Every level carries its state, its assignee, and the id of the session working it when one exists, so the observation surface renders the hierarchy without composing four reads client-side — four reads can interleave with a process advance and show a state that never existed.",
        "params": {
          "goalId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "goalId": {
              "type": "string"
            }
          },
          "required": [
            "goalId"
          ]
        },
        "returns": "GoalActivity",
        "safety": "read"
      },
      "addWorkOrdering": {
        "description": "Add an ordering edge between two of a goal's work items: `blocked_node_id` waits for `blocking_node_id`. Both endpoints must be nodes of the SAME goal and of the kind named by `node_kind` (`initiative` or `project`); a self-edge or a foreign endpoint is rejected.",
        "params": {
          "goalId": "string",
          "params": "AddWorkOrderingRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "goalId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "node_kind": {
                  "type": "string",
                  "enum": [
                    "project",
                    "initiative"
                  ]
                },
                "blocked_node_id": {
                  "type": "string"
                },
                "blocking_node_id": {
                  "type": "string"
                }
              },
              "required": [
                "node_kind",
                "blocked_node_id",
                "blocking_node_id"
              ]
            }
          },
          "required": [
            "goalId",
            "params"
          ]
        },
        "returns": "WorkOrdering",
        "safety": "write"
      },
      "listWorkOrderings": {
        "description": "List a goal's ordering edges.",
        "params": {
          "goalId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "goalId": {
              "type": "string"
            }
          },
          "required": [
            "goalId"
          ]
        },
        "returns": "WorkOrdering[]",
        "safety": "read"
      },
      "removeWorkOrdering": {
        "description": "Remove one ordering edge, unblocking whatever it was gating.",
        "params": {
          "goalId": "string",
          "orderingId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "goalId": {
              "type": "string"
            },
            "orderingId": {
              "type": "string"
            }
          },
          "required": [
            "goalId",
            "orderingId"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "createTicket": {
        "description": "createTicket on the workManagement resource.",
        "params": {
          "params": "CreateTicketRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "team_id": {
              "type": "string"
            },
            "title": {
              "type": "string"
            },
            "description": {
              "type": "string"
            },
            "state": {
              "type": "string",
              "enum": [
                "cancelled",
                "backlog",
                "todo",
                "in_progress",
                "in_review",
                "done"
              ]
            },
            "status": {
              "type": "string",
              "enum": [
                "executing",
                "specifying",
                "ready",
                "needs_input",
                "planning",
                "validating",
                "changes_requested",
                "wont_fix",
                "duplicate"
              ]
            },
            "priority": {
              "type": "string",
              "enum": [
                "urgent",
                "high",
                "medium",
                "low",
                "none"
              ]
            },
            "assignee_user_id": {
              "type": "string"
            },
            "assignee_persona_id": {
              "type": "string"
            },
            "project_id": {
              "type": "string"
            },
            "workspace_id": {
              "type": "string"
            },
            "created_by_user_id": {
              "type": "string"
            },
            "created_by_persona_id": {
              "type": "string"
            }
          },
          "required": [
            "team_id",
            "title"
          ]
        },
        "returns": "Ticket",
        "safety": "write"
      },
      "listTickets": {
        "description": "listTickets on the workManagement resource.",
        "params": {
          "params": "ListTicketsParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "team_id": {
              "type": "string"
            },
            "state": {
              "type": "string",
              "enum": [
                "cancelled",
                "backlog",
                "todo",
                "in_progress",
                "in_review",
                "done"
              ]
            },
            "status": {
              "type": "string",
              "enum": [
                "executing",
                "specifying",
                "ready",
                "needs_input",
                "planning",
                "validating",
                "changes_requested",
                "wont_fix",
                "duplicate"
              ]
            },
            "priority": {
              "type": "string",
              "enum": [
                "urgent",
                "high",
                "medium",
                "low",
                "none"
              ]
            },
            "assignee_user_id": {
              "type": "string"
            },
            "assignee_persona_id": {
              "type": "string"
            },
            "project_id": {
              "type": "string"
            },
            "search": {
              "type": "string"
            },
            "limit": {
              "type": "number"
            },
            "offset": {
              "type": "number"
            }
          }
        },
        "returns": "TicketListResponse",
        "safety": "read"
      },
      "getTicket": {
        "description": "getTicket on the workManagement resource.",
        "params": {
          "ticketId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            }
          },
          "required": [
            "ticketId"
          ]
        },
        "returns": "TicketDetail",
        "safety": "read"
      },
      "updateTicket": {
        "description": "updateTicket on the workManagement resource.",
        "params": {
          "ticketId": "string",
          "params": "UpdateTicketRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "title": {
                  "type": "string"
                },
                "description": {
                  "type": "string"
                },
                "priority": {
                  "type": "string",
                  "enum": [
                    "urgent",
                    "high",
                    "medium",
                    "low",
                    "none"
                  ]
                },
                "project_id": {
                  "type": "string"
                },
                "workspace_id": {
                  "type": "string"
                }
              }
            }
          },
          "required": [
            "ticketId",
            "params"
          ]
        },
        "returns": "Ticket",
        "safety": "write"
      },
      "deleteTicket": {
        "description": "deleteTicket on the workManagement resource.",
        "params": {
          "ticketId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            }
          },
          "required": [
            "ticketId"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "transitionTicketState": {
        "description": "transitionTicketState on the workManagement resource.",
        "params": {
          "ticketId": "string",
          "params": "TicketTransitionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "target_state": {
                  "type": "string",
                  "enum": [
                    "cancelled",
                    "backlog",
                    "todo",
                    "in_progress",
                    "in_review",
                    "done"
                  ]
                },
                "target_status": {
                  "type": "string",
                  "enum": [
                    "executing",
                    "specifying",
                    "ready",
                    "needs_input",
                    "planning",
                    "validating",
                    "changes_requested",
                    "wont_fix",
                    "duplicate"
                  ]
                }
              },
              "required": [
                "target_state"
              ]
            }
          },
          "required": [
            "ticketId",
            "params"
          ]
        },
        "returns": "Ticket",
        "safety": "write"
      },
      "assignTicket": {
        "description": "assignTicket on the workManagement resource.",
        "params": {
          "ticketId": "string",
          "params": "AssignTicketRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "persona_id": {
                  "type": "string"
                }
              }
            }
          },
          "required": [
            "ticketId",
            "params"
          ]
        },
        "returns": "Ticket",
        "safety": "write"
      },
      "addComment": {
        "description": "addComment on the workManagement resource.",
        "params": {
          "ticketId": "string",
          "params": "AddCommentRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "body": {
                  "type": "string"
                },
                "author_user_id": {
                  "type": "string"
                },
                "author_persona_id": {
                  "type": "string"
                }
              },
              "required": [
                "body"
              ]
            }
          },
          "required": [
            "ticketId",
            "params"
          ]
        },
        "returns": "Comment",
        "safety": "write"
      },
      "listComments": {
        "description": "listComments on the workManagement resource.",
        "params": {
          "ticketId": "string",
          "params": "ListCommentsParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "limit": {
                  "type": "number"
                },
                "offset": {
                  "type": "number"
                }
              }
            }
          },
          "required": [
            "ticketId"
          ]
        },
        "returns": "Comment[]",
        "safety": "read"
      },
      "listTicketActivity": {
        "description": "listTicketActivity on the workManagement resource.",
        "params": {
          "ticketId": "string",
          "params": "{ limit?: number; offset?: number } (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "limit": {
                  "type": "number"
                },
                "offset": {
                  "type": "number"
                }
              }
            }
          },
          "required": [
            "ticketId"
          ]
        },
        "returns": "unknown[]",
        "safety": "read"
      },
      "addDependency": {
        "description": "addDependency on the workManagement resource.",
        "params": {
          "ticketId": "string",
          "params": "AddDependencyRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "blocking_ticket_id": {
                  "type": "string"
                }
              },
              "required": [
                "blocking_ticket_id"
              ]
            }
          },
          "required": [
            "ticketId",
            "params"
          ]
        },
        "returns": "unknown",
        "safety": "write"
      },
      "removeDependency": {
        "description": "removeDependency on the workManagement resource.",
        "params": {
          "ticketId": "string",
          "depId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            },
            "depId": {
              "type": "string"
            }
          },
          "required": [
            "ticketId",
            "depId"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "startWorkSession": {
        "description": "Start a work session on a work item and ignite its first turn. The executing role is the work item's own — for a ticket it resolves through the ticket's project to that project's assigned position, so `persona_id`, `agent_process_id` and `workspace_id` are omitted unless you are deliberately overriding the role.",
        "params": {
          "workItemKind": "WorkItemKind — - `goal`, `project` or `ticket`.",
          "workItemId": "string — - The work item to start work on.",
          "params": "StartWorkSessionRequest — - The opening prompt, and any role override."
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workItemKind": {
              "type": "string",
              "enum": [
                "ticket",
                "project",
                "goal"
              ]
            },
            "workItemId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "prompt": {
                  "type": "string"
                },
                "persona_id": {
                  "type": "string"
                },
                "agent_process_id": {
                  "type": "string"
                },
                "workspace_id": {
                  "type": "string"
                }
              },
              "required": [
                "prompt"
              ]
            }
          },
          "required": [
            "workItemKind",
            "workItemId",
            "params"
          ]
        },
        "returns": "StartWorkSessionResponse",
        "safety": "write"
      },
      "listTicketSessions": {
        "description": "listTicketSessions on the workManagement resource.",
        "params": {
          "ticketId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "ticketId": {
              "type": "string"
            }
          },
          "required": [
            "ticketId"
          ]
        },
        "returns": "TicketSession[]",
        "safety": "read"
      },
      "createProject": {
        "description": "createProject on the workManagement resource.",
        "params": {
          "params": "CreateProjectRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "team_id": {
              "type": "string"
            },
            "initiative_id": {
              "type": "string"
            },
            "name": {
              "type": "string"
            },
            "description": {
              "type": "string"
            },
            "state": {
              "type": "string"
            },
            "lead_user_id": {
              "type": "string"
            }
          },
          "required": [
            "team_id",
            "initiative_id",
            "name"
          ]
        },
        "returns": "Project",
        "safety": "write"
      },
      "listProjects": {
        "description": "listProjects on the workManagement resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "Project[]",
        "safety": "read"
      },
      "getProject": {
        "description": "Read one project with its progress rollup and every ticket in it, in any state. The response is the `ProjectDetail` envelope, NOT a flat `Project`.",
        "params": {
          "projectId": "string — - The project to read."
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "projectId": {
              "type": "string"
            }
          },
          "required": [
            "projectId"
          ]
        },
        "returns": "ProjectDetail",
        "safety": "read"
      },
      "updateProject": {
        "description": "updateProject on the workManagement resource.",
        "params": {
          "projectId": "string",
          "params": "UpdateProjectRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "projectId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "name": {
                  "type": "string"
                },
                "description": {
                  "type": "string"
                },
                "state": {
                  "type": "string"
                },
                "lead_user_id": {
                  "type": "string"
                }
              }
            }
          },
          "required": [
            "projectId",
            "params"
          ]
        },
        "returns": "Project",
        "safety": "write"
      },
      "deleteProject": {
        "description": "deleteProject on the workManagement resource.",
        "params": {
          "projectId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "projectId": {
              "type": "string"
            }
          },
          "required": [
            "projectId"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "assignProject": {
        "description": "Assign a project to the org chart position that will own it. Emits `wm.project.assigned`, which is what starts the assignee's work session.",
        "params": {
          "projectId": "string — - The project to assign.",
          "params": "AssignProjectRequest — - The assignee position id."
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "projectId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "assignee_position_id": {
                  "type": "string"
                }
              },
              "required": [
                "assignee_position_id"
              ]
            }
          },
          "required": [
            "projectId",
            "params"
          ]
        },
        "returns": "Project",
        "safety": "write"
      },
      "setProjectVerification": {
        "description": "Record the manager's verification decision on a project. `awaiting` asks the manager to verify, `accepted` completes the project, and `not_ready` returns it to its assignee.",
        "params": {
          "projectId": "string — - The project being verified.",
          "params": "SetProjectVerificationRequest — - The goal the project rolls up to and the verification state."
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "projectId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "goal_id": {
                  "type": "string"
                },
                "verification_state": {
                  "type": "string",
                  "enum": [
                    "not_ready",
                    "awaiting",
                    "accepted"
                  ]
                }
              },
              "required": [
                "goal_id",
                "verification_state"
              ]
            }
          },
          "required": [
            "projectId",
            "params"
          ]
        },
        "returns": "Project",
        "safety": "write"
      },
      "createInitiative": {
        "description": "createInitiative on the workManagement resource.",
        "params": {
          "params": "CreateInitiativeRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "team_id": {
              "type": "string"
            },
            "goal_id": {
              "type": "string"
            },
            "owner_position_id": {
              "type": "string"
            },
            "name": {
              "type": "string"
            },
            "description": {
              "type": "string"
            },
            "state": {
              "type": "string"
            }
          },
          "required": [
            "team_id",
            "goal_id",
            "owner_position_id",
            "name"
          ]
        },
        "returns": "Initiative",
        "safety": "write"
      },
      "listInitiatives": {
        "description": "listInitiatives on the workManagement resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "Initiative[]",
        "safety": "read"
      },
      "getInitiative": {
        "description": "getInitiative on the workManagement resource.",
        "params": {
          "initiativeId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "initiativeId": {
              "type": "string"
            }
          },
          "required": [
            "initiativeId"
          ]
        },
        "returns": "Initiative",
        "safety": "read"
      },
      "updateInitiative": {
        "description": "updateInitiative on the workManagement resource.",
        "params": {
          "initiativeId": "string",
          "params": "UpdateInitiativeRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "initiativeId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "name": {
                  "type": "string"
                },
                "description": {
                  "type": "string"
                },
                "state": {
                  "type": "string"
                }
              }
            }
          },
          "required": [
            "initiativeId",
            "params"
          ]
        },
        "returns": "Initiative",
        "safety": "write"
      },
      "deleteInitiative": {
        "description": "deleteInitiative on the workManagement resource.",
        "params": {
          "initiativeId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "initiativeId": {
              "type": "string"
            }
          },
          "required": [
            "initiativeId"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "linkProjectToInitiative": {
        "description": "Move a project to another initiative of the same goal. A project always belongs to exactly one initiative, so linking re-parents it: afterwards `listInitiativeProjects` on the target includes the project and its previous initiative no longer does. Moving to an initiative of a different goal is refused with a 409 `HIERARCHY_CONFLICT`.",
        "params": {
          "initiativeId": "string — - The initiative the project moves to.",
          "params": "LinkProjectRequest — - The project to move."
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "initiativeId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "project_id": {
                  "type": "string"
                }
              },
              "required": [
                "project_id"
              ]
            }
          },
          "required": [
            "initiativeId",
            "params"
          ]
        },
        "returns": "unknown",
        "safety": "write"
      },
      "listInitiativeProjects": {
        "description": "listInitiativeProjects on the workManagement resource.",
        "params": {
          "initiativeId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "initiativeId": {
              "type": "string"
            }
          },
          "required": [
            "initiativeId"
          ]
        },
        "returns": "Project[]",
        "safety": "read"
      },
      "unlinkProjectFromInitiative": {
        "description": "Always refused with a 409 `HIERARCHY_CONFLICT`: a project must belong to an initiative, so it cannot be detached from one. Move it to another initiative of its goal with `linkProjectToInitiative`, or delete it with `deleteProject`.",
        "params": {
          "initiativeId": "string — - The initiative the project belongs to.",
          "projectId": "string — - The project."
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "initiativeId": {
              "type": "string"
            },
            "projectId": {
              "type": "string"
            }
          },
          "required": [
            "initiativeId",
            "projectId"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "getBoardView": {
        "description": "getBoardView on the workManagement resource.",
        "params": {
          "teamId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "teamId": {
              "type": "string"
            }
          },
          "required": [
            "teamId"
          ]
        },
        "returns": "BoardViewResponse",
        "safety": "read"
      },
      "getAgentView": {
        "description": "getAgentView on the workManagement resource.",
        "params": {
          "personaId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "personaId": {
              "type": "string"
            }
          },
          "required": [
            "personaId"
          ]
        },
        "returns": "unknown",
        "safety": "read"
      },
      "getMyWorkView": {
        "description": "getMyWorkView on the workManagement resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "unknown",
        "safety": "read"
      },
      "getTicketBySession": {
        "description": "getTicketBySession on the workManagement resource.",
        "params": {
          "sessionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "sessionId": {
              "type": "string"
            }
          },
          "required": [
            "sessionId"
          ]
        },
        "returns": "SessionTicketMapping",
        "safety": "read"
      },
      "getSessionWorkItems": {
        "description": "Resolve a batch of session ids to the work items they belong to. Covers all three linked kinds in ONE call — `task` (a ticket), `project`, and `goal` — so a session-list badge does not need a second, goal-shaped lookup beside this one. A session with no linked work item is simply absent from the result; order and length do not mirror the input. Each entry carries the kind, the work item id, a display `identifier` (the ticket identifier, the project name, or the goal objective), the owning `team_id`, and the goal's manager session id when the item rolls up to a goal that has one.",
        "params": {
          "sessionIds": "string[]"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "sessionIds": {
              "type": "array",
              "items": {
                "type": "string"
              }
            }
          },
          "required": [
            "sessionIds"
          ]
        },
        "returns": "SessionWorkItem[]",
        "safety": "read"
      },
      "getWorkItemFiles": {
        "description": "List the files a work item changed, with how much changed in each. The list is DERIVED from the work item's sessions: every successful mutating file-tool call, joined to its result on the call id. A turn that wrote two files yields two rows; a failed write and an interrupted call are both absent. Scope is files written with the file tools — a file written by a shell command is not listed, and the surface should say so. Counts come from the stored workspace content, read without a running workspace, so this answers long after the workspace has gone to sleep.",
        "params": {
          "workItemKind": "string — `ticket`, `project` or `goal`",
          "workItemId": "string — The work item's id"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workItemKind": {
              "type": "string"
            },
            "workItemId": {
              "type": "string"
            }
          },
          "required": [
            "workItemKind",
            "workItemId"
          ]
        },
        "returns": "WorkItemFilesResponse",
        "safety": "read"
      },
      "compareFileVersions": {
        "description": "Read one file as it stood at two versions, for a read-only comparison. Returns the two documents and the integer counts — never any rendered text — so the client draws the comparison itself. A side past the per-side size ceiling, or one that is not text, comes back `unavailable` with a typed reason and no content: open the file normally instead of rendering part of it.",
        "params": {
          "workspaceId": "string — The workspace the file lives in",
          "params": "CompareFileVersionsParams — The path and the two version ids to compare"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "path": {
                  "type": "string"
                },
                "from_version": {
                  "type": "string"
                },
                "to_version": {
                  "type": "string"
                }
              },
              "required": [
                "path",
                "from_version",
                "to_version"
              ]
            }
          },
          "required": [
            "workspaceId",
            "params"
          ]
        },
        "returns": "FileComparisonResponse",
        "safety": "read"
      },
      "listPathWorkItems": {
        "description": "The reverse lookup: which work items wrote this file, newest first. More than one entry is ordinary — a draft written by one work item and revised by another is two entries for the same path.",
        "params": {
          "workspaceId": "string — The workspace the file lives in",
          "params": "ListPathWorkItemsParams — The path, and an optional cap on how many entries to return"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "path": {
                  "type": "string"
                },
                "limit": {
                  "type": "number"
                }
              },
              "required": [
                "path"
              ]
            }
          },
          "required": [
            "workspaceId",
            "params"
          ]
        },
        "returns": "WorkItemPathEntry[]",
        "safety": "read"
      },
      "rebuildFileProvenance": {
        "description": "Start rebuilding the reverse lookup for every work item in the account that owns a session, by replaying the same derivation the forward list uses. This does NOT wait for the rebuild to finish. It answers as soon as the rebuild is under way, reporting `state: 'running'` and the counts reached so far; poll by calling it again with the returned `rebuild_id` until `state` is `completed`, and only then are the counts final. There is no cap on how much a rebuild covers — a large account takes longer, it is not cut short. One rebuild runs per account at a time. Calling it with no `rebuild_id` while one is under way reports that one instead of starting a second, and `started` says which of the two happened. Polling a rebuild that is no longer the account's current one fails rather than answering about another. Idempotent: it sets each row rather than adding to it, so running it twice leaves the same rows. Use it to cover work that was done before the reverse lookup existed.",
        "params": {
          "params": "RebuildFileProvenanceParams (optional) — Optionally, the rebuild to report on instead of starting one"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "rebuild_id": {
              "type": "string",
              "description": "Report on this rebuild instead of starting one."
            }
          }
        },
        "returns": "RebuildFileProvenanceResult",
        "safety": "write"
      },
      "getExecutionCadence": {
        "description": "getExecutionCadence on the workManagement resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "ExecutionCadence",
        "safety": "read"
      },
      "setExecutionCadence": {
        "description": "setExecutionCadence on the workManagement resource.",
        "params": {
          "params": "ExecutionCadence"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "schedule_sweep": {
              "type": "string",
              "enum": [
                "1h",
                "30s",
                "1m",
                "5m",
                "15m"
              ]
            },
            "project_run_reaper": {
              "type": "string",
              "enum": [
                "1h",
                "30s",
                "1m",
                "5m",
                "15m"
              ]
            }
          },
          "required": [
            "schedule_sweep",
            "project_run_reaper"
          ]
        },
        "returns": "ExecutionCadence",
        "safety": "write"
      }
    }
  },
  "workspaces": {
    "description": "Workspace aggregate state, assignments, capabilities, actions, files, sandboxes, repositories, and version history.",
    "methods": {
      "list": {
        "description": "list on the workspaces resource.",
        "params": {
          "deviceId": "string (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "deviceId": {
              "type": "string"
            }
          }
        },
        "returns": "WorkspaceProjection[]",
        "safety": "read"
      },
      "listNames": {
        "description": "Complete id/name catalog for every non-deleted workspace, equipment-free (workspace_state ADR-6adfe).",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "WorkspaceNameEntry[]",
        "safety": "read"
      },
      "getState": {
        "description": "getState on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "deviceId": "string (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "deviceId": {
              "type": "string"
            }
          },
          "required": [
            "workspace"
          ]
        },
        "returns": "WorkspaceProjection",
        "safety": "read"
      },
      "getStateByName": {
        "description": "getStateByName on the workspaces resource.",
        "params": {
          "name": "string",
          "deviceId": "string (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "name": {
              "type": "string"
            },
            "deviceId": {
              "type": "string"
            }
          },
          "required": [
            "name"
          ]
        },
        "returns": "WorkspaceProjection",
        "safety": "read"
      },
      "createLocal": {
        "description": "createLocal on the workspaces resource.",
        "params": {
          "params": "CreateLocalWorkspaceRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string"
            },
            "workspace_root": {
              "type": "string"
            },
            "workspace_settings_dir": {
              "type": "string"
            },
            "device_id": {
              "type": "string"
            },
            "idempotency_key": {
              "type": "string"
            }
          },
          "required": [
            "workspace_root",
            "device_id",
            "idempotency_key"
          ]
        },
        "returns": "WorkspaceProjection",
        "safety": "write"
      },
      "create": {
        "description": "Create a cloud workspace, optionally publishing a selected connected-device folder first.",
        "params": {
          "params": "CreateCloudWorkspaceRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "params": {
              "anyOf": [
                {
                  "type": "object",
                  "properties": {
                    "kind": {
                      "type": "string",
                      "enum": [
                        "cloud"
                      ]
                    },
                    "name": {
                      "type": "string"
                    },
                    "idempotency_key": {
                      "type": "string"
                    },
                    "source_device_id": {
                      "type": "string"
                    },
                    "source_root": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "kind",
                    "idempotency_key",
                    "source_device_id",
                    "source_root"
                  ]
                },
                {
                  "type": "object",
                  "properties": {
                    "kind": {
                      "type": "string",
                      "enum": [
                        "cloud"
                      ]
                    },
                    "name": {
                      "type": "string"
                    },
                    "idempotency_key": {
                      "type": "string"
                    },
                    "source_device_id": {},
                    "source_root": {}
                  },
                  "required": [
                    "kind",
                    "idempotency_key"
                  ]
                }
              ]
            }
          },
          "required": [
            "params"
          ]
        },
        "returns": "WorkspaceProjection",
        "safety": "write"
      },
      "bind": {
        "description": "bind on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "params": "BindWorkspaceRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "device_id": {
                  "type": "string"
                },
                "folder_path": {
                  "type": "string"
                },
                "idempotency_key": {
                  "type": "string"
                }
              },
              "required": [
                "device_id",
                "folder_path",
                "idempotency_key"
              ]
            }
          },
          "required": [
            "workspace",
            "params"
          ]
        },
        "returns": "WorkspaceProjection",
        "safety": "write"
      },
      "reassignWorkstation": {
        "description": "Move a local workspace onto a different computer. Unlike {@link bind} this does not require the OUTGOING device to be connected — the premise is that it is gone.",
        "params": {
          "workspace": "WorkspaceRef",
          "params": "ReassignWorkstationRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "device_id": {
                  "type": "string"
                },
                "folder_path": {
                  "type": "string"
                },
                "workspace_settings_dir": {
                  "type": "string"
                },
                "idempotency_key": {
                  "type": "string"
                }
              },
              "required": [
                "device_id",
                "folder_path",
                "idempotency_key"
              ]
            }
          },
          "required": [
            "workspace",
            "params"
          ]
        },
        "returns": "WorkspaceProjection",
        "safety": "destructive"
      },
      "unbind": {
        "description": "unbind on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "deviceId": "string",
          "params": "UnbindWorkspaceRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "deviceId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "idempotency_key": {
                  "type": "string"
                }
              },
              "required": [
                "idempotency_key"
              ]
            }
          },
          "required": [
            "workspace",
            "deviceId",
            "params"
          ]
        },
        "returns": "WorkspaceProjection",
        "safety": "write"
      },
      "convert": {
        "description": "convert on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "params": "ConvertWorkspaceRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "idempotency_key": {
                  "type": "string"
                }
              },
              "required": [
                "idempotency_key"
              ]
            }
          },
          "required": [
            "workspace",
            "params"
          ]
        },
        "returns": "WorkspaceProjection",
        "safety": "destructive"
      },
      "archive": {
        "description": "archive on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "params": "ArchiveWorkspaceRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "idempotency_key": {
                  "type": "string"
                },
                "reason": {
                  "type": "string"
                }
              },
              "required": [
                "idempotency_key"
              ]
            }
          },
          "required": [
            "workspace",
            "params"
          ]
        },
        "returns": "WorkspaceProjection",
        "safety": "destructive"
      },
      "wake": {
        "description": "wake on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "params": "WorkspaceLifecycleActionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "idempotency_key": {
                  "type": "string"
                }
              },
              "required": [
                "idempotency_key"
              ]
            }
          },
          "required": [
            "workspace",
            "params"
          ]
        },
        "returns": "WorkspaceProjection",
        "safety": "write"
      },
      "sleep": {
        "description": "sleep on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "params": "WorkspaceLifecycleActionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "idempotency_key": {
                  "type": "string"
                }
              },
              "required": [
                "idempotency_key"
              ]
            }
          },
          "required": [
            "workspace",
            "params"
          ]
        },
        "returns": "WorkspaceProjection",
        "safety": "write"
      },
      "retryArchiveCleanup": {
        "description": "retryArchiveCleanup on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "params": "RetryWorkspaceArchiveCleanupRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "operation_id": {
                  "type": "string"
                }
              },
              "required": [
                "operation_id"
              ]
            }
          },
          "required": [
            "workspace",
            "params"
          ]
        },
        "returns": "WorkspaceProjection",
        "safety": "write"
      },
      "cancelOperation": {
        "description": "Cancel a running cloud setup, transfer, or pre-swap conversion owned by the caller or administered by an account owner.",
        "params": {
          "workspace": "WorkspaceRef",
          "operationId": "string",
          "params": "CancelWorkspaceOperationRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "operationId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "idempotency_key": {
                  "type": "string"
                }
              },
              "required": [
                "idempotency_key"
              ]
            }
          },
          "required": [
            "workspace",
            "operationId",
            "params"
          ]
        },
        "returns": "WorkspaceProjection",
        "safety": "write"
      },
      "transfer": {
        "description": "transfer on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "params": "TransferWorkspaceRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "device_id": {
                  "type": "string"
                },
                "direction": {
                  "type": "string",
                  "enum": [
                    "push",
                    "pull"
                  ]
                },
                "expected_source": {
                  "type": "object",
                  "properties": {
                    "tree": {
                      "type": "string"
                    },
                    "sidecar": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "tree",
                    "sidecar"
                  ]
                },
                "expected_destination": {
                  "type": "object",
                  "properties": {
                    "tree": {
                      "type": "string"
                    },
                    "sidecar": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "tree",
                    "sidecar"
                  ]
                },
                "resolve_divergence": {
                  "type": "boolean"
                },
                "idempotency_key": {
                  "type": "string"
                }
              },
              "required": [
                "device_id",
                "direction",
                "idempotency_key"
              ]
            }
          },
          "required": [
            "workspace",
            "params"
          ]
        },
        "returns": "WorkspaceProjection",
        "safety": "destructive"
      },
      "saveEnvironment": {
        "description": "saveEnvironment on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "params": "SaveWorkspaceEnvironmentRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "idempotency_key": {
                  "type": "string"
                },
                "declaration": {
                  "type": "object",
                  "properties": {
                    "image": {
                      "type": "string"
                    },
                    "features": {
                      "type": "array",
                      "items": {
                        "type": "string"
                      }
                    },
                    "packages": {
                      "type": "array",
                      "items": {
                        "type": "object",
                        "properties": {
                          "manager": {
                            "type": "string",
                            "enum": [
                              "apk",
                              "apt",
                              "npm",
                              "pip"
                            ]
                          },
                          "name": {
                            "type": "string"
                          },
                          "version": {
                            "type": "string"
                          }
                        },
                        "required": [
                          "manager",
                          "name",
                          "version"
                        ]
                      }
                    },
                    "containerEnv": {
                      "type": "object",
                      "properties": {},
                      "additionalProperties": {
                        "type": "string"
                      }
                    },
                    "postCreateCommand": {
                      "type": "array",
                      "items": {
                        "type": "string"
                      }
                    }
                  },
                  "required": [
                    "image"
                  ]
                }
              },
              "required": [
                "idempotency_key",
                "declaration"
              ]
            }
          },
          "required": [
            "workspace",
            "params"
          ]
        },
        "returns": "WorkspaceProjection",
        "safety": "write"
      },
      "getOperationHistory": {
        "description": "getOperationHistory on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "limit": "number (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "limit": {
              "type": "number"
            }
          },
          "required": [
            "workspace"
          ]
        },
        "returns": "WorkspaceOperationHistory",
        "safety": "read"
      },
      "listRepositories": {
        "description": "listRepositories on the workspaces resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "CloudRepository[]",
        "safety": "read"
      },
      "getRepositoryStatus": {
        "description": "getRepositoryStatus on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            }
          },
          "required": [
            "workspace"
          ]
        },
        "returns": "RepositoryStatusResponse",
        "safety": "read"
      },
      "listSandboxes": {
        "description": "listSandboxes on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            }
          },
          "required": [
            "workspace"
          ]
        },
        "returns": "PodInfo[]",
        "safety": "read"
      },
      "executeFileOp": {
        "description": "executeFileOp on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "params": "ExecuteFileOpRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "op_type": {
                  "type": "string",
                  "enum": [
                    "delete",
                    "read",
                    "write",
                    "move",
                    "list",
                    "info",
                    "exists",
                    "search",
                    "read_batch",
                    "write_batch"
                  ]
                },
                "path": {
                  "type": "string"
                },
                "content": {
                  "type": "string"
                },
                "expected_version": {
                  "type": "string"
                },
                "options": {
                  "type": "object",
                  "properties": {},
                  "additionalProperties": {}
                }
              },
              "required": [
                "op_type",
                "path"
              ]
            }
          },
          "required": [
            "workspace",
            "params"
          ]
        },
        "returns": "FileOpResponse",
        "safety": "destructive"
      },
      "readFile": {
        "description": "readFile on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "path": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "path": {
              "type": "string"
            }
          },
          "required": [
            "workspace",
            "path"
          ]
        },
        "returns": "FileReadResult",
        "safety": "read"
      },
      "writeFile": {
        "description": "writeFile on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "path": "string",
          "content": "string",
          "expectedVersion": "string (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "path": {
              "type": "string"
            },
            "content": {
              "type": "string"
            },
            "expectedVersion": {
              "type": "string"
            }
          },
          "required": [
            "workspace",
            "path",
            "content"
          ]
        },
        "returns": "FileWriteResult",
        "safety": "write"
      },
      "viewChanges": {
        "description": "viewChanges on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "sessionId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "sessionId": {
              "type": "string"
            }
          },
          "required": [
            "workspace",
            "sessionId"
          ]
        },
        "returns": "VersioningOpResponse",
        "safety": "read"
      },
      "saveVersion": {
        "description": "saveVersion on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "params": "SaveCheckpointRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "session_id": {
                  "type": "string"
                },
                "message": {
                  "type": "string"
                }
              },
              "required": [
                "session_id",
                "message"
              ]
            }
          },
          "required": [
            "workspace",
            "params"
          ]
        },
        "returns": "VersioningOpResponse",
        "safety": "write"
      },
      "listHistory": {
        "description": "listHistory on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "params": "ViewHistoryParams"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "session_id": {
                  "type": "string"
                },
                "limit": {
                  "type": "number"
                }
              },
              "required": [
                "session_id"
              ]
            }
          },
          "required": [
            "workspace",
            "params"
          ]
        },
        "returns": "VersionHistoryResponse",
        "safety": "read"
      },
      "restoreVersion": {
        "description": "restoreVersion on the workspaces resource.",
        "params": {
          "workspace": "WorkspaceRef",
          "params": "RestoreVersionRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspace": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "name"
                  ]
                }
              ]
            },
            "params": {
              "type": "object",
              "properties": {
                "session_id": {
                  "type": "string"
                },
                "version": {
                  "type": "string"
                },
                "message": {
                  "type": "string"
                },
                "paths": {
                  "type": "array",
                  "items": {
                    "type": "string"
                  }
                }
              },
              "required": [
                "session_id",
                "version"
              ]
            }
          },
          "required": [
            "workspace",
            "params"
          ]
        },
        "returns": "VersioningOpResponse",
        "safety": "write"
      }
    }
  },
  "chat": {
    "description": "Chat session factory — create or reconnect a ChatSession (documented, not dispatchable).",
    "methods": {
      "openDraft": {
        "description": "Return the in-memory New Chat handle. No backend row exists until the first persist action (`send`, `attachWorkspace`, image ensure). Single-flight: concurrent callers join the pending open rather than each minting a draft the others would immediately orphan.",
        "params": {
          "params": "OpenDraftParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "workspaceId": {
              "type": "string"
            },
            "personaId": {
              "type": "string"
            }
          }
        },
        "returns": "ChatSession",
        "safety": "write"
      },
      "startNewChat": {
        "description": "The one \"the user asked for a new chat\" verb: drop whatever is live and return a fresh draft. Callers that only need the side effect can ignore the result — subscribers are notified either way.",
        "params": {
          "params": "OpenDraftParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "workspaceId": {
              "type": "string"
            },
            "personaId": {
              "type": "string"
            }
          }
        },
        "returns": "ChatSession",
        "safety": "write"
      },
      "create": {
        "description": "Create a new chat session and return a connected ChatSession. When `workspaceId` is omitted (or undefined), the session is created workspace-less (account-scoped) via `POST /v1/sessions/sessions`; its `ChatSession.workspaceId` is undefined and the backend persists `workspace_id = null`.",
        "params": {
          "workspaceId": "string (optional)",
          "params": "CreateChatParams (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "type": "string"
            },
            "params": {
              "type": "object",
              "properties": {
                "personaId": {
                  "type": "string"
                },
                "personaName": {
                  "type": "string"
                },
                "agentConfig": {
                  "type": "object",
                  "properties": {
                    "system_prompt": {
                      "type": "string"
                    },
                    "provider_type": {
                      "type": "string",
                      "enum": [
                        "openai",
                        "groq",
                        "lmstudio",
                        "anthropic",
                        "fireworks",
                        "grok",
                        "test"
                      ]
                    },
                    "model_name": {
                      "type": "string"
                    },
                    "completion_params": {
                      "type": "object",
                      "properties": {
                        "temperature": {
                          "type": "number"
                        },
                        "top_p": {
                          "type": "number"
                        },
                        "reasoning_effort": {
                          "anyOf": [
                            {
                              "type": "null"
                            },
                            {
                              "type": "string"
                            }
                          ]
                        },
                        "stop": {
                          "type": "array",
                          "items": {
                            "type": "string"
                          }
                        }
                      },
                      "additionalProperties": {}
                    },
                    "automatic_process_selection": {
                      "type": "boolean"
                    },
                    "automatic_process_ids": {
                      "type": "array",
                      "items": {
                        "type": "string"
                      }
                    }
                  }
                },
                "agentProcessId": {
                  "type": "string",
                  "description": "Explicit agent process for the session. When omitted, the backend resolves the session persona's default process → seeded Simple and materializes the result. Every session runs under a process — there is no legacy mode path (agent-processes cutover)."
                },
                "automaticProcessSelection": {
                  "type": "boolean",
                  "description": "Explicit chat override; omitted keeps the persona preference live."
                },
                "internalAgentVisibility": {
                  "type": "string",
                  "enum": [
                    "friendly",
                    "exact"
                  ]
                },
                "pinModelToTurn": {
                  "type": "boolean",
                  "description": "Pin the session's model to every execution in the turn. Omitted ⇒ the backend's ratified default, `false`. The client never fabricates a value — see the omit-if-undefined note in `create` below."
                },
                "deviceId": {
                  "type": "string"
                },
                "name": {
                  "type": "string"
                },
                "fingerprint": {
                  "type": "string",
                  "description": "Param-schema fingerprint from `getParamSchema`. Required when `agentConfig` sets completion params or names a provider/model pair — the backend's completion-params write guard 422s without it."
                }
              }
            }
          }
        },
        "returns": "ChatSession",
        "safety": "write"
      },
      "connect": {
        "description": "Reconnect an existing session id and return a live ChatSession.",
        "params": {
          "sessionId": "string",
          "workspaceId": "string (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "sessionId": {
              "type": "string"
            },
            "workspaceId": {
              "type": "string"
            }
          },
          "required": [
            "sessionId"
          ]
        },
        "returns": "ChatSession",
        "safety": "read"
      }
    }
  },
  "chatSession": {
    "description": "Connected chat handle — send, capabilities, invoke, stop, and live event callbacks. Documented; not platform_call.",
    "methods": {
      "ensurePersisted": {
        "description": "Create the backend row once; concurrent callers share the flight.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "string",
        "safety": "write"
      },
      "onTranscriptWindow": {
        "description": "Notified whenever the transcript window's contents change.",
        "params": {
          "handler": "() => void"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {}
        },
        "returns": "() => void",
        "safety": "read"
      },
      "peekCachedPayload": {
        "description": "Cached event payload for a sequence number, if already fetched.",
        "params": {
          "seq": "number"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "seq": {
              "type": "number"
            }
          },
          "required": [
            "seq"
          ]
        },
        "returns": "Record<string, unknown> | undefined",
        "safety": "read"
      },
      "send": {
        "description": "Send a message. `attachments` carries already-uploaded images as `{ type: 'image', image_id, label }` — upload with `sdk.sessions.uploadImage` first, so a failed upload surfaces at attach time rather than aborting a send. They are MERGED with the file/directory attachments derived from `mentions`, in mentions-then-images order, and all of them precede the user text in the assembled turn.",
        "params": {
          "content": "string",
          "mentions": "MentionRef[] (optional)",
          "viewContext": "ViewContext (optional)",
          "attachments": "ImageAttachment[] (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "content": {
              "type": "string"
            },
            "mentions": {
              "type": "array",
              "items": {
                "type": "object",
                "properties": {
                  "type": {
                    "type": "string",
                    "enum": [
                      "file",
                      "directory"
                    ]
                  },
                  "path": {
                    "type": "string"
                  },
                  "label": {
                    "type": "string"
                  }
                },
                "required": [
                  "type",
                  "path",
                  "label"
                ]
              }
            },
            "viewContext": {
              "type": "object",
              "properties": {
                "route": {
                  "type": "string"
                },
                "surface": {
                  "type": "string"
                },
                "primary_type": {
                  "type": "string"
                },
                "scope": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "type": {
                        "type": "string"
                      },
                      "id": {
                        "type": "string"
                      },
                      "label": {
                        "type": "string"
                      },
                      "state": {
                        "type": "string",
                        "description": "The record's RENDERED state — the lane the user sees it in, which is not always the raw enum (app-shell ADR-011)."
                      },
                      "note": {
                        "type": "string",
                        "description": "One badge that changes the answer: blocked-by, unassigned, orphan, session running."
                      }
                    },
                    "required": [
                      "type",
                      "id"
                    ]
                  },
                  "description": "The ancestor chain the view is scoped to, outermost first (app-shell ADR-014)."
                },
                "counts": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "lane": {
                        "type": "string"
                      },
                      "total": {
                        "type": "number"
                      }
                    },
                    "required": [
                      "lane",
                      "total"
                    ]
                  },
                  "description": "Per-lane true totals — never truncated, so the agent knows the real numbers."
                },
                "filters": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "name": {
                        "type": "string"
                      },
                      "value": {
                        "type": "string"
                      }
                    },
                    "required": [
                      "name",
                      "value"
                    ]
                  }
                },
                "visible": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "type": {
                        "type": "string"
                      },
                      "id": {
                        "type": "string"
                      },
                      "label": {
                        "type": "string"
                      },
                      "state": {
                        "type": "string",
                        "description": "The record's RENDERED state — the lane the user sees it in, which is not always the raw enum (app-shell ADR-011)."
                      },
                      "note": {
                        "type": "string",
                        "description": "One badge that changes the answer: blocked-by, unassigned, orphan, session running."
                      }
                    },
                    "required": [
                      "type",
                      "id"
                    ]
                  },
                  "description": "Every record actually rendered. The client does not sample (app-shell ADR-014)."
                },
                "selected": {
                  "type": "object",
                  "properties": {
                    "type": {
                      "type": "string"
                    },
                    "id": {
                      "type": "string"
                    },
                    "label": {
                      "type": "string"
                    },
                    "state": {
                      "type": "string",
                      "description": "The record's RENDERED state — the lane the user sees it in, which is not always the raw enum (app-shell ADR-011)."
                    },
                    "note": {
                      "type": "string",
                      "description": "One badge that changes the answer: blocked-by, unassigned, orphan, session running."
                    }
                  },
                  "required": [
                    "type",
                    "id"
                  ]
                },
                "captured_at": {
                  "type": "string",
                  "description": "When the snapshot was taken — stamped at composer submit (app-shell ADR-015)."
                },
                "truncated": {
                  "type": "boolean",
                  "description": "Set when the CLIENT dropped anything from this snapshot before sending it."
                }
              }
            },
            "attachments": {
              "type": "array",
              "items": {
                "type": "object",
                "properties": {
                  "type": {
                    "type": "string",
                    "enum": [
                      "image"
                    ]
                  },
                  "image_id": {
                    "type": "string"
                  },
                  "label": {
                    "type": "string"
                  }
                },
                "required": [
                  "type",
                  "image_id",
                  "label"
                ]
              }
            }
          },
          "required": [
            "content"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "sendAsync": {
        "description": "Persist a draft if needed, then send. Rejects and rolls back the optimistic row on failure.",
        "params": {
          "content": "string",
          "mentions": "MentionRef[] (optional)",
          "viewContext": "ViewContext (optional)",
          "attachments": "ImageAttachment[] (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "content": {
              "type": "string"
            },
            "mentions": {
              "type": "array",
              "items": {
                "type": "object",
                "properties": {
                  "type": {
                    "type": "string",
                    "enum": [
                      "file",
                      "directory"
                    ]
                  },
                  "path": {
                    "type": "string"
                  },
                  "label": {
                    "type": "string"
                  }
                },
                "required": [
                  "type",
                  "path",
                  "label"
                ]
              }
            },
            "viewContext": {
              "type": "object",
              "properties": {
                "route": {
                  "type": "string"
                },
                "surface": {
                  "type": "string"
                },
                "primary_type": {
                  "type": "string"
                },
                "scope": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "type": {
                        "type": "string"
                      },
                      "id": {
                        "type": "string"
                      },
                      "label": {
                        "type": "string"
                      },
                      "state": {
                        "type": "string",
                        "description": "The record's RENDERED state — the lane the user sees it in, which is not always the raw enum (app-shell ADR-011)."
                      },
                      "note": {
                        "type": "string",
                        "description": "One badge that changes the answer: blocked-by, unassigned, orphan, session running."
                      }
                    },
                    "required": [
                      "type",
                      "id"
                    ]
                  },
                  "description": "The ancestor chain the view is scoped to, outermost first (app-shell ADR-014)."
                },
                "counts": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "lane": {
                        "type": "string"
                      },
                      "total": {
                        "type": "number"
                      }
                    },
                    "required": [
                      "lane",
                      "total"
                    ]
                  },
                  "description": "Per-lane true totals — never truncated, so the agent knows the real numbers."
                },
                "filters": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "name": {
                        "type": "string"
                      },
                      "value": {
                        "type": "string"
                      }
                    },
                    "required": [
                      "name",
                      "value"
                    ]
                  }
                },
                "visible": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "type": {
                        "type": "string"
                      },
                      "id": {
                        "type": "string"
                      },
                      "label": {
                        "type": "string"
                      },
                      "state": {
                        "type": "string",
                        "description": "The record's RENDERED state — the lane the user sees it in, which is not always the raw enum (app-shell ADR-011)."
                      },
                      "note": {
                        "type": "string",
                        "description": "One badge that changes the answer: blocked-by, unassigned, orphan, session running."
                      }
                    },
                    "required": [
                      "type",
                      "id"
                    ]
                  },
                  "description": "Every record actually rendered. The client does not sample (app-shell ADR-014)."
                },
                "selected": {
                  "type": "object",
                  "properties": {
                    "type": {
                      "type": "string"
                    },
                    "id": {
                      "type": "string"
                    },
                    "label": {
                      "type": "string"
                    },
                    "state": {
                      "type": "string",
                      "description": "The record's RENDERED state — the lane the user sees it in, which is not always the raw enum (app-shell ADR-011)."
                    },
                    "note": {
                      "type": "string",
                      "description": "One badge that changes the answer: blocked-by, unassigned, orphan, session running."
                    }
                  },
                  "required": [
                    "type",
                    "id"
                  ]
                },
                "captured_at": {
                  "type": "string",
                  "description": "When the snapshot was taken — stamped at composer submit (app-shell ADR-015)."
                },
                "truncated": {
                  "type": "boolean",
                  "description": "Set when the CLIENT dropped anything from this snapshot before sending it."
                }
              }
            },
            "attachments": {
              "type": "array",
              "items": {
                "type": "object",
                "properties": {
                  "type": {
                    "type": "string",
                    "enum": [
                      "image"
                    ]
                  },
                  "image_id": {
                    "type": "string"
                  },
                  "label": {
                    "type": "string"
                  }
                },
                "required": [
                  "type",
                  "image_id",
                  "label"
                ]
              }
            }
          },
          "required": [
            "content"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "seedDefaultPersona": {
        "description": "Load the account default persona onto a draft.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "void",
        "safety": "write"
      },
      "setPersona": {
        "description": "Bind a persona and rematerialize process / automatic inherit.",
        "params": {
          "personaId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "personaId": {
              "type": "string"
            }
          },
          "required": [
            "personaId"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "setProcess": {
        "description": "Pin an explicit process until the next setPersona.",
        "params": {
          "agentProcessId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "agentProcessId": {
              "type": "string"
            }
          },
          "required": [
            "agentProcessId"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "setAutomaticProcessSelection": {
        "description": "Set or inherit (`null`) automatic process selection.",
        "params": {
          "value": "boolean | null"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "value": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "boolean"
                }
              ]
            }
          },
          "required": [
            "value"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "setPinModelToTurn": {
        "description": "Pin the session model to every execution in the turn.",
        "params": {
          "pin": "boolean"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "pin": {
              "type": "boolean"
            }
          },
          "required": [
            "pin"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "setContextRagEnabled": {
        "description": "Toggle run-start context retrieval for this chat.",
        "params": {
          "enabled": "boolean"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "enabled": {
              "type": "boolean"
            }
          },
          "required": [
            "enabled"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "setContextRagChildRunsEnabled": {
        "description": "Toggle context retrieval for sub-agent runs in this chat.",
        "params": {
          "enabled": "boolean"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "enabled": {
              "type": "boolean"
            }
          },
          "required": [
            "enabled"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "setInternalAgentVisibility": {
        "description": "Set internal-agent visibility for this chat.",
        "params": {
          "visibility": "InternalAgentVisibility"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "visibility": {
              "type": "string",
              "enum": [
                "friendly",
                "exact"
              ]
            }
          },
          "required": [
            "visibility"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "attachWorkspace": {
        "description": "Attach a workspace; persists immediately on a draft.",
        "params": {
          "workspaceId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "workspaceId": {
              "type": "string"
            }
          },
          "required": [
            "workspaceId"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "clearWorkspace": {
        "description": "Drop a draft workspace before persist.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "void",
        "safety": "write"
      },
      "loadSettings": {
        "description": "Replace settings from the persisted session row.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "void",
        "safety": "read"
      },
      "onPersisted": {
        "description": "Subscribe to the first persist of a draft.",
        "params": {
          "handler": "PersistedHandler"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {}
        },
        "returns": "() => void",
        "safety": "read"
      },
      "onSettings": {
        "description": "Subscribe to settings projection changes.",
        "params": {
          "handler": "SettingsHandler"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {}
        },
        "returns": "() => void",
        "safety": "read"
      },
      "listCapabilities": {
        "description": "List the capabilities available in this session's workspace. Merges TWO discovery calls: - `sdk.tools.listAvailableTools(workspaceId, deviceId)` → `kind:\"coded\"` (device id threaded so device-routed local tools are listed). - workspace: `GET /v1/tools/{workspaceId}/skills` (merged workspace + catalog), never SDK-retried - workspace-less: `sdk.skills.list()` (catalog only; no workstation wake) `kind` is set purely by which call produced the entry. Neither call is wrapped in a fallback: if either discovery surface fails, the whole listing rejects (no-fallbacks).",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "Capability[]",
        "safety": "read"
      },
      "planArgCapture": {
        "description": "Decide how the composer should capture arguments for a capability.",
        "params": {
          "cap": "Capability"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "name": {
              "type": "string"
            },
            "description": {
              "type": "string"
            },
            "kind": {
              "type": "string",
              "enum": [
                "coded",
                "skill"
              ]
            },
            "parameters": {
              "type": "object",
              "properties": {},
              "additionalProperties": {}
            },
            "mcpId": {
              "type": "string"
            },
            "framework": {
              "type": "string",
              "description": "Convention layout that supplied a skill: `sot`, `claude`, `cursor`, or `codex`."
            },
            "qualifiedName": {
              "type": "string",
              "description": "`framework:name` address that reaches this skill even when shadowed."
            },
            "shadowedBy": {
              "type": "string",
              "description": "Framework whose same-named skill wins a bare lookup; absent when this entry wins."
            },
            "namespace": {
              "type": "string",
              "description": "Empty on workspace skills; catalog skills use `shogun`."
            }
          },
          "required": [
            "name",
            "description",
            "kind"
          ]
        },
        "returns": "ArgCapture",
        "safety": "read"
      },
      "invoke": {
        "description": "Invoke a capability. Accepts the selected `Capability` (the palette always holds it) or an unambiguous name string. For a **coded** capability: generates a client `invocation_id` and makes ONE call to `POST /v1/reasoning/{ws}/invoke-capability`, which executes the tool server-side and persists the call+result pair. Returns `{ kind:\"coded\", status, entryRef?, preview?, errorKind? }`. A 409 (session busy) rejection is surfaced as a typed `SessionBusyError`. **`workspace_init` / `/init`:** still a coded capability on this path, but its executor **auto-starts** `Agent.Execute` with the seeded `workspace-initializer` system persona in the current session (exception to seed-don't-auto-start). The coded outcome's preview is the `WorkspaceInitAgentResult` JSON envelope (`persona`, `run_id`, `session_id`, `started`). Follow-up user messages are normal `send()` turns. A **skill** capability is not invocable. Send the name as text and the assistant reads it with `skill_read`. A bare-name string that matches more than one capability (a coded tool and a skill may share a name) throws `AmbiguousCapabilityError` — pass the selected `Capability` object to dispatch by its `kind`.",
        "params": {
          "cap": "Capability | string",
          "args": "Record<string, unknown> (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "cap": {
              "anyOf": [
                {
                  "type": "string"
                },
                {
                  "type": "object",
                  "properties": {
                    "name": {
                      "type": "string"
                    },
                    "description": {
                      "type": "string"
                    },
                    "kind": {
                      "type": "string",
                      "enum": [
                        "coded",
                        "skill"
                      ]
                    },
                    "parameters": {
                      "type": "object",
                      "properties": {},
                      "additionalProperties": {}
                    },
                    "mcpId": {
                      "type": "string"
                    },
                    "framework": {
                      "type": "string",
                      "description": "Convention layout that supplied a skill: `sot`, `claude`, `cursor`, or `codex`."
                    },
                    "qualifiedName": {
                      "type": "string",
                      "description": "`framework:name` address that reaches this skill even when shadowed."
                    },
                    "shadowedBy": {
                      "type": "string",
                      "description": "Framework whose same-named skill wins a bare lookup; absent when this entry wins."
                    },
                    "namespace": {
                      "type": "string",
                      "description": "Empty on workspace skills; catalog skills use `shogun`."
                    }
                  },
                  "required": [
                    "name",
                    "description",
                    "kind"
                  ]
                }
              ]
            },
            "args": {
              "type": "object",
              "properties": {},
              "additionalProperties": {}
            }
          },
          "required": [
            "cap"
          ]
        },
        "returns": "InvokeOutcome",
        "safety": "write"
      },
      "respondToMediaActionApproval": {
        "description": "Answer a pending media-action approval for the current turn.",
        "params": {
          "response": "MediaActionApprovalResponse"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "approvalId": {
              "type": "string"
            },
            "approved": {
              "type": "boolean"
            }
          },
          "required": [
            "approvalId",
            "approved"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "respondToClarification": {
        "description": "Answer a pending clarification question.",
        "params": {
          "response": "ClarificationResponse"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "questionId": {
              "type": "string"
            },
            "selectedOption": {
              "type": "string"
            },
            "skipped": {
              "type": "boolean"
            }
          },
          "required": [
            "questionId",
            "selectedOption",
            "skipped"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "editMessageAndRewind": {
        "description": "Edit a message and rewind the session to it: on success the transcript window drops pages at/above the rewind point and refetches the tail — one owner for the rewind + window reset (adr.md ADR-006).",
        "params": {
          "messageId": "string",
          "content": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "messageId": {
              "type": "string"
            },
            "content": {
              "type": "string"
            }
          },
          "required": [
            "messageId",
            "content"
          ]
        },
        "returns": "Message[]",
        "safety": "write"
      },
      "stop": {
        "description": "Stop the in-flight turn.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "void",
        "safety": "write"
      },
      "redirect": {
        "description": "Redirect the in-flight turn with new user content.",
        "params": {
          "content": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "content": {
              "type": "string"
            }
          },
          "required": [
            "content"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "onMessage": {
        "description": "Subscribe to new chat messages.",
        "params": {
          "handler": "MessageHandler"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {}
        },
        "returns": "() => void",
        "safety": "read"
      },
      "onLiveLlmCalls": {
        "description": "Subscribe to changes in {@link liveLlmCalls}.",
        "params": {
          "handler": "(calls: LiveLlmCall[]) => void"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {}
        },
        "returns": "() => void",
        "safety": "read"
      },
      "onProgressUpdate": {
        "description": "Subscribe to progress-tree updates.",
        "params": {
          "handler": "ProgressHandler"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {}
        },
        "returns": "() => void",
        "safety": "read"
      },
      "onStateChange": {
        "description": "Subscribe to chat state changes.",
        "params": {
          "handler": "StateHandler"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {}
        },
        "returns": "() => void",
        "safety": "read"
      },
      "onClarificationRequest": {
        "description": "Subscribe to clarification requests.",
        "params": {
          "handler": "ClarificationHandler"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {}
        },
        "returns": "() => void",
        "safety": "read"
      },
      "onConnectionStateChange": {
        "description": "Subscribe to connection-state changes.",
        "params": {
          "handler": "ConnectionHandler"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {}
        },
        "returns": "() => void",
        "safety": "read"
      },
      "onQueueChange": {
        "description": "Subscribe to outbound queue changes.",
        "params": {
          "handler": "QueueHandler"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {}
        },
        "returns": "() => void",
        "safety": "read"
      },
      "onError": {
        "description": "Subscribe to session errors.",
        "params": {
          "handler": "ErrorHandler"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {}
        },
        "returns": "() => void",
        "safety": "read"
      },
      "onContextHealth": {
        "description": "Subscribe to context-health updates.",
        "params": {
          "handler": "ContextHealthHandler"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {}
        },
        "returns": "() => void",
        "safety": "read"
      },
      "disconnect": {
        "description": "Disconnect this handle from the live stream.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "void",
        "safety": "write"
      },
      "loadOlderEvents": {
        "description": "Backward events page below {@link oldestLoadedSequence}, aligned so it begins at a run start: no loaded run is ever missing its start. Empty result = session start.",
        "params": {
          "limit": "number (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "limit": {
              "type": "number"
            }
          }
        },
        "returns": "readonly SessionEvent[]",
        "safety": "read"
      },
      "loadNewerEvents": {
        "description": "Forward events page that refills the released span above the detached history segment, ending on a run boundary or where it meets the loaded runs above. Empty result = not detached.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "readonly SessionEvent[]",
        "safety": "read"
      },
      "setViewportDemand": {
        "description": "Report what one view shows. The SDK keeps the live tail plus every reported view's span with one page of margin each side, under the window ceiling, and releases the rest from memory and the saved history (sdk ADR-f43c7). A view reports `null` when it is closed or hidden.",
        "params": {
          "view": "'runs-panel' | 'transcript'",
          "range": "RunsPanelDemand | TranscriptDemand | null"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "view": {
              "type": "string",
              "enum": [
                "runs-panel",
                "transcript"
              ]
            },
            "range": {
              "anyOf": [
                {
                  "type": "null"
                },
                {
                  "type": "object",
                  "properties": {
                    "oldestRunId": {
                      "type": "string"
                    },
                    "newestRunId": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "oldestRunId",
                    "newestRunId"
                  ]
                },
                {
                  "type": "object",
                  "properties": {
                    "oldestMessageIndex": {
                      "type": "number"
                    },
                    "newestMessageIndex": {
                      "type": "number"
                    }
                  },
                  "required": [
                    "oldestMessageIndex",
                    "newestMessageIndex"
                  ]
                }
              ]
            }
          },
          "required": [
            "view",
            "range"
          ]
        },
        "returns": "void",
        "safety": "read"
      },
      "revealMessage": {
        "description": "Pages the transcript backward until the message with this id is loaded. Returns false when the transcript start is reached without finding it.",
        "params": {
          "messageId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "messageId": {
              "type": "string"
            }
          },
          "required": [
            "messageId"
          ]
        },
        "returns": "boolean",
        "safety": "read"
      },
      "loadTranscriptTail": {
        "description": "Initial tail page of the transcript window. Idempotent; a no-op on a draft (no session row exists yet).",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "void",
        "safety": "read"
      },
      "loadOlderMessages": {
        "description": "Backward transcript page below the oldest loaded message_index. Empty result = transcript start.",
        "params": {
          "limit": "number (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "limit": {
              "type": "number"
            }
          }
        },
        "returns": "readonly Message[]",
        "safety": "read"
      },
      "loadNewerMessages": {
        "description": "Forward transcript page that refills the released rows above the detached history segment. Never saved to the history cache. Empty result = not detached.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "readonly Message[]",
        "safety": "read"
      },
      "reloadTranscript": {
        "description": "Reset to the latest page (used after edit-and-rewind).",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "void",
        "safety": "read"
      },
      "getEventData": {
        "description": "Return one event's FULL `data`, hydrating on demand. A small/inline event (no `handle`) returns its skeleton `data` directly. A large event returns the cached full payload, or hydrates it via the completion-looping {@link SessionsResource.readEventPayload}, caches it (LRU), and returns it. On failure it sets `hydrationError` — `not_available` for a 404 (the event is gone), `failed` for a network/Temporal error — and RETHROWS (never a silent empty render, no-fallbacks).",
        "params": {
          "seq": "number"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "seq": {
              "type": "number"
            }
          },
          "required": [
            "seq"
          ]
        },
        "returns": "Record<string, unknown>",
        "safety": "read"
      },
      "hydrateWindow": {
        "description": "Pre-hydrate a WINDOW of events in one batch request (scroll-into-view — a single {@link SessionsResource.readEventPayloads}, not N calls). Only large, not-yet-cached events are fetched; complete entries are cached, and a partial first page is left for a later {@link getEventData} to complete. Best-effort pre-fetch: a batch failure surfaces to error handlers, and each individual event can still hydrate on click.",
        "params": {
          "seqs": "number[]"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "seqs": {
              "type": "array",
              "items": {
                "type": "number"
              }
            }
          },
          "required": [
            "seqs"
          ]
        },
        "returns": "void",
        "safety": "read"
      }
    }
  },
  "liveFeed": {
    "description": "Connected Live Feed handle — bounded home window, loadOlder/loadNewer, publish/engage apply, markSeen. Documented; not platform_call.",
    "methods": {
      "hydrate": {
        "description": "Initial home page. Called by FeedResource.connect.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "void",
        "safety": "read"
      },
      "loadOlder": {
        "description": "Fetch the next older page. Drops the newest buffered page when the window would exceed three pages.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "FeedPost[]",
        "safety": "read"
      },
      "loadNewer": {
        "description": "Fetch newer posts than the current window. Restores a page dropped by loadOlder.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "FeedPost[]",
        "safety": "read"
      },
      "publish": {
        "description": "Publish through the resource and apply the returned post into the window.",
        "params": {
          "params": "PublishFeedPostRequest"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {
            "kind": {
              "type": "string",
              "enum": [
                "original",
                "reply",
                "quote",
                "repost"
              ]
            },
            "body": {
              "type": "string"
            },
            "parent_id": {
              "type": "string"
            },
            "quote_of_id": {
              "type": "string"
            },
            "repost_of_id": {
              "type": "string"
            },
            "source_kind": {
              "type": "string",
              "enum": [
                "session",
                "ticket",
                "automation_run",
                "context_source",
                "media_bulk"
              ]
            },
            "source_id": {
              "type": "string"
            }
          },
          "required": [
            "kind"
          ]
        },
        "returns": "FeedPost",
        "safety": "write"
      },
      "engage": {
        "description": "engage on the liveFeed resource.",
        "params": {
          "postId": "string",
          "op": "FeedEngageOp"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "postId": {
              "type": "string"
            },
            "op": {
              "type": "string",
              "enum": [
                "repost",
                "like",
                "bookmark"
              ]
            }
          },
          "required": [
            "postId",
            "op"
          ]
        },
        "returns": "FeedPost",
        "safety": "write"
      },
      "markSeen": {
        "description": "markSeen on the liveFeed resource.",
        "params": {},
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {}
        },
        "returns": "number",
        "safety": "write"
      },
      "loadThread": {
        "description": "loadThread on the liveFeed resource.",
        "params": {
          "postId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "postId": {
              "type": "string"
            }
          },
          "required": [
            "postId"
          ]
        },
        "returns": "FeedThread",
        "safety": "read"
      },
      "delete": {
        "description": "delete on the liveFeed resource.",
        "params": {
          "postId": "string"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "postId": {
              "type": "string"
            }
          },
          "required": [
            "postId"
          ]
        },
        "returns": "FeedPost",
        "safety": "write"
      },
      "apply": {
        "description": "Upsert a GET-shaped post into the home window (dedup by post id). Incoming fanout snapshots keep local viewer_* unless trustViewer.",
        "params": {
          "post": "FeedPost",
          "opts": "{ notice?: boolean; trustViewer?: boolean } (optional)"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "params",
          "properties": {
            "post": {
              "type": "object",
              "properties": {
                "id": {
                  "type": "string"
                },
                "poster_kind": {
                  "type": "string",
                  "enum": [
                    "user",
                    "persona",
                    "system"
                  ]
                },
                "poster_id": {
                  "type": "string"
                },
                "poster_display_name": {
                  "type": "string"
                },
                "kind": {
                  "type": "string",
                  "enum": [
                    "original",
                    "reply",
                    "quote",
                    "repost"
                  ]
                },
                "body": {
                  "type": "string"
                },
                "parent_id": {
                  "type": "string"
                },
                "root_id": {
                  "type": "string"
                },
                "quote_of_id": {
                  "type": "string"
                },
                "repost_of_id": {
                  "type": "string"
                },
                "source_kind": {
                  "type": "string",
                  "enum": [
                    "session",
                    "ticket",
                    "automation_run",
                    "context_source",
                    "media_bulk"
                  ]
                },
                "source_id": {
                  "type": "string"
                },
                "source_status": {
                  "type": "string",
                  "enum": [
                    "available",
                    "gone"
                  ]
                },
                "created_at": {
                  "type": "string"
                },
                "updated_at": {
                  "type": "string"
                },
                "deleted_at": {
                  "type": "string"
                },
                "deleted": {
                  "type": "boolean"
                },
                "like_count": {
                  "type": "number"
                },
                "reply_count": {
                  "type": "number"
                },
                "repost_count": {
                  "type": "number"
                },
                "viewer_liked": {
                  "type": "boolean"
                },
                "viewer_bookmarked": {
                  "type": "boolean"
                },
                "viewer_reposted": {
                  "type": "boolean"
                },
                "quoted_post": {
                  "type": "object"
                },
                "reposted_post": {
                  "type": "object"
                }
              },
              "required": [
                "id",
                "poster_kind",
                "poster_id",
                "poster_display_name",
                "kind",
                "body",
                "root_id",
                "created_at",
                "updated_at",
                "deleted",
                "like_count",
                "reply_count",
                "repost_count",
                "viewer_liked",
                "viewer_bookmarked",
                "viewer_reposted"
              ]
            },
            "opts": {
              "type": "object",
              "properties": {
                "notice": {
                  "type": "boolean"
                },
                "trustViewer": {
                  "type": "boolean"
                }
              }
            }
          },
          "required": [
            "post"
          ]
        },
        "returns": "void",
        "safety": "write"
      },
      "onChange": {
        "description": "Subscribe to home-window changes.",
        "params": {
          "handler": "LiveFeedChangeHandler"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {}
        },
        "returns": "() => void",
        "safety": "read"
      },
      "onNotice": {
        "description": "Subscribe to toast-worthy notices (new posts / replies-to-viewer).",
        "params": {
          "handler": "LiveFeedNoticeHandler"
        },
        "argSchema": {
          "type": "object",
          "x-arg-style": "body",
          "properties": {}
        },
        "returns": "() => void",
        "safety": "read"
      }
    }
  }
}
